import React from "react";
import {
    Card,
    Elevation,
    H3,
    H4,
    Button,
    Collapse,
    ControlGroup,
    Tag,
    Text,
    NumericInput,
    InputGroup,
    HTMLSelect,
    Callout,
    Drawer,
    Classes as DrawerClasses,
    HTMLTable,
    Spinner,
} from "@blueprintjs/core";
import { inject, observer } from "mobx-react";
import cogoToast from "cogo-toast";
import { DateInput } from "@blueprintjs/datetime";
import {
 apiCallPost,
 apiCallGet,
 apiCallPatch,
 apiCallPostMultipartProgress,
 indianRupee,
 copyToClipboard,
 printApiUrl,
} from "../../../../micro/fns/util";
import SingleSelectWrapper from "../../../../micro/utils/SingleSelectWrapper";
import { isSxBillingSuperuser } from "../../../utils/sxBillingSuperuser";

class BillingTab extends React.Component {
 state = {
        isLoading: true,
  isSaving: false,
        hasLoadedInitialData: false,
  // Panels open/closed
  isAuthOpen: true,
  isLineItemsOpen: true,
  isImplantOpen: true,
  isPaymentsOpen: true,
  isInvoiceOpen: true,
  // Lookups
  tpaOptions: [],
  isLoadingTPA: false,
  insuranceOptions: [],
  isLoadingInsurance: false,
  // Anesthesia options
  anesthesiaOptions: [],
  isLoadingAnesthesia: false,
  // Surgery Payment Master lookup
  surgeryMasterOptions: [], // { id, label, record }
  isLoadingSurgeryMaster: false,
  surgeryMasterQuery: "",
  selectedSurgeryMasterId: "",
  // Authorization form
  authForm: {
   amountRupees: 0, // Changed from amountPaise to amountRupees for clarity
   type: "Pre-Authorization", // Pre-Authorization | Post-Authorization | Final Authorization
   tpaId: "",
   tpaName: "",
   insuranceId: "",
   insuranceName: "",
   letterFileId: "",
   letterFileName: "",
   remarks: "",
   insuranceNo: "",
   ccn: "",
   claimPayor: "",
   isUploading: false,
  },
  authFormDirty: false,
  // Authorization data
  authorization: {
   entries: [], // { type, amountRupees, tpaId, tpaName, insuranceId, insuranceName, letterFileId, letterFileName, uploadedById, uploadedByName, ts }
   activeIndex: -1, // latest (0) by default when entries exist
  },
  // Surgery billing cards
  surgeryBills: [],
 hasUnsavedBillingChanges: false,
  // Implant/IOL bill (table view)
  implantBill: {
   isLoading: false,
   items: [
    {
     itemName: "",
     hsnCode: "90213900",
     serialNo: "",
     expiry: "",
     qty: 1,
     pricePaise: 0,
     cgst: 2.5,
     sgst: 2.5,
     totalPaise: 0,
    },
   ], // rows from consumption or manual additions
   totalBeforeDiscountPaise: 0,
   totalGstPaise: 0,
   totalDiscountPaise: 0,
   totalPaise: 0,
  },
  // Other sections (kept minimal shells)
  lineItems: {
   items: [],
   totalsPaise: 0,
  },
  payments: {
   events: [],
   totalPaidPaise: 0,
   totalRefundedPaise: 0,
  },
  paymentForm: {
   type: "Payment",
   mode: "Cash",
   amountPaise: 0,
   paymentRemarks: "",
  },
  // Final invoice snapshot (read-only after generation)
  finalInvoice: null, // { number, generatedAt, branchShort, fyKey, surgeryBillsSnapshot, overallPaise, checksum }
  // Implant/IOL invoice snapshot (read-only after generation)
  finalImplantInvoice: null, // { number, generatedAt, bookingReference, counter, itemsSnapshot, totalPaise, checksum }
  // Audit
  auditEntries: [], // {surgeryId, action, by:{id,name}, panel, fieldPath, prevValue, newValue, at}
  lastUpdatedBy: null,
  lastUpdatedAt: null,
  isAuditOpen: false,
  auditFilter: { panel: "ALL", action: "ALL" },
 };
 consolidatedAutoSaveTimer = null;
 consolidatedAutoSaveInFlight = false;
  consolidatedFieldsToWatch = [
    "surgeryRatePaise",
    "anaestheticChargesPaise",
    "tissueProcessingPaise",
    "combinedRatePaise",
    "procedureTaxPercent",
    "procedureCostRupees",
    "implantCostInclusivePaise",
    "implantTaxPercent",
    "femtoChargesPaise",
    "specialDiscountPaise",
    "specialDiscountRemarks",
    "iolDiscountPaise",
    "mouDiscountPercent",
    "mouDiscountPaise",
    "selectedAnesthesiaId",
  ];

 // Simple in-memory fallback store (scoped to this browser session)
 static memoryStore = new Map();
 static mrCounterStore = new Map(); // FY-scoped in-memory counters
 static invoiceCounterStore = new Map(); // Branch+FY scoped counters
 static implantInvoiceCounterStore = new Map(); // IOL invoice counters

 componentDidMount() {
  this.initializeBillingData();
 }

 componentWillUnmount() {
  // Ensure the latest edits are preserved when navigating away
  try {
   this.persistToMemory();
  } catch (e) {}
 this.clearConsolidatedAutoSaveTimer();
 }

 getDefaultAuthForm = () => ({
  amountRupees: 0,
  type: "Pre-Authorization",
  tpaId: "",
  tpaName: "",
  insuranceId: "",
  insuranceName: "",
  letterFileId: "",
  letterFileName: "",
  remarks: "",
  insuranceNo: "",
  ccn: "",
  claimPayor: "",
  isUploading: false,
 });

clearConsolidatedAutoSaveTimer = () => {
    if (this.consolidatedAutoSaveTimer) {
        clearTimeout(this.consolidatedAutoSaveTimer);
        this.consolidatedAutoSaveTimer = null;
    }
};

scheduleConsolidatedAutoSave = (idx) => {
    this.clearConsolidatedAutoSaveTimer();
    this.consolidatedAutoSaveTimer = setTimeout(() => {
        this.consolidatedAutoSaveTimer = null;
        if (!this.state.hasUnsavedBillingChanges) {
            return;
        }
        if (this.consolidatedAutoSaveInFlight) {
            this.scheduleConsolidatedAutoSave(idx);
            return;
        }
        this.saveSurgeryLine(idx, { silent: true });
    }, 1500);
};

 // Helper methods for dropdown items (similar to SurgeryBooking.js)
 createTPAItems = () => {
  const items = this.state.tpaOptions.map((tpa) => tpa.name).filter(Boolean);
  console.log("🔍 BillingTab: createTPAItems returning", items.length, "items");
  return items;
 };

 createInsuranceItems = () => {
  const items = this.state.insuranceOptions
   .map((insurance) => insurance.name)
   .filter(Boolean);
  console.log(
   "🔍 BillingTab: createInsuranceItems returning",
   items.length,
   "items"
  );
  return items;
 };

 // Claim Payor options (static list)
 createClaimPayorItems = () => {
  return [
   "AYUSHMAN BHARAT(CREDIT)",
   "CORPORATE(CREDIT)",
   "ECHS(CREDIT)",
   "ESI(CREDIT)",
   "CGHS(CREDIT)",
   "WBHS(CREDIT)",
   "SWASTHYASATHI(CREDIT)",
   "ICAR(CREDIT)",
   "CESC(CREDIT)",
  ];
 };

 // Handle Claim Payor selection
 handleClaimPayorSelect = (selectedClaimPayor) => {
  if (selectedClaimPayor) {
   this.setState((s) => ({
    authForm: {
     ...s.authForm,
     claimPayor: selectedClaimPayor,
    },
    authFormDirty: true,
   }));
  }
 };

 // Authorization Type options (static list)
 createAuthTypeItems = () => {
  return ["Pre-Authorization", "Post-Authorization", "Final Authorization"];
 };

 // Handle Auth Type selection
 handleAuthTypeSelect = (selectedType) => {
  if (selectedType) {
   this.setState((s) => ({
    authForm: {
     ...s.authForm,
     type: selectedType,
    },
    authFormDirty: true,
   }));
  }
 };

 // Handle TPA selection
 handleTPASelect = (selectedTPA) => {
  const tpa = this.state.tpaOptions.find((t) => t.name === selectedTPA);
  if (tpa) {
   this.setState((s) => ({
    authForm: {
     ...s.authForm,
     tpaId: tpa.id,
     tpaName: tpa.name,
    },
    authFormDirty: true,
   }));
  }
 };

 // Handle Insurance selection
 handleInsuranceSelect = (selectedInsurance) => {
  const insurance = this.state.insuranceOptions.find(
   (i) => i.name === selectedInsurance
  );
  if (insurance) {
   this.setState((s) => ({
    authForm: {
     ...s.authForm,
     insuranceId: insurance.id,
     insuranceName: insurance.name,
    },
    authFormDirty: true,
   }));
  }
 };

 // Initialize all billing data in sequence
 initializeBillingData = async () => {
 try {
        this.setState({ isLoading: true, hasLoadedInitialData: false });

   // Invalidate any stale in-memory snapshot if parent marked a refresh
   // Preserve unsaved payments/authorization before clearing so they can be re-applied
   const surgeryId = this.getBookingId();
   const memKey = surgeryId ? `billing:${surgeryId}` : null;
   let preservedUnsaved = null;
   try {
    if (surgeryId && typeof window !== "undefined") {
     const invalidate =
      window?.__neoCache?.bookingById?.[surgeryId]?.__billingInvalidateMemory === true;
     if (invalidate) {
      if (memKey && BillingTab.memoryStore.has(memKey)) {
       preservedUnsaved = BillingTab.memoryStore.get(memKey);
       BillingTab.memoryStore.delete(memKey);
      }
      // Clear the flag so subsequent mounts use latest memory normally
      try {
       window.__neoCache.bookingById[surgeryId].__billingInvalidateMemory = false;
      } catch (e) {}
     }
    }
   } catch (e) {}

   // Attempt to restore from in-memory snapshot first so user edits survive tab switches
   const memSnap = memKey ? BillingTab.memoryStore.get(memKey) : null;

   if (memSnap) {
    // 1) Hydrate UI immediately from memory snapshot
    this.setState({
     authorization: memSnap.authorization || { entries: [], activeIndex: -1 },
     surgeryBills: Array.isArray(memSnap.surgeryBills)
      ? memSnap.surgeryBills.map((b) => ({
         ...b,
         combinedRatePaise: this.computePackageCostPaise(b),
        }))
      : this.state.surgeryBills,
     implantBill: memSnap.implantBill || this.state.implantBill,
     lineItems: memSnap.lineItems || this.state.lineItems,
     payments: memSnap.payments || this.state.payments,
     finalInvoice: memSnap.finalInvoice || this.state.finalInvoice,
     finalImplantInvoice:
      memSnap.finalImplantInvoice || this.state.finalImplantInvoice,
    });

    // 2) Notify parent of payment data from memory snapshot (for real-time cancellation validation)
    if (
     typeof this.props.onPaymentsUpdate === "function" &&
     memSnap.payments?.events?.length > 0
    ) {
     try {
      const paymentsForParent = memSnap.payments.events.map((e) => ({
       type: e.type === "Refund" ? "Refund" : "Payment",
       amount: e.amount || (e.amountPaise ? e.amountPaise / 100 : 0),
       amountPaise: e.amountPaise,
       mode: e.mode,
       ts: e.ts,
       doneBy: e.doneBy,
       moneyReceiptNo: e.moneyReceiptNo,
       remarks: e.paymentRemarks || e.remarks || "",
      }));
      this.props.onPaymentsUpdate(paymentsForParent);
     } catch (callbackErr) {
      console.warn(
       "Failed to notify parent of memory snapshot payment data:",
       callbackErr
      );
     }
    }

    // 3) Load surgery data and lookups in background without overwriting the hydrated bills
    const sd = await this.loadSurgeryBookingData();
    console.log(
     "🔄 [With Memory] Re-hydrating invoices from fresh surgery data"
    );
    this.hydrateInvoiceFromSurgery(sd);
    await Promise.all([
     this.loadAnesthesiaOptions(),
     this.loadTPAOptions(),
     this.loadInsuranceOptions(),
    ]);
    // Ensure totals are consistent
    this.recomputeOverallTotals();
    this.setState({ isLoading: false, hasLoadedInitialData: true });
    return; // Skip bootstrapping from server so we don't clobber user edits
   }

   // No memory snapshot → perform normal bootstrap
   // Step 1: Load surgery booking data first (uses shared cache if available)
   const sd = await this.loadSurgeryBookingData();
   this.hydrateInvoiceFromSurgery(sd);

   // Step 2: Load dropdown options before bootstrapping surgeries
   await Promise.all([
    this.loadAnesthesiaOptions(),
    this.loadTPAOptions(),
    this.loadInsuranceOptions(),
   ]);

   // Step 3: Bootstrap surgeries with loaded data (after options are loaded)
   this.bootstrapSurgeries();

   // Step 3.5: Re-bootstrap with proper anesthesia lookup now that options are loaded
   this.rebootstrapAnesthesia();

   // Step 3.6: Hydrate Implant/IOL bill items from surgery data if present
   this.hydrateImplantBillFromSurgery(sd);

   // Step 4: Load authorization (from booking getById), avoid snapshot API
   await this.loadAuthorizationFromSurgery();
   // Optionally hydrate payments from booking payload if available
   await this.loadPaymentsFromSurgery();

   // If we invalidated memory but had unsaved payments/auth, re-apply them (only when server had none)
   if (preservedUnsaved) {
    const hasServerPayments =
     Array.isArray(this.state.payments?.events) &&
     this.state.payments.events.length > 0;
    const hasUnsavedPayments =
     Array.isArray(preservedUnsaved?.payments?.events) &&
     preservedUnsaved.payments.events.length > 0;
    const hasServerAuth =
     Array.isArray(this.state.authorization?.entries) &&
     this.state.authorization.entries.length > 0;
    const hasUnsavedAuth =
     Array.isArray(preservedUnsaved?.authorization?.entries) &&
     preservedUnsaved.authorization.entries.length > 0;

    if (!hasServerPayments && hasUnsavedPayments) {
     this.setState(
      { payments: preservedUnsaved.payments },
      () => {
       this.recomputeOverallTotals();
       this.persistToMemory();
      }
     );
    }

    if (!hasServerAuth && hasUnsavedAuth) {
     this.setState(
      { authorization: preservedUnsaved.authorization },
      () => {
       this.persistToMemory();
      }
     );
    }
   }

   // Recompute totals to ensure everything is calculated
   this.recomputeOverallTotals();

   // Recompute surgery bill totals to ensure all calculations are up to date
   const updatedSurgeryBills = [...this.state.surgeryBills];
   updatedSurgeryBills.forEach((bill, idx) => {
    const totals = this.computeTotalsForSurgery(bill);
    bill.taxProc.absolutePaise = totals.procedureTaxPaise;
    bill.procedureCostPaise = totals.procedureCostPaise;
    bill.taxImplant.absolutePaise = totals.implantTaxPaise;
    bill.mouDiscountPaise =
     bill.mouDiscountPaise || totals.effectiveMouDiscountPaise;
    bill.subtotalPaise = totals.subtotalPaise;
   });

   // Step 9: Update state with recalculated bills and force re-render.
   // Opening the tab must never change the billed status; unbilling only
   // happens after an actual billing/surgery change.
   this.setState({ surgeryBills: updatedSurgeryBills }, () => {
    this.forceUpdate();
   });
  } catch (error) {
   console.error("Error initializing billing data:", error);
   cogoToast.error("Failed to initialize billing data");
    } finally {
        this.setState({ isLoading: false, hasLoadedInitialData: true });
    }
 };

 componentDidUpdate(prevProps) {
  // If the parent loads surgery later (async route fetch), initialize once
  const prevId = prevProps.surgery?.id || prevProps.surgery?._id;
  const currId = this.props.surgery?.id || this.props.surgery?._id;
  if (currId && prevId !== currId) {
   this.initializeBillingData();
  }

  // Check if surgery status has changed to cancelled
  const prevStatus = String(prevProps.surgery?.status || "")
   .toLowerCase()
   .trim();
  const currStatus = String(this.props.surgery?.status || "")
   .toLowerCase()
   .trim();
  if (prevStatus !== currStatus && currStatus === "cancelled") {
   // Update local surgeryData state to reflect the cancellation
   this.setState((s) => ({
    surgeryData: {
     ...(s.surgeryData || {}),
     status: this.props.surgery?.status,
    },
   }));
   cogoToast.warn("Surgery has been cancelled. Save actions are now disabled.");
  }

  const prevBilled = Boolean(prevProps.surgery?.isBilled);
  const currBilled = Boolean(this.props.surgery?.isBilled);
  if (prevBilled !== currBilled) {
   this.setState((s) => ({
    surgeryData: {
     ...(s.surgeryData || {}),
     isBilled: currBilled,
     markedBilledAt:
      this.props.surgery?.markedBilledAt ??
      s.surgeryData?.markedBilledAt ??
      null,
     markedBilledBy:
      this.props.surgery?.markedBilledBy ??
      s.surgeryData?.markedBilledBy ??
      null,
    },
   }));
  }

  if (prevProps.surgeries !== this.props.surgeries) {
   if (Array.isArray(this.props.surgeries) && this.props.surgeries.length) {
    // Only auto-initialize if user hasn't already added many rows
    if ((this.state.surgeryBills || []).length <= 1) {
     const bills = this.props.surgeries.map((s) =>
      this.createEmptySurgeryBill({
       printedName: s.printedName || s.procedure || s.name || "Surgery",
       surgeryName:
        s.surgeryName || s.printedName || s.procedure || s.name || "Surgery",
       group: s.group || s.payor || "General",
      })
     );
     this.setState({ surgeryBills: bills }, this.recomputeOverallTotals);
    }
   }
    // Refresh consolidated billing from server/cache to reflect latest add/remove without page reload
    this.refreshConsolidatedAfterSurgeryChange();
  }

  // Check if anesthesia selection has changed in parent and update billing accordingly
  const prevAnesthesia = prevProps.surgery?.anesthesia;
  const currAnesthesia = this.props.surgery?.anesthesia;
  if (prevAnesthesia !== currAnesthesia && currAnesthesia) {
   // Only update the dropdown selection from DB field. Do not override an existing non-zero rate here.
   const option = this.findAnesthesiaOption(currAnesthesia);
   if (option && this.state.surgeryBills[0]) {
    const updatedBills = [...this.state.surgeryBills];
    const existingRatePaise = Number(
     updatedBills[0].anaestheticChargesPaise || 0
    );
    updatedBills[0] = {
     ...updatedBills[0],
     selectedAnesthesiaId: option.id,
     anaestheticChargesPaise:
      existingRatePaise > 0
       ? existingRatePaise
       : Math.trunc((option.rate || 0) * 100),
    };
    this.setState({ surgeryBills: updatedBills }, () => {
     this.recomputeOverallTotals();
     // Do not auto-save here to avoid forcing a rate change
    });
   }
  }
 
  // Check if we need to re-bootstrap anesthesia when options become available
  if (
   currAnesthesia &&
   this.state.anesthesiaOptions.length > 0 &&
   this.state.surgeryBills.length > 0
  ) {
   const currentBill = this.state.surgeryBills[0];
   const expectedAnesthesiaId = this.findAnesthesiaOption(currAnesthesia)?.id;

   // If the current bill doesn't have the right anesthesia ID, re-bootstrap
   if (
    !currentBill.selectedAnesthesiaId ||
    currentBill.selectedAnesthesiaId !== expectedAnesthesiaId
   ) {
    console.log(
     "🔄 BillingTab: Anesthesia mismatch detected - current:",
     currentBill.selectedAnesthesiaId,
     "expected:",
     expectedAnesthesiaId
    );
    setTimeout(() => this.rebootstrapAnesthesia(), 100);
   }
  }

 const prevCb = prevProps.surgery?.consolidatedBilling;
 const currCb = this.props.surgery?.consolidatedBilling;
 const consolidatedChanged = this.hasConsolidatedChanged(prevCb, currCb);
 const anesthesiaChangedFromParent =
  (prevProps.surgery?.anesthesia || "") !== (this.props.surgery?.anesthesia || "");
 if (consolidatedChanged || anesthesiaChangedFromParent) {
  this.applyConsolidatedBillingFromProps(currCb, this.props.surgery?.anesthesia);
 }
 }

 bootstrapSurgeries = () => {
  // Create a single consolidated surgery bill from all selected surgeries
  const surgeryData = this.state.surgeryData;
  let consolidatedBill;

  // First check if there's already saved consolidated billing data
  if (surgeryData && surgeryData.consolidatedBilling) {
   const cb = surgeryData.consolidatedBilling;
   const surgeries = surgeryData.selectedSurgeries || [];
   const surgeryNames = surgeries
    .map((s) => s.name || s.printedName || "Surgery")
    .join(", ");
   const groups = [...new Set(surgeries.map((s) => s.payor || "General"))].join(
    ", "
   );

   // Decouple dropdown selection from rate calculation
   // Dropdown: Always use surgeryData.anesthesia (from API response)
   const anesthesiaStringForDropdown = surgeryData.anesthesia || "";
   const anesthesiaOptionForDropdown = this.findAnesthesiaOption(
    anesthesiaStringForDropdown
   );

   // Rate: Use consolidatedBilling value or fallback to calculated rate from dropdown selection
   const anesthesiaRateForFallback = anesthesiaOptionForDropdown
    ? anesthesiaOptionForDropdown.rate
    : 0;

   console.log(
    "🩺 BillingTab: Loading consolidated billing - dropdown anesthesia:",
    anesthesiaStringForDropdown,
    "fallback rate:",
    anesthesiaRateForFallback,
    "consolidated rate:",
    cb.anaestheticChargesPaise,
    "options:",
    this.state.anesthesiaOptions.length
   );

   consolidatedBill = this.createEmptySurgeryBill({
    printedName: surgeryNames || "Surgery",
    surgeryName: surgeryNames || "Surgery",
    group: groups || "General",
    surgeryRatePaise: cb.surgeryRatePaise || 0,
    anaestheticChargesPaise:
     typeof cb.anaestheticChargesPaise === "number"
      ? cb.anaestheticChargesPaise
      : Math.trunc(anesthesiaRateForFallback * 100),
    tissueProcessingPaise: cb.tissueProcessingPaise || 0,
    combinedRatePaise: cb.combinedRatePaise || 0,
    procedureTaxPercent: cb.procedureTaxPercent || 0,
    procedureCostPaise: Math.trunc((cb.procedureCostRupees || 0) * 100),
    procedureCostRupees: cb.procedureCostRupees || 0,
    implantCostPaise: cb.implantCostInclusivePaise || 0,
    implantTaxPercent: cb.implantTaxPercent || 0,
    femtoChargesPaise: cb.femtoChargesPaise || 0,
    specialDiscountPaise: cb.specialDiscountPaise || 0,
    specialDiscountRemarks: cb.specialDiscountRemarks || "",
    iolDiscountPaise: cb.iolDiscountPaise || 0,
    mouDiscountPercent: cb.mouDiscountPercent || 0,
    mouDiscountPaise: cb.mouDiscountPaise || 0,
    selectedAnesthesiaId: anesthesiaOptionForDropdown
     ? anesthesiaOptionForDropdown.id
     : anesthesiaStringForDropdown,
   });

   // Auto-calculate MoU discount amount if percentage exists but amount is missing/zero
   if ((cb.mouDiscountPercent || 0) > 0 && !(cb.mouDiscountPaise || 0)) {
    const baseAmount = this.calculateMouDiscountBase(consolidatedBill);
    const calculatedAmountRupees = (cb.mouDiscountPercent * baseAmount) / 100;
    consolidatedBill.mouDiscountPaise = Math.max(
     0,
     Math.round(calculatedAmountRupees * 100)
    );

    console.log("🔄 BillingTab: Auto-calculated MoU discount amount on load:", {
     percentage: cb.mouDiscountPercent,
     baseAmount,
     calculatedAmountRupees,
     calculatedAmountPaise: consolidatedBill.mouDiscountPaise,
    });
   }
  } else if (
   surgeryData &&
   surgeryData.selectedSurgeries &&
   Array.isArray(surgeryData.selectedSurgeries)
  ) {
   // Consolidate values across all selected surgeries
   const surgeries = surgeryData.selectedSurgeries;

   // Additive fields
   const totalSurgeryRate = surgeries.reduce(
    (sum, s) => sum + (s.pricing?.surgeryRate || 0) * 100,
    0
   );
   const totalTissueProcessing = surgeries.reduce(
    (sum, s) => sum + (s.pricing?.tissueProcessing || 0) * 100,
    0
   );
   const totalProcedureCost = surgeries.reduce(
    (sum, s) => sum + (s.pricing?.procedureCost || 0) * 100,
    0
   );
   const totalImplantCost = surgeries.reduce(
    (sum, s) => sum + (s.pricing?.implantCost || 0) * 100,
    0
   );
   const totalFemtoCharges = surgeries.reduce(
    (sum, s) => sum + (s.pricing?.femto || 0) * 100,
    0
   );
   const totalSpecialDiscount = surgeries.reduce(
    (sum, s) => sum + (s.discounts?.specialDiscount || 0) * 100,
    0
   );

   // Get anesthetic charges from surgery data anesthesia field (string at base level)
   const anesthesiaString = surgeryData.anesthesia || "";
   const anesthesiaOption = this.findAnesthesiaOption(anesthesiaString);
   const anesthesiaRate = anesthesiaOption ? anesthesiaOption.rate : 0;

   console.log(
    "🩺 BillingTab: Bootstrapping from selectedSurgeries - anesthesia:",
    anesthesiaString,
    "rate:",
    anesthesiaRate,
    "options:",
    this.state.anesthesiaOptions.length
   );

   // MoU Discount - if same across surgeries, use it, otherwise 0
   const mouDiscounts = surgeries.map((s) => s.discounts?.mouDiscount || 0);
   const mouDiscountPercent = mouDiscounts.every((d) => d === mouDiscounts[0])
    ? mouDiscounts[0]
    : 0;

   // Use first surgery's tax percentages as default
   const firstSurgery = surgeries[0];
   const procedureTaxPercent = firstSurgery?.pricing?.taxApplicable || 0;
   const implantTaxPercent = firstSurgery?.pricing?.implantTax || 0;

   // Create consolidated surgery names for display
   const surgeryNames = surgeries
    .map((s) => s.name || s.printedName || "Surgery")
    .join(", ");
   const groups = [...new Set(surgeries.map((s) => s.payor || "General"))].join(
    ", "
   );

   consolidatedBill = this.createEmptySurgeryBill({
    printedName: surgeryNames,
    surgeryName: surgeryNames,
    group: groups,
    surgeryRatePaise: Math.trunc(totalSurgeryRate),
    anaestheticChargesPaise: Math.trunc(anesthesiaRate * 100),
    tissueProcessingPaise: Math.trunc(totalTissueProcessing),
    procedureTaxPercent: procedureTaxPercent,
    procedureCostPaise: Math.trunc(totalProcedureCost),
    procedureCostRupees: totalProcedureCost / 100,
    implantCostPaise: Math.trunc(totalImplantCost),
    implantTaxPercent: implantTaxPercent,
    femtoChargesPaise: Math.trunc(totalFemtoCharges),
    specialDiscountPaise: Math.trunc(totalSpecialDiscount),
    specialDiscountRemarks:
     surgeries
      .map((s) => s.discounts?.specialDiscountRemarks)
      .filter(Boolean)
      .join("; ") || "",
    iolDiscountPaise: 0, // User will input this
    mouDiscountPercent: mouDiscountPercent,
    selectedAnesthesiaId: anesthesiaOption
     ? anesthesiaOption.id
     : anesthesiaString,
   });

   // Auto-calculate MoU discount amount if percentage exists but amount is missing/zero
   if (mouDiscountPercent > 0) {
    const baseAmount = this.calculateMouDiscountBase(consolidatedBill);
    const calculatedAmountRupees = (mouDiscountPercent * baseAmount) / 100;
    consolidatedBill.mouDiscountPaise = Math.max(
     0,
     Math.round(calculatedAmountRupees * 100)
    );

    console.log(
     "🔄 BillingTab: Auto-calculated MoU discount amount from selectedSurgeries:",
     {
      percentage: mouDiscountPercent,
      baseAmount,
      calculatedAmountRupees,
      calculatedAmountPaise: consolidatedBill.mouDiscountPaise,
     }
    );
   }
  } else {
   // Fallback to single surgery
   const surgery = this.props.surgery || {};
   const printed = surgery.procedure || surgery.printedName || "Surgery";
   const group = surgery.group || "General";

   // Try to get anesthesia from props.surgery
   const anesthesiaString = surgery.anesthesia || "";
   const anesthesiaOption = this.findAnesthesiaOption(anesthesiaString);
   const anesthesiaRate = anesthesiaOption ? anesthesiaOption.rate : 0;

   console.log(
    "🩺 BillingTab: Fallback surgery - anesthesia:",
    anesthesiaString,
    "rate:",
    anesthesiaRate
   );

   consolidatedBill = this.createEmptySurgeryBill({
    printedName: printed,
    surgeryName: printed,
    group,
    anaestheticChargesPaise: Math.trunc(anesthesiaRate * 100),
    selectedAnesthesiaId: anesthesiaOption
     ? anesthesiaOption.id
     : anesthesiaString,
   });
  }

  const initial = [consolidatedBill];

  this.setState({ surgeryBills: initial }, () => {
   // Recompute totals for the consolidated bill
   const updatedBills = [...initial];
   const totals = this.computeTotalsForSurgery(updatedBills[0]);
   updatedBills[0].taxProc.absolutePaise = totals.procedureTaxPaise;
   updatedBills[0].procedureCostPaise = totals.procedureCostPaise;
   updatedBills[0].taxImplant.absolutePaise = totals.implantTaxPaise;
   updatedBills[0].mouDiscountPaise =
    updatedBills[0].mouDiscountPaise || totals.effectiveMouDiscountPaise;
   updatedBills[0].subtotalPaise = totals.subtotalPaise;

   this.setState({ surgeryBills: updatedBills }, () => {
    this.recomputeOverallTotals();
   });
  });
 };
 
// Refresh consolidated billing when selected surgeries change in parent (called from componentDidUpdate)
refreshConsolidatedAfterSurgeryChange = async () => {
  try {
    // Preserve any in-progress payments in memory; only hydrate from server if empty
    const hasLocalPayments =
      Array.isArray(this.state.payments?.events) && this.state.payments.events.length > 0;

   const surgeryId = this.getBookingId();
   if (!surgeryId) return;
   // Drop memory snapshot so we don't hydrate stale values
   try {
    const memKey = `billing:${surgeryId}`;
    if (BillingTab.memoryStore.has(memKey)) {
     BillingTab.memoryStore.delete(memKey);
    }
   } catch (e) {}
   // Load latest booking (uses shared cache or fresh API) and rebuild bills from consolidatedBilling
   const sd = await this.loadSurgeryBookingData();
   if (sd) {
    // Rebuild consolidated bill based on the latest surgeryData in state
    this.bootstrapSurgeries();
    this.rebootstrapAnesthesia();
      // Rehydrate payments from booking only if we don't already have local entries
      if (!hasLocalPayments) {
        await this.loadPaymentsFromSurgery();
      }
    this.recomputeOverallTotals();
   }
  } catch (e) {
   // non-blocking
  }
 };

 hasConsolidatedChanged = (prevCb, nextCb) => {
  if (!prevCb && !nextCb) return false;
  if (!prevCb || !nextCb) return true;
  return this.consolidatedFieldsToWatch.some((field) => {
   const prevVal = typeof prevCb[field] === "undefined" ? null : prevCb[field];
   const nextVal = typeof nextCb[field] === "undefined" ? null : nextCb[field];
   if (typeof prevVal === "number" || typeof nextVal === "number") {
    return Number(prevVal ?? 0) !== Number(nextVal ?? 0);
   }
   return prevVal !== nextVal;
  });
 };

 applyConsolidatedBillingFromProps = (consolidatedBilling, anesthesiaSelection) => {
  const cb = consolidatedBilling;
  if (!cb && !anesthesiaSelection) return;
  if (!Array.isArray(this.state.surgeryBills) || this.state.surgeryBills.length === 0) {
   this.bootstrapSurgeries();
   return;
  }

  this.updateSurgeryBill(
   0,
   (sb) => {
    if (!sb) return sb;
    const next = { ...sb };
    if (cb) {
     if (typeof cb.surgeryRatePaise === "number") next.surgeryRatePaise = cb.surgeryRatePaise;
     if (typeof cb.anaestheticChargesPaise === "number") next.anaestheticChargesPaise = cb.anaestheticChargesPaise;
     if (typeof cb.tissueProcessingPaise === "number") next.tissueProcessingPaise = cb.tissueProcessingPaise;
     if (typeof cb.combinedRatePaise === "number") next.combinedRatePaise = cb.combinedRatePaise;
     if (typeof cb.procedureTaxPercent === "number") {
      next.taxProc = this.splitTaxPct(cb.procedureTaxPercent);
     }
     if (typeof cb.procedureCostRupees === "number") {
      next.procedureCostRupees = cb.procedureCostRupees;
      next.procedureCostPaise = Math.max(0, Math.trunc(Number(cb.procedureCostRupees) * 100));
     }
     if (typeof cb.implantCostInclusivePaise === "number") next.implantCostInclusivePaise = cb.implantCostInclusivePaise;
     if (typeof cb.implantTaxPercent === "number") {
      next.taxImplant = this.splitTaxPct(cb.implantTaxPercent);
     }
     if (typeof cb.femtoChargesPaise === "number") next.femtoChargesPaise = cb.femtoChargesPaise;
     if (typeof cb.specialDiscountPaise === "number") next.specialDiscountPaise = cb.specialDiscountPaise;
     if (typeof cb.specialDiscountRemarks !== "undefined") next.specialDiscountRemarks = cb.specialDiscountRemarks || "";
     if (typeof cb.iolDiscountPaise === "number") next.iolDiscountPaise = cb.iolDiscountPaise;
     if (typeof cb.mouDiscountPercent === "number") next.mouDiscountPercent = cb.mouDiscountPercent;
     if (typeof cb.mouDiscountPaise === "number") next.mouDiscountPaise = cb.mouDiscountPaise;
     if (typeof cb.selectedAnesthesiaId !== "undefined") next.selectedAnesthesiaId = cb.selectedAnesthesiaId || "";
    }

    const anesthesiaToApply = cb?.selectedAnesthesiaId || anesthesiaSelection;
    if (anesthesiaToApply) {
     next.selectedAnesthesiaId = anesthesiaToApply;
     if (!cb?.anaestheticChargesPaise) {
      const option = this.findAnesthesiaOption(anesthesiaToApply);
      if (option) {
       next.anaestheticChargesPaise = Math.trunc(Number(option.rate || 0) * 100);
      }
     }
    }

    return next;
   },
   { skipDirtyFlag: true }
  );

  if (cb || anesthesiaSelection) {
   this.setState((prev) => ({
    surgeryData: {
     ...(prev.surgeryData || {}),
     consolidatedBilling: cb || prev.surgeryData?.consolidatedBilling,
     anesthesia: anesthesiaSelection || prev.surgeryData?.anesthesia,
    },
   }));
  }
 };

 // Re-bootstrap anesthesia after options are loaded
 rebootstrapAnesthesia = () => {
  const surgeryData = this.state.surgeryData;
  const surgeryFromProps = this.props.surgery;

  // Decouple dropdown selection from rate calculation
  // Dropdown: Always use surgeryData.anesthesia (from API response)
  const cb = surgeryData?.consolidatedBilling;
  const anesthesiaStringForDropdown =
   surgeryData?.anesthesia || surgeryFromProps?.anesthesia || "";

  if (!anesthesiaStringForDropdown || this.state.surgeryBills.length === 0) {
   console.log("🚫 BillingTab: No anesthesia to re-bootstrap:", {
    anesthesiaStringForDropdown,
    billsLength: this.state.surgeryBills.length,
   });
   return;
  }

  const anesthesiaOptionForDropdown = this.findAnesthesiaOption(
   anesthesiaStringForDropdown
  );

  if (anesthesiaOptionForDropdown && this.state.surgeryBills[0]) {
   console.log(
    "🔄 BillingTab: Re-bootstrapping anesthesia:",
    anesthesiaStringForDropdown,
    "->",
    anesthesiaOptionForDropdown
   );

   // Dropdown selection: Always use the anesthesia from API response
   const resolvedSelectedId =
    anesthesiaOptionForDropdown.id || anesthesiaStringForDropdown;

   // Rate: Use consolidatedBilling value or fallback to calculated rate from dropdown selection
   const resolvedChargesPaise =
    typeof cb?.anaestheticChargesPaise === "number"
     ? Number(cb.anaestheticChargesPaise)
     : Math.trunc(Number(anesthesiaOptionForDropdown?.rate || 0) * 100);

   const updatedBills = [...this.state.surgeryBills];
   const existingRatePaise = Number(
    updatedBills[0].anaestheticChargesPaise || 0
   );
   const keepExistingRate = existingRatePaise > 0;
   updatedBills[0] = {
    ...updatedBills[0],
    selectedAnesthesiaId: resolvedSelectedId,
    anaestheticChargesPaise: keepExistingRate
     ? existingRatePaise
     : resolvedChargesPaise,
   };

   // Recompute totals
   const totals = this.computeTotalsForSurgery(updatedBills[0]);
   updatedBills[0].taxProc.absolutePaise = totals.procedureTaxPaise;
   updatedBills[0].procedureCostPaise = totals.procedureCostPaise;
   updatedBills[0].taxImplant.absolutePaise = totals.implantTaxPaise;
   updatedBills[0].mouDiscountPaise =
    updatedBills[0].mouDiscountPaise || totals.effectiveMouDiscountPaise;
   updatedBills[0].subtotalPaise = totals.subtotalPaise;

   this.setState({ surgeryBills: updatedBills }, () => {
    console.log(
     "✅ BillingTab: Anesthesia re-bootstrapped successfully - dropdown should now show:",
     resolvedSelectedId
    );
    this.recomputeOverallTotals();
   });
  } else {
   console.warn("🚨 BillingTab: Could not re-bootstrap anesthesia:", {
    anesthesiaStringForDropdown,
    foundOption: anesthesiaOptionForDropdown,
    hasBills: !!this.state.surgeryBills[0],
    optionsCount: this.state.anesthesiaOptions.length,
   });
  }
 };

 createEmptySurgeryBill = ({
  printedName = "Surgery",
  surgeryName = "Surgery",
  group = "General",
  // Pricing data from surgery booking
  surgeryRatePaise = 0,
  anaestheticChargesPaise = 0,
  tissueProcessingPaise = 0,
  procedureTaxPercent = 0,
  implantCostPaise = 0,
  implantTaxPercent = 0,
  femtoChargesPaise = 0,
  specialDiscountPaise = 0,
  specialDiscountRemarks = "",
  iolDiscountPaise = 0,
  mouDiscountPercent = 0,
  mouDiscountPaise = 0,
  procedureCostPaise = 0,
  procedureCostRupees = null,
  // Anesthesia selection
  selectedAnesthesiaId = "",
 } = {}) => ({
  header: { printedName, surgeryName, group },
  // Rows - use provided values or defaults
  surgeryRatePaise, // Row 2
  anaestheticChargesPaise, // Row 3
  selectedAnesthesiaId, // Anesthesia dropdown selection
  tissueProcessingPaise, // Row 4
  // Row 5: Package Cost (derived, not editable)
  combinedRatePaise: Math.max(
   0,
   Math.trunc(
    Number(surgeryRatePaise || 0) +
     Number(anaestheticChargesPaise || 0) +
     Number(tissueProcessingPaise || 0)
   )
  ),
  // Row 6: Procedure tax % and absolute
  taxProc: { ...this.splitTaxPct(procedureTaxPercent), absolutePaise: 0 },
  procedureCostPaise, // Row 7 (auto, paise-based calc)
  procedureCostRupees, // Optional: raw rupees from payload for direct display
  implantCostInclusivePaise: implantCostPaise, // Row 8 (editable)
  // Row 9: Implant tax on (Implant - IOL Discount)
  taxImplant: { ...this.splitTaxPct(implantTaxPercent), absolutePaise: 0 },
  femtoChargesPaise, // Row 10
  specialDiscountPaise, // Row 11
  specialDiscountRemarks, // Row 11 remarks
  iolDiscountPaise, // Row 12
  mouDiscountPercent, // Row 13 (percent)
  mouDiscountPaise, // Row 13 (absolute, from saved data)
  // Computed
  subtotalPaise: 0,
 });

 getBookingId = () => this.props.surgery?.id || this.props.surgery?._id;

isSurgeryCancelled = () => {
    try {
        const status = String(
            this.state.surgeryData?.status || this.props.surgery?.status || ""
        )
            .toLowerCase()
            .trim();
        return status === "cancelled";
    } catch (e) {
        return false;
    }
};

isCashPayorCase = () => {
    try {
        const payorPanel =
            this.state.surgeryData?.payorPanel || this.props.surgery?.payorPanel || "";
        return String(payorPanel || "").trim().toLowerCase() === "cash";
    } catch (e) {
        return false;
    }
};

 blockIfCancelled = () => {
  if (this.isSurgeryCancelled()) {
   cogoToast.warn("Action not possible for cancelled surgeries");
   return true;
  }
  return false;
 };

isCurrentlyBilled = () =>
    Boolean(this.state.surgeryData?.isBilled || this.props.surgery?.isBilled);

getLocalPayorPanel = () => {
    const sd = this.state.surgeryData || {};
    return (
        sd.payorPanel ||
        this.props.surgery?.payorPanel ||
        (sd.selectedSurgeries || [])[0]?.payor ||
        ""
    );
};

// Payments and authorizations are saved as whole arrays, so a tab working on
// an outdated copy would overwrite entries added elsewhere. Before any billing
// write, compare against the server and reload the page if anything moved.
guardFreshBeforeBillingWrite = async () => {
    const surgeryId = this.getBookingId();
    if (!surgeryId) return { ok: true, fresh: null };
    try {
        const res = await apiCallGet(
            `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getById/${surgeryId}?_ts=${Date.now()}`
        );
        const fresh = res?.success ? res.data : null;
        if (!fresh) return { ok: true, fresh: null };

        const norm = (v) => String(v || "").trim().toLowerCase();
        const freshPanel = fresh.payorPanel || (fresh.selectedSurgeries || [])[0]?.payor || "";
        const freshPayments = Array.isArray(fresh.payments?.events)
            ? fresh.payments.events.length
            : Array.isArray(fresh.payments)
            ? fresh.payments.length
            : 0;
        const freshAuths = Array.isArray(fresh.authorization?.entries)
            ? fresh.authorization.entries.length
            : 0;
        const localStatus =
            this.props.surgery?.status || this.state.surgeryData?.status || "";

        const changes = [];
        if (norm(freshPanel) !== norm(this.getLocalPayorPanel())) {
            changes.push(`payor panel is now "${freshPanel || "-"}"`);
        }
        if (Boolean(fresh.isBilled) !== this.isCurrentlyBilled()) {
            changes.push(fresh.isBilled ? "it was marked billed" : "it was marked not billed");
        }
        if (norm(fresh.status) !== norm(localStatus)) {
            changes.push(`status is now "${fresh.status || "-"}"`);
        }
        if (freshPayments !== (this.state.payments?.events || []).length) {
            changes.push("payments were added or changed");
        }
        if (freshAuths !== (this.state.authorization?.entries || []).length) {
            changes.push("authorizations were added or changed");
        }
        if (changes.length === 0) return { ok: true, fresh };

        BillingTab.memoryStore.delete(`billing:${surgeryId}`);
        if (typeof this.props.onServerStateChanged === "function") {
            this.props.onServerStateChanged(changes);
        } else {
            cogoToast.warn(
                `This surgery was updated elsewhere (${changes.join(", ")}). Reloading...`,
                { hideAfter: 6 }
            );
            setTimeout(() => window.location.reload(), 1500);
        }
        return { ok: false, fresh };
    } catch (err) {
        console.error("Billing freshness check failed:", err);
        cogoToast.error("Could not verify the latest billing data. Please try again.");
        return { ok: false, fresh: null };
    }
};

// Index of the most recent authorization entry (entries are normally
// prepended, but older data may not be ordered).
getLatestAuthIndex = (entries = this.state.authorization?.entries || []) => {
    let latestIdx = entries.length ? 0 : -1;
    let latestTs = -Infinity;
    entries.forEach((e, idx) => {
        const t = e?.ts ? new Date(e.ts).getTime() : NaN;
        if (!Number.isNaN(t) && t > latestTs) {
            latestTs = t;
            latestIdx = idx;
        }
    });
    return latestIdx;
};

isFinalAuthType = (type) => {
    const t = String(type || "").toLowerCase().replace(/[\s-]+/g, "");
    return t === "finalauthorization" || t === "finalauth" || t === "final";
};

// An older authorization cannot be re-activated once a newer Final
// Authorization exists; otherwise the balance is computed from the old amount
// while the invoice uses the latest one.
getSetActiveBlockReason = (idx) => {
    const entries = this.state.authorization?.entries || [];
    const target = entries[idx];
    if (!target) return "Authorization not found";
    const targetTs = target.ts ? new Date(target.ts).getTime() : NaN;
    const newerFinal = entries.some((e, i) => {
        if (i === idx || !this.isFinalAuthType(e?.type)) return false;
        const t = e?.ts ? new Date(e.ts).getTime() : NaN;
        if (Number.isNaN(t) || Number.isNaN(targetTs)) return i < idx;
        return t > targetTs;
    });
    return newerFinal
        ? "A newer Final Authorization exists. Only the latest Final Authorization can be active."
        : null;
};

// Billing must satisfy the rules of the CURRENT payor panel: Cash needs the
// balance settled by payments; any other panel needs the latest authorization
// to be an active Final Authorization, with nothing left to pay.
getMarkBilledBlockReason = () => {
    const { balance } = this.computeSummary();
    const isCash = String(this.getLocalPayorPanel()).trim().toLowerCase() === "cash";
    if (!isCash) {
        const entries = this.state.authorization?.entries || [];
        const latestIdx = this.getLatestAuthIndex(entries);
        if (latestIdx < 0 || !this.isFinalAuthType(entries[latestIdx]?.type)) {
            return "This booking is under a payor panel. Add a Final Authorization before marking it billed.";
        }
        if (this.state.authorization?.activeIndex !== latestIdx) {
            return "The latest Final Authorization must be the active authorization before marking billed.";
        }
    }
    if (Number(balance) !== 0) {
        return "Balance must be zero to mark as billed";
    }
    return null;
};

// Maximum amount a new Payment may collect (in paise).
getMaxCollectablePaise = () => {
    const { balance } = this.computeSummary();
    return Math.max(0, Math.round(Number(balance) || 0));
};

emitSurgeryPatchToParent = (partial) => {
    try {
        if (
            partial &&
            typeof partial === "object" &&
            typeof this.props.onSurgeryDataChange === "function"
        ) {
            this.props.onSurgeryDataChange(partial);
        }
    } catch (e) {}
};

requestBalanceCheck = async (reason = "balance-check") => {
    try {
        if (typeof this.props.onBalanceCheck === "function") {
            const summary = this.computeSummary();
            await this.props.onBalanceCheck(reason, {
                balancePaise: summary?.balance ?? 0,
                totalReceivablePaise: summary?.totalReceivable ?? 0,
                authorizedPaise: summary?.authorized ?? 0,
                netPayablePaise: summary?.netPayable ?? 0,
                collectedPaise: summary?.collected ?? 0,
                refundedPaise: summary?.refunded ?? 0,
                source: reason || "billing-tab",
            });
        }
    } catch (err) {
        console.warn("BillingTab: balance check request failed", {
            reason,
            err,
        });
    }
};

 // Method to refresh anesthesia when tab becomes active
 refreshAnesthesia = () => {
  console.log("🔄 BillingTab: refreshAnesthesia called");
  const currentAnesthesia =
   this.state.surgeryData?.consolidatedBilling?.selectedAnesthesiaId ||
   this.state.surgeryData?.anesthesia ||
   this.props.surgery?.anesthesia;

  if (currentAnesthesia) {
   console.log("🔄 BillingTab: Refreshing anesthesia for:", currentAnesthesia);

   // Load anesthesia options if not already loaded
   if (this.state.anesthesiaOptions.length === 0) {
    this.loadAnesthesiaOptions();
   } else {
    // If options are already loaded, just re-bootstrap
    this.rebootstrapAnesthesia();
   }
  } else {
   console.log("🚫 BillingTab: No anesthesia to refresh");
  }
 };

 // Helper method to find anesthesia option by ID or name (more flexible matching)
 findAnesthesiaOption = (anesthesiaString) => {
  if (!anesthesiaString || !this.state.anesthesiaOptions.length) return null;

  // Try exact ID match first
  let option = this.state.anesthesiaOptions.find(
   (opt) => opt.id === anesthesiaString
  );
  if (option) return option;

  // Try exact name match
  option = this.state.anesthesiaOptions.find(
   (opt) => opt.name === anesthesiaString
  );
  if (option) return option;

  // Try case-insensitive name match
  option = this.state.anesthesiaOptions.find(
   (opt) => opt.name.toLowerCase() === anesthesiaString.toLowerCase()
  );
  if (option) return option;

  // Try partial match (contains)
  option = this.state.anesthesiaOptions.find(
   (opt) =>
    opt.name.toLowerCase().includes(anesthesiaString.toLowerCase()) ||
    anesthesiaString.toLowerCase().includes(opt.name.toLowerCase())
  );

  console.log("🔍 BillingTab: Anesthesia lookup:", {
   searchString: anesthesiaString,
   foundOption: option,
   availableOptions: this.state.anesthesiaOptions.map((o) => ({
    id: o.id,
    name: o.name,
   })),
  });

  return option || null;
 };

 // Update anesthesia selection in billing when parent component changes it
 updateAnesthesiaInBilling = (anesthesiaSelection) => {
  if (!anesthesiaSelection || this.state.surgeryBills.length === 0) return;

  // Find the anesthesia option to get the rate
  const anesthesiaOption = this.findAnesthesiaOption(anesthesiaSelection);

  if (anesthesiaOption) {
   console.log(
    "🩺 BillingTab: Updating anesthesia selection:",
    anesthesiaSelection,
    "rate:",
    anesthesiaOption.rate
   );

   // Update the first (consolidated) surgery bill with new anesthesia
   const updatedBills = [...this.state.surgeryBills];
   if (updatedBills[0]) {
    const existingRatePaise = Number(
     updatedBills[0].anaestheticChargesPaise || 0
    );
    updatedBills[0] = {
     ...updatedBills[0],
     selectedAnesthesiaId: anesthesiaOption.id,
     anaestheticChargesPaise:
      existingRatePaise > 0
       ? existingRatePaise
       : Math.trunc((anesthesiaOption.rate || 0) * 100),
    };

    // Recompute totals for the updated bill
    const totals = this.computeTotalsForSurgery(updatedBills[0]);
    updatedBills[0].taxProc.absolutePaise = totals.procedureTaxPaise;
    updatedBills[0].procedureCostPaise = totals.procedureCostPaise;
    updatedBills[0].taxImplant.absolutePaise = totals.implantTaxPaise;
    updatedBills[0].mouDiscountPaise =
     updatedBills[0].mouDiscountPaise || totals.effectiveMouDiscountPaise;
    updatedBills[0].subtotalPaise = totals.subtotalPaise;

    this.setState({ surgeryBills: updatedBills }, () => {
     this.recomputeOverallTotals();
    // Auto-save the updated anesthesia selection only if rate was not already present.
    // Skipped for billed bookings: this runs while the tab loads, and a background
    // save there could unbill a booking nobody edited.
    if (existingRatePaise === 0 && !this.isCurrentlyBilled()) {
     this.saveSurgeryLine(0, { silent: true });
    }
     // Audit: anesthesia updated programmatically via parent
     try {
      const entry = this.buildAuditEntry({
       actionType: "UPDATE",
       actionSlug: `Changed anesthesia to ${
        anesthesiaOption.name || anesthesiaOption.id
       }`,
       actionMetadata: {
        panel: "BILLING",
        fieldName: "surgeryBills[0].selectedAnesthesiaId",
        changeType: "UPDATE",
        previousValue: "",
        newValue: anesthesiaOption.id,
        anesthesiaName: anesthesiaOption.name || "",
       },
      });
      this.sendAuditEntries([entry]);
     } catch (e) {}
    });
   }
  } else {
   console.warn(
    "🚨 BillingTab: Could not find anesthesia option for:",
    anesthesiaSelection
   );
  }
 };

 // Load surgery booking data from neoSxBookingMaster API
 loadSurgeryBookingData = async () => {
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   return undefined;
  }

  try {
   // Reuse cross-page cache/single-flight if present (set by parent details page)
   if (typeof window !== "undefined") {
    window.__neoCache = window.__neoCache || {};
    window.__neoInflight = window.__neoInflight || {};
    window.__neoCache.bookingById = window.__neoCache.bookingById || {};
    window.__neoInflight.bookingById = window.__neoInflight.bookingById || {};
   }

   let response;
   // Check if cached data exists and has implantInvoices field (cache validation)
   const cachedData = window?.__neoCache?.bookingById?.[surgeryId];
   const isCacheValid = cachedData && "implantInvoices" in cachedData;

   if (isCacheValid) {
    console.log("✅ Using valid cached data with implantInvoices");
    response = {
     success: true,
     data: cachedData,
    };
   } else {
    // Invalid or missing cache - fetch fresh data
    if (cachedData && !("implantInvoices" in cachedData)) {
     console.log(
      "⚠️ Cache exists but missing implantInvoices field - forcing refresh"
     );
     delete window.__neoCache.bookingById[surgeryId];
    }

    // Check for in-flight request
    if (window?.__neoInflight?.bookingById?.[surgeryId]) {
     response = await window.__neoInflight.bookingById[surgeryId];
     if (response?.success && response?.data) {
      window.__neoCache.bookingById[surgeryId] = response.data;
     }
    } else {
     // Make fresh API call
     const promise = apiCallGet(
      `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getById/${surgeryId}`
     ).finally(() => {
      try {
       if (window?.__neoInflight?.bookingById)
        delete window.__neoInflight.bookingById[surgeryId];
      } catch (e) {}
     });
     if (window?.__neoInflight?.bookingById) {
      window.__neoInflight.bookingById[surgeryId] = promise;
     }
     response = await promise;
     if (response?.success && response?.data) {
      window.__neoCache.bookingById[surgeryId] = response.data;
     }
    }
   }

   if (response && response.success && response.data) {
    // Persist surgery data for downstream usage
    console.log(
    "📦 Surgery data loaded, implantInvoices:",
    response.data.implantInvoices
    );
    this.setState({ surgeryData: response.data });
    return response.data;
   } else {
    console.warn("No surgery data found for ID:", surgeryId);
    return undefined;
   }
  } catch (error) {
   console.error("Error loading surgery booking data:", error);
   cogoToast.error("Failed to load surgery booking data");
   return undefined;
  }
 };

// Save consolidated surgery billing data via PATCH update
saveSurgeryLine = async (idx, options = {}) => {
 const { silent = false } = options;
 if (this.blockIfCancelled()) return;
 const surgeryId = this.getBookingId();
 if (!surgeryId) return;
 if (silent && this.consolidatedAutoSaveInFlight) return;
 const wasBilledBeforeSave = this.isCurrentlyBilled();
 this.clearConsolidatedAutoSaveTimer();
 try {
  if (silent) {
      this.consolidatedAutoSaveInFlight = true;
  } else {
      this.setState({ isSaving: true });
  }
   const sb = this.state.surgeryBills[idx];
   // Compute previous consolidated snapshot from state.surgeryData (if exists) for before-values
   const prevConsolidated =
    this.state.surgeryData && this.state.surgeryData.consolidatedBilling
     ? {
        surgeryRatePaise:
         this.state.surgeryData.consolidatedBilling.surgeryRatePaise || 0,
        anaestheticChargesPaise:
         this.state.surgeryData.consolidatedBilling.anaestheticChargesPaise ||
         0,
        tissueProcessingPaise:
         this.state.surgeryData.consolidatedBilling.tissueProcessingPaise || 0,
        combinedRatePaise:
         this.state.surgeryData.consolidatedBilling.combinedRatePaise || 0,
        procedureTaxPercent:
         this.state.surgeryData.consolidatedBilling.procedureTaxPercent || 0,
        procedureCostRupees:
         this.state.surgeryData.consolidatedBilling.procedureCostRupees || 0,
        implantCostInclusivePaise:
         this.state.surgeryData.consolidatedBilling.implantCostInclusivePaise ||
         0,
        implantTaxPercent:
         this.state.surgeryData.consolidatedBilling.implantTaxPercent || 0,
        femtoChargesPaise:
         this.state.surgeryData.consolidatedBilling.femtoChargesPaise || 0,
        specialDiscountPaise:
         this.state.surgeryData.consolidatedBilling.specialDiscountPaise || 0,
        specialDiscountRemarks:
         this.state.surgeryData.consolidatedBilling.specialDiscountRemarks ||
         "",
        iolDiscountPaise:
         this.state.surgeryData.consolidatedBilling.iolDiscountPaise || 0,
        mouDiscountPercent:
         this.state.surgeryData.consolidatedBilling.mouDiscountPercent || 0,
        mouDiscountPaise:
         this.state.surgeryData.consolidatedBilling.mouDiscountPaise || 0,
        selectedAnesthesiaId:
         this.state.surgeryData.consolidatedBilling.selectedAnesthesiaId || "",
       }
     : {};
   const original = Array.isArray(this.state.surgeryData?.selectedSurgeries)
    ? this.state.surgeryData.selectedSurgeries
    : [];

   // Since we're consolidating, we need to distribute the consolidated values back to individual surgeries
   // For now, we'll update the consolidated billing data structure
   const procedureCostRupees =
    sb.procedureCostRupees != null
     ? Number(sb.procedureCostRupees || 0)
     : Math.round((Number(sb.procedureCostPaise || 0) / 100) * 100) / 100;

   // Store consolidated billing data separately from individual surgery pricing
   const payload = {
    surgeryBookingId: surgeryId,
    consolidatedBilling: {
     surgeryRatePaise: sb.surgeryRatePaise || 0,
     anaestheticChargesPaise: sb.anaestheticChargesPaise || 0,
     tissueProcessingPaise: sb.tissueProcessingPaise || 0,
     combinedRatePaise: sb.combinedRatePaise || 0,
     procedureTaxPercent: (sb.taxProc?.cgst || 0) + (sb.taxProc?.sgst || 0),
     procedureCostRupees: procedureCostRupees,
     implantCostInclusivePaise: sb.implantCostInclusivePaise || 0,
     implantTaxPercent: (sb.taxImplant?.cgst || 0) + (sb.taxImplant?.sgst || 0),
     femtoChargesPaise: sb.femtoChargesPaise || 0,
     specialDiscountPaise: sb.specialDiscountPaise || 0,
     specialDiscountRemarks: sb.specialDiscountRemarks || "",
     iolDiscountPaise: sb.iolDiscountPaise || 0,
     mouDiscountPercent: sb.mouDiscountPercent || 0,
     mouDiscountPaise: sb.mouDiscountPaise || 0,
     selectedAnesthesiaId: sb.selectedAnesthesiaId || "",
    },
    // Keep the original selectedSurgeries structure intact
    selectedSurgeries: original,
    // Store anesthesia as string at base level
    anesthesia: sb.selectedAnesthesiaId || "",
   };

   await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    payload
   );
   // Audit: consolidated billing persisted with before/after diff
   try {
    const nextConsolidated = {
     surgeryRatePaise: payload.consolidatedBilling.surgeryRatePaise,
     anaestheticChargesPaise:
      payload.consolidatedBilling.anaestheticChargesPaise,
     tissueProcessingPaise: payload.consolidatedBilling.tissueProcessingPaise,
     combinedRatePaise: payload.consolidatedBilling.combinedRatePaise,
     procedureTaxPercent: payload.consolidatedBilling.procedureTaxPercent,
     procedureCostRupees: payload.consolidatedBilling.procedureCostRupees,
     implantCostInclusivePaise:
      payload.consolidatedBilling.implantCostInclusivePaise,
     implantTaxPercent: payload.consolidatedBilling.implantTaxPercent,
     femtoChargesPaise: payload.consolidatedBilling.femtoChargesPaise,
     specialDiscountPaise: payload.consolidatedBilling.specialDiscountPaise,
     specialDiscountRemarks:
      payload.consolidatedBilling.specialDiscountRemarks || "",
     iolDiscountPaise: payload.consolidatedBilling.iolDiscountPaise,
     mouDiscountPercent: payload.consolidatedBilling.mouDiscountPercent,
     mouDiscountPaise: payload.consolidatedBilling.mouDiscountPaise,
     selectedAnesthesiaId: payload.consolidatedBilling.selectedAnesthesiaId,
    };
    const { previousValue, newValue } = this.computeConsolidatedDiff(
     prevConsolidated,
     nextConsolidated
    );
    const changedKeys = Object.keys(newValue || {});
    if (changedKeys.length) {
     const entries = changedKeys
      .map((k) => {
       const label = this.getConsolidatedFieldLabel(k);
       const fromDisp = this.formatConsolidatedDisplayValue(
        k,
        previousValue[k]
       );
       const toDisp = this.formatConsolidatedDisplayValue(k, newValue[k]);
       return this.buildAuditEntry({
        actionType: "UPDATE",
        actionSlug: `Saved consolidated billing (${label}: ${fromDisp} → ${toDisp})`,
        actionMetadata: {
         panel: "BILLING",
         fieldName: `consolidatedBilling.${k}`,
         changeType: "PERSIST",
         previousValue: fromDisp,
         newValue: toDisp,
         rawPreviousValue:
          typeof previousValue[k] !== "undefined" ? previousValue[k] : "",
         rawNewValue: typeof newValue[k] !== "undefined" ? newValue[k] : "",
        },
       });
      })
      .filter(Boolean);
     if (entries.length) {
      await this.sendAuditEntries(entries);
     }
    }
   } catch (auditErr) {}
    if (!silent) {
        cogoToast.success("Consolidated billing saved");
    } else {
        cogoToast.info("Consolidated billing auto-saved");
    }

   // Keep local copy in sync
  await new Promise((resolve) =>
   this.setState(
    (s) => ({
     surgeryData: {
      ...(s.surgeryData || {}),
      consolidatedBilling: payload.consolidatedBilling,
      anesthesia: payload.anesthesia,
     },
     hasUnsavedBillingChanges: false,
    }),
    resolve
   )
  );

  this.emitSurgeryPatchToParent({
   consolidatedBilling: payload.consolidatedBilling,
   anesthesia: payload.anesthesia,
  });

  const summaryAfterSave = this.computeSummary();
  const balanceAfterSave =
      summaryAfterSave && typeof summaryAfterSave.balance === "number"
          ? summaryAfterSave.balance
          : 0;
  const shouldAutoUnbill =
      wasBilledBeforeSave && Math.abs(Math.round(balanceAfterSave)) > 0;
  if (shouldAutoUnbill) {
      await this.autoUnbillAfterBillingChanges("consolidated-save");
  }
  } catch (e) {
   cogoToast.error("Failed to save consolidated billing");
   console.error("Save error:", e);
  } finally {
   if (silent) {
       this.consolidatedAutoSaveInFlight = false;
   } else {
       this.setState({ isSaving: false });
   }
  }
 };

 // Load billing via API if available; else fallback to memory
 loadBillingData = async () => {
  const surgeryId = this.getBookingId();
  if (!surgeryId) return;
  try {
   this.setState({ isLoading: true });
   const payload = { surgeryBookingId: surgeryId };
   const res = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/sxBilling/getSnapshot`,
    payload
   );
   if (res && res.success) {
    const data = res.data || {};
    // If snapshot has no authorization entries, but surgeryData does,
    // merge in authorization from surgery to pre-render initial data
    let merged = data;
    try {
     const authFromSurgery = this.state.surgeryData?.authorization;
     const hasEntries =
      Array.isArray(data.authorization?.entries) &&
      data.authorization.entries.length > 0;
     const hasSurgeryEntries =
      Array.isArray(authFromSurgery?.entries) &&
      authFromSurgery.entries.length > 0;
     if (!hasEntries && hasSurgeryEntries) {
      merged = { ...data, authorization: authFromSurgery };
     }
    } catch (e) {}
    this.setState(
     this.normalizeLoadedData(merged),
     this.recomputeOverallTotals
    );
    return;
   }
   throw new Error("Snapshot API not available");
  } catch (err) {
   // Try to load authorization data from surgery booking
   await this.loadAuthorizationFromSurgery();

   const key = `billing:${surgeryId}`;
   if (BillingTab.memoryStore.has(key)) {
    this.setState(BillingTab.memoryStore.get(key), this.recomputeOverallTotals);
   } else {
    this.persistToMemory();
   }
  } finally {
   this.setState({ isLoading: false });
  }
 };

 // Removed: loadConsumptionItemsFromSurgery (no longer needed)

 // Load authorization data from surgery booking
 loadAuthorizationFromSurgery = async () => {
  const surgeryData = this.state.surgeryData;

  if (
   surgeryData &&
   surgeryData.authorization &&
   surgeryData.authorization.entries
  ) {
   try {
    const rawAuth = surgeryData.authorization;
    const entries = Array.isArray(rawAuth.entries) ? rawAuth.entries : [];
    const normalizedEntries = entries.map((e) => ({
     ...e,
     ts: e?.ts?.$date
      ? new Date(e.ts.$date)
      : e?.ts
      ? new Date(e.ts)
      : new Date(),
    }));
    this.setState(
     {
      authorization: {
       entries: normalizedEntries,
       activeIndex:
        typeof rawAuth.activeIndex === "number" && rawAuth.activeIndex >= 0
         ? rawAuth.activeIndex
         : normalizedEntries.length
         ? 0
         : -1,
      },
     },
     () => {}
    );
   } catch (e) {
    this.setState(
     {
      authorization: surgeryData.authorization,
     },
     () => {}
    );
   }
  } else {
   // Initialize empty authorization structure
   this.setState(
    {
     authorization: {
      entries: [],
      activeIndex: 0,
     },
    },
    () => {}
   );
  }
 };

 // Load payments from surgery booking (supports rupees or paise in payload)
 loadPaymentsFromSurgery = async () => {
  const p = this.state.surgeryData?.payments;
  if (!p) return;
  try {
   const mapEvt = (e) => {
    const amountPaise =
     typeof e?.amountPaise === "number"
      ? e.amountPaise
      : Math.trunc(Number(e?.amount || 0) * 100);
    const typeNormalized = e?.type === "Refund" ? "Refund" : "Payment";
    return {
     ts: e?.ts ? new Date(e.ts) : new Date(),
     type: typeNormalized,
     mode: e?.mode || "Cash",
     amountPaise,
     amount: typeof e?.amount === "number" ? e.amount : amountPaise / 100,
     doneBy: e?.doneBy || this.props.authStore?.name || "Unknown",
     moneyReceiptNo:
      e?.moneyReceiptNo || e?.moneyReceiptNo || e?.moneyReceiptNo,
     paymentRemarks: e?.paymentRemarks ?? e?.remarks ?? "",
    };
   };
   const events = Array.isArray(p.events) ? p.events.map(mapEvt) : [];
   const totalPaidPaise =
    typeof p?.totalPaidPaise === "number"
     ? p.totalPaidPaise
     : Math.trunc(Number(p?.totalPaid || 0) * 100);
   const totalRefundedPaise =
    typeof p?.totalRefundedPaise === "number"
     ? p.totalRefundedPaise
     : Math.trunc(Number(p?.totalRefunded || 0) * 100);
   this.setState({
    payments: { events, totalPaidPaise, totalRefundedPaise },
   });

   // Notify parent component about initial payment data (for real-time cancellation validation)
   if (typeof this.props.onPaymentsUpdate === "function" && events.length > 0) {
    try {
     // Convert events to format expected by parent
     const paymentsForParent = events.map((e) => ({
      type: e.type === "Refund" ? "Refund" : "Payment",
      amount: e.amount || (e.amountPaise ? e.amountPaise / 100 : 0),
      amountPaise: e.amountPaise,
      mode: e.mode,
      ts: e.ts,
      doneBy: e.doneBy,
      moneyReceiptNo: e.moneyReceiptNo,
      remarks: e.paymentRemarks || "",
     }));
     this.props.onPaymentsUpdate(paymentsForParent);
    } catch (callbackErr) {
     console.warn(
      "Failed to notify parent of initial payment data:",
      callbackErr
     );
    }
   }
  } catch (e) {}
 };

 // Hydrate Implant/IOL bill items from surgery booking (persisted on server)
 hydrateImplantBillFromSurgery = (surgeryData) => {
  try {
   const items = Array.isArray(surgeryData?.implantBillItems)
    ? surgeryData.implantBillItems
    : Array.isArray(surgeryData?.implantBill?.items)
    ? surgeryData.implantBill.items
    : null;
   if (Array.isArray(items) && items.length > 0) {
    // Enforce qty=1 for all rows
    const normalized = items.map((r) => ({
     itemName: r.itemName || "",
     hsnCode: r.hsnCode || "90213900",
     serialNo: r.serialNo || "",
     expiry: r.expiry || this.formatToISTDate(new Date()) || "",
     qty: 1,
     pricePaise: Math.max(0, Number(r.pricePaise || 0)),
     cgst: Math.max(0, Number(r.cgst || 0)),
     sgst: Math.max(0, Number(r.sgst || 0)),
     totalPaise: Math.max(0, Number(r.totalPaise || 0)),
    }));
    this.setState(
     {
      implantBill: { ...this.state.implantBill, items: normalized },
     },
     this.recomputeIolTotals
    );
   } else {
    // If no saved implant bill items, auto-load from consumptions
    this.hydrateImplantBillFromConsumptions();
   }
  } catch (e) {}
 };

 // Hydrate Implant/IOL bill from consumption data
 hydrateImplantBillFromConsumptions = async (showToast = false) => {
  try {
   if (showToast) {
    cogoToast.loading("Loading items from consumption...");
   }
   this.setState({ isLoading: true });

   // Get consumptions from props (passed from parent component)
   const consumptions = Array.isArray(this.props.itemConsumptions)
    ? this.props.itemConsumptions
    : [];

   console.log("🔥🔥🔥 ===== CONSUMPTION DATA FROM TAB ===== 🔥🔥🔥");
   console.log(
    "Full itemConsumptions:",
    JSON.stringify(this.props.itemConsumptions, null, 2)
   );
   console.log("Number of consumptions:", consumptions.length);
   console.log("🔥🔥🔥 ================================= 🔥🔥🔥");

   if (consumptions.length === 0) {
    if (showToast) {
     cogoToast.info("No consumptions found for this surgery");
    }
    this.setState({ isLoading: false });
    return;
   }

   // Get the total implant cost from surgery bill
   const firstSurgeryBill = this.state.surgeryBills?.[0];
   const implantCostPaise = firstSurgeryBill?.implantCostInclusivePaise || 0;
   const iolDiscountPaise = firstSurgeryBill?.iolDiscountPaise || 0;
   const totalImplantCostPaise = Math.max(
    0,
    implantCostPaise - iolDiscountPaise
   );

   // First, count total items to calculate per-item cost
   let totalItemCount = 0;
   consumptions.forEach((consumption) => {
    if (Array.isArray(consumption.consumedItems)) {
     totalItemCount += consumption.consumedItems.length;
    }
   });

   // Total per item (what shows in TOTAL column)
   const totalPerItemPaise =
    totalItemCount > 0 ? Math.trunc(totalImplantCostPaise / totalItemCount) : 0;

   // Back-calculate price (what shows in PRICE column) - removing 5% tax
   const pricePerItemPaise = Math.trunc(totalPerItemPaise / 1.05);

   console.log("💰 Price calculation:", {
    implantCostPaise,
    iolDiscountPaise,
    totalImplantCostPaise,
    totalItemCount,
    totalPerItemPaise: totalPerItemPaise,
    totalPerItemRupees: totalPerItemPaise / 100,
    pricePerItemPaise: pricePerItemPaise,
    pricePerItemRupees: pricePerItemPaise / 100,
   });

   // Extract consumed items from all consumptions
   const consumedItems = [];
   const itemsMap = new Map(); // Use map to avoid duplicates based on serial number

   consumptions.forEach((consumption, consumptionIndex) => {
    console.log(
     `\n📦 CONSUMPTION #${consumptionIndex + 1} - Full structure:`,
     consumption
    );

    if (Array.isArray(consumption.consumedItems)) {
     consumption.consumedItems.forEach((item, itemIndex) => {
      // Map fields directly from consumption item
      const itemName = item.itemNameWithVariants || item.itemName || "";
      const serialNo = item.serialNumber || "";
      const batchNo = item.batchNumber || "";
      const hsnCode = item.itemHSN || item.itemHsnCode || item.hsnCode || "";
      const expiryDate = item.expiryDate || "";

      console.log("🔍 Item from consumption:", {
       "item.itemName": item.itemName,
       "item.itemNameWithVariants": item.itemNameWithVariants,
       "extracted itemName": itemName,
      });

      // Format expiry date from ISO string
      let formattedExpiry = "";
      if (expiryDate) {
       try {
        const date = new Date(expiryDate);
        if (!isNaN(date.getTime())) {
         formattedExpiry = this.formatToISTDate(date);
        }
       } catch (e) {
        console.error("Error formatting date:", e);
       }
      }

      console.log("  ✅ Mapped fields:", {
       itemName,
       serialNo,
       batchNo,
       hsnCode,
       expiryDate: expiryDate,
       formattedExpiry,
      });

      // Create unique key
      const uniqueKey = batchNo || serialNo || `${itemName}-${Math.random()}`;

      if (!itemsMap.has(uniqueKey)) {
       const implantItem = {
        itemName: itemName,
        hsnCode: hsnCode,
        serialNo: serialNo || batchNo, // Use batch if serial is empty
        expiry: formattedExpiry,
        qty: 1,
        pricePaise: pricePerItemPaise, // Back-calculated price (excluding tax)
        cgst: 2.5,
        sgst: 2.5,
        totalPaise: totalPerItemPaise, // Total from surgery bill
       };

       console.log("✅ Adding implantItem to bill:", implantItem);
       itemsMap.set(uniqueKey, implantItem);
       consumedItems.push(implantItem);
      }
     });
    }
   });

   if (consumedItems.length > 0) {
    if (showToast) {
     cogoToast.success(
      `Loaded ${consumedItems.length} item(s) from consumption`
     );
    }
    this.setState(
     {
      implantBill: { ...this.state.implantBill, items: consumedItems },
     },
     this.recomputeIolTotals
    );
   } else {
    if (showToast) {
     cogoToast.warn("No items found in consumptions");
    }
   }
  } catch (error) {
   console.error("Error hydrating implant bill from consumptions:", error);
   if (showToast) {
    cogoToast.error("Failed to load items from consumption");
   }
  } finally {
   this.setState({ isLoading: false });
  }
 };

 // Format consumption expiry date to DD-MM-YYYY
 formatConsumptionExpiry = (expiryValue) => {
  try {
   // Default: 1 year from now
   const getDefaultDate = () => {
    const futureDate = new Date();
    futureDate.setFullYear(futureDate.getFullYear() + 1);
    return this.formatToISTDate(futureDate);
   };

   if (!expiryValue || expiryValue === "") {
    return getDefaultDate();
   }

   // If it's already in DD-MM-YYYY format, validate and return
   if (
    typeof expiryValue === "string" &&
    /^\d{2}-\d{2}-\d{4}$/.test(expiryValue)
   ) {
    const [dd, mm, yyyy] = expiryValue.split("-").map((x) => parseInt(x, 10));
    // Validate the date components
    if (
     yyyy >= 1900 &&
     yyyy <= 2100 &&
     mm >= 1 &&
     mm <= 12 &&
     dd >= 1 &&
     dd <= 31
    ) {
     const testDate = new Date(yyyy, mm - 1, dd);
     if (
      testDate.getFullYear() === yyyy &&
      testDate.getMonth() === mm - 1 &&
      testDate.getDate() === dd
     ) {
      return expiryValue;
     }
    }
   }

   // If it's a Date object
   if (expiryValue instanceof Date) {
    if (!isNaN(expiryValue.getTime())) {
     return this.formatToISTDate(expiryValue);
    }
   }

   // Try to parse as ISO string or other formats
   if (typeof expiryValue === "string" || typeof expiryValue === "number") {
    const date = new Date(expiryValue);
    if (
     !isNaN(date.getTime()) &&
     date.getFullYear() >= 1900 &&
     date.getFullYear() <= 2100
    ) {
     return this.formatToISTDate(date);
    }
   }

   // If all else fails, return default
   return getDefaultDate();
  } catch (e) {
   const futureDate = new Date();
   futureDate.setFullYear(futureDate.getFullYear() + 1);
   return this.formatToISTDate(futureDate);
  }
 };

 normalizeLoadedData = (snap) => {
  const entries = Array.isArray(snap.authorization?.entries)
   ? snap.authorization.entries
   : [];
  const surgeryBills = Array.isArray(snap.surgeryBills)
   ? snap.surgeryBills
   : this.state.surgeryBills;
  const implantBill = snap.implantBill || this.state.implantBill;
  return {
   authorization: {
    entries: entries.sort((a, b) => new Date(b.ts) - new Date(a.ts)),
    activeIndex:
     typeof snap.authorization?.activeIndex === "number" &&
     snap.authorization?.activeIndex >= 0
      ? snap.authorization.activeIndex
      : entries.length
      ? 0
      : -1,
   },
   surgeryBills,
   implantBill,
   lineItems: snap.lineItems || this.state.lineItems,
   payments: snap.payments || this.state.payments,
   finalInvoice: snap.finalInvoice || this.state.finalInvoice,
   finalImplantInvoice:
    snap.finalImplantInvoice || this.state.finalImplantInvoice,
  };
 };

 persistToMemory = () => {
  const surgeryId = this.getBookingId();
  if (!surgeryId) return;
  const key = `billing:${surgeryId}`;
  const snapshot = {
   authorization: this.state.authorization,
   surgeryBills: this.state.surgeryBills,
   implantBill: this.state.implantBill,
   lineItems: this.state.lineItems,
   payments: this.state.payments,
   finalInvoice: this.state.finalInvoice,
   finalImplantInvoice: this.state.finalImplantInvoice,
  };
  BillingTab.memoryStore.set(key, snapshot);
 };

 // Save handler attempts API first then stores to memory
 // options.skipAutoUnbill: e.g. after admin clears authorizations — do not flip isBilled / wipe billed metadata
 saveBillingData = async (options = {}) => {
  if (this.blockIfCancelled()) return;
  const surgeryId = this.getBookingId();
  if (!surgeryId) return;
 const hadDirtyChanges = this.state.hasUnsavedBillingChanges;
 const skipAutoUnbill = Boolean(options.skipAutoUnbill);
 let saveSucceeded = false;
  try {
   cogoToast.loading("Saving billing data");
   this.setState({ isSaving: true });
   const payload = {
    surgeryBookingId: surgeryId,
    authorization: this.state.authorization,
    surgeryBills: this.state.surgeryBills,
    implantBill: this.state.implantBill,
    lineItems: this.state.lineItems,
    payments: this.state.payments,
    finalInvoice: this.state.finalInvoice,
   };
   const res = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/sxBilling/saveSnapshot`,
    payload
   );
   if (res && res.success) {
   saveSucceeded = true;
    cogoToast.success("Billing saved");
    // Audit: snapshot saved (server)
    try {
     const entry = this.buildAuditEntry({
      actionType: "UPDATE",
      actionSlug: "Saved billing snapshot (server)",
      actionMetadata: {
       panel: "BILLING",
       fieldName: "billing.snapshot",
       changeType: "SNAPSHOT",
       previousValue: "",
       newValue: {
        source: "server",
        counts: {
         authEntries: (this.state.authorization?.entries || []).length,
         surgeryBills: (this.state.surgeryBills || []).length,
         iolRows: (this.state.implantBill?.items || []).length,
         paymentEvents: (this.state.payments?.events || []).length,
        },
       },
      },
     });
     // this.sendAuditEntries([entry]);
    } catch (e) {}
   } else {
    throw new Error("Save API not available");
   }
  } catch (err) {
   this.persistToMemory();
   cogoToast.success("Saved locally (temporary)");
   // Audit: snapshot saved (memory)
   try {
    const entry = this.buildAuditEntry({
     actionType: "UPDATE",
     actionSlug: "Saved billing snapshot (local)",
     actionMetadata: {
      panel: "BILLING",
      fieldName: "billing.snapshot",
      changeType: "SNAPSHOT",
      previousValue: "",
      newValue: {
       source: "memory",
       counts: {
        authEntries: (this.state.authorization?.entries || []).length,
        surgeryBills: (this.state.surgeryBills || []).length,
        iolRows: (this.state.implantBill?.items || []).length,
        paymentEvents: (this.state.payments?.events || []).length,
       },
      },
     },
    });
    // this.sendAuditEntries([entry]);
   } catch (e) {}
 } finally {
  const shouldClearDirty = saveSucceeded;
const shouldAutoUnbill =
  saveSucceeded &&
  hadDirtyChanges &&
  this.isCurrentlyBilled() &&
  !skipAutoUnbill;
  await this.afterBillingSave({
   clearDirty: shouldClearDirty,
  autoUnbill: shouldAutoUnbill,
  });
 }
 };

afterBillingSave = async ({ clearDirty, autoUnbill }) => {
 await new Promise((resolve) =>
  this.setState(
   (prev) => ({
    isSaving: false,
    hasUnsavedBillingChanges: clearDirty
     ? false
     : prev.hasUnsavedBillingChanges,
   }),
   resolve
  )
 );
 if (autoUnbill) {
  await this.autoUnbillAfterBillingChanges("billing-save");
 }
 };

autoUnbillAfterBillingChanges = async (reason = "unspecified") => {
  try {
   const surgeryId = this.getBookingId();
   if (!surgeryId) return;
  const billedFlag = this.isCurrentlyBilled();
  if (!billedFlag) return;

   const res = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    { isBilled: false }
   );
   if (res?.success) {
    this.setState((prev) => ({
     surgeryData: {
      ...(prev.surgeryData || {}),
      isBilled: false,
      // Keep markedBilledAt / markedBilledBy as audit trail; only isBilled flips
     },
    }));
  this.emitSurgeryPatchToParent({
   isBilled: false,
  });
    try {
     if (typeof this.props.onStatusChange === "function") {
      this.props.onStatusChange({
       isBilled: false,
      });
     }
    } catch (e) {}
    cogoToast.info("Billing updated. Booking marked as not billed.");
   }
  } catch (err) {
   console.error("Failed to auto unbill after billing changes:", {
       reason,
       err,
   });
  }
 };

 // Derived summary metrics
 computeOverallTotals = () => {
  const totals = this.state.surgeryBills.reduce(
   (acc, s) => {
    acc.grandTotalPaise += s.subtotalPaise || 0;
    return acc;
   },
   { grandTotalPaise: 0 }
  );
  return totals;
 };

 computeSummary = () => {
  const { grandTotalPaise } = this.computeOverallTotals();
  // Convert everything to rupees for consistent calculations
  const totalReceivableRupees = grandTotalPaise / 100;
  let authorizedRupees = 0;
  // A Cash booking is settled by payments only; authorizations left over from
  // an earlier PPN panel must not reduce its balance.
  const isCash = String(this.getLocalPayorPanel()).trim().toLowerCase() === "cash";
  if (!isCash && this.state.authorization.activeIndex >= 0) {
   const active =
    this.state.authorization.entries[this.state.authorization.activeIndex];
   authorizedRupees = active?.amountRupees || 0;
  }
  const collectedRupees = (this.state.payments.totalPaidPaise || 0) / 100;
  const refundedRupees = (this.state.payments.totalRefundedPaise || 0) / 100;
  const netPayableRupees = totalReceivableRupees - authorizedRupees;
  const balanceRupees = netPayableRupees - collectedRupees + refundedRupees;

  // Return values in paise for display consistency with indianRupee function
  return {
   totalReceivable: Math.round(totalReceivableRupees * 100),
   authorized: Math.round(authorizedRupees * 100),
   netPayable: Math.round(netPayableRupees * 100),
   collected: Math.round(collectedRupees * 100),
   refunded: Math.round(refundedRupees * 100),
   balance: Math.round(balanceRupees * 100),
  };
 };

 // Lookups
 loadTPAOptions = async () => {
  if (this.state.isLoadingTPA) return;

  // Allow reloading if options are empty
  if (this.state.tpaOptions.length > 0) {
   console.log(
    "✅ BillingTab: TPA options already loaded:",
    this.state.tpaOptions.length,
    "items"
   );
   return;
  }

  try {
   console.log("🔄 BillingTab: Loading TPA options...");
   this.setState({ isLoadingTPA: true });
   const res = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxTPAMaster/getAll`,
    {}
   );
   const arr = Array.isArray(res) ? res : res?.data?.entities || [];
   const mapped = arr.map((x) => ({
    id: x._id || x.id,
    name: x.panelName || x.name || x.tpaName || "Unnamed TPA",
   }));
   this.setState({ tpaOptions: mapped, isLoadingTPA: false });
   console.log(
    "✅ BillingTab: TPA options loaded successfully:",
    mapped.length,
    "items"
   );
  } catch (err) {
   console.error("❌ BillingTab: Error loading TPA options:", err);
   this.setState({ isLoadingTPA: false });
  }
 };

 loadInsuranceOptions = async () => {
  if (this.state.isLoadingInsurance) return;

  // Allow reloading if options are empty
  if (this.state.insuranceOptions.length > 0) {
   console.log(
    "✅ BillingTab: Insurance options already loaded:",
    this.state.insuranceOptions.length,
    "items"
   );
   return;
  }

  try {
   console.log("🔄 BillingTab: Loading Insurance options...");
   this.setState({ isLoadingInsurance: true });
   const res = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxInsuranceCompanyMaster/getAll`,
    {}
   );
   const arr = Array.isArray(res) ? res : res?.data?.entities || res || [];
   const mapped = arr.map((x) => ({
    id: x._id || x.id,
    name:
     x.companyName ||
     x.insurerName ||
     x.panelName ||
     x.name ||
     "Unnamed Insurer",
   }));
   this.setState({ insuranceOptions: mapped, isLoadingInsurance: false });
   console.log(
    "✅ BillingTab: Insurance options loaded successfully:",
    mapped.length,
    "items"
   );
  } catch (err) {
   console.error("❌ BillingTab: Error loading Insurance options:", err);
   this.setState({ isLoadingInsurance: false });
  }
 };

 loadAnesthesiaOptions = async () => {
  if (this.state.isLoadingAnesthesia) return;

  // Allow reloading if options are empty or if called during initialization
  if (this.state.anesthesiaOptions.length > 0) {
   return;
  }
  try {
   this.setState({ isLoadingAnesthesia: true });

   // Use same cache as other components for consistency
   if (window.__neoCache?.anesthesiaOptionsForSurgery) {
    const options = window.__neoCache.anesthesiaOptionsForSurgery;
    const mapped = options.map((o) => ({
     id: o.value, // Use value as id for consistency
     name: o.label,
     rate: o.rate || 0,
    }));
    this.setState(
     { anesthesiaOptions: mapped, isLoadingAnesthesia: false },
     () => {
      // After options are loaded, check if we need to re-bootstrap anesthesia
      const currentAnesthesia =
       this.state.surgeryData?.anesthesia || this.props.surgery?.anesthesia;
      if (currentAnesthesia && this.state.surgeryBills.length > 0) {
       console.log(
        "🔄 BillingTab: Cached options loaded, re-bootstrapping anesthesia for:",
        currentAnesthesia
       );
       setTimeout(() => this.rebootstrapAnesthesia(), 100);
      }
     }
    );
    console.log(
     "✅ BillingTab: Using cached anesthesia options:",
     mapped.length,
     "options"
    );
    return;
   }

   if (window.__neoInflight?.getAnesthesiaForSurgery) {
    const response = await window.__neoInflight.getAnesthesiaForSurgery;
    const items = Array.isArray(response?.data)
     ? response.data
     : Array.isArray(response)
     ? response
     : [];
    const options = items
     .filter(
      (item) =>
       item && typeof item.name === "string" && item.name.trim().length > 0
     )
     .map((item) => ({
      value: item.name,
      label: item.name,
      rate: item.rate || 0,
     }));
    const mapped = options.map((o) => ({
     id: o.value,
     name: o.label,
     rate: o.rate || 0,
    }));
    this.setState(
     { anesthesiaOptions: mapped, isLoadingAnesthesia: false },
     () => {
      // After options are loaded, check if we need to re-bootstrap anesthesia
      const currentAnesthesia =
       this.state.surgeryData?.anesthesia || this.props.surgery?.anesthesia;
      if (currentAnesthesia && this.state.surgeryBills.length > 0) {
       console.log(
        "🔄 BillingTab: Inflight options loaded, re-bootstrapping anesthesia for:",
        currentAnesthesia
       );
       setTimeout(() => this.rebootstrapAnesthesia(), 100);
      }
     }
    );
    if (typeof window !== "undefined")
     window.__neoCache.anesthesiaOptionsForSurgery = options;
    console.log(
     "✅ BillingTab: Using inflight anesthesia options:",
     mapped.length,
     "options"
    );
    return;
   }

   window.__neoInflight.getAnesthesiaForSurgery = apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/getAll`,
    {}
   );
   const res = await window.__neoInflight.getAnesthesiaForSurgery;

   const arr = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res)
    ? res
    : [];
   const options = arr
    .filter(
     (item) =>
      item && typeof item.name === "string" && item.name.trim().length > 0
    )
    .map((item) => ({
     value: item.name,
     label: item.name,
     rate: item.rate || 0,
    }));
   const mapped = options.map((o) => ({
    id: o.value,
    name: o.label,
    rate: o.rate || 0,
   }));

   this.setState(
    { anesthesiaOptions: mapped, isLoadingAnesthesia: false },
    () => {
     // After options are loaded, check if we need to re-bootstrap anesthesia
     const currentAnesthesia =
      this.state.surgeryData?.anesthesia || this.props.surgery?.anesthesia;
     if (currentAnesthesia && this.state.surgeryBills.length > 0) {
      console.log(
       "🔄 BillingTab: Options loaded, re-bootstrapping anesthesia for:",
       currentAnesthesia
      );
      setTimeout(() => this.rebootstrapAnesthesia(), 100);
     }
    }
   );
   if (typeof window !== "undefined")
    window.__neoCache.anesthesiaOptionsForSurgery = options;

   console.log(
    "✅ BillingTab: Anesthesia options loaded successfully:",
    mapped.length,
    "options"
   );
   console.log("🩺 BillingTab: Sample anesthesia option:", mapped[0]);
  } catch (err) {
   this.setState({ isLoadingAnesthesia: false });
   console.error("Error loading anesthesia options:", err);
  }
 };

 // Surgery Payment Master lookup (with fallback endpoint)
 loadSurgeryMasterOptions = async () => {
  if (
   this.state.isLoadingSurgeryMaster ||
   this.state.surgeryMasterOptions.length
  )
   return;
  try {
   this.setState({ isLoadingSurgeryMaster: true });
   let arr = [];
   try {
    const resV3 = await apiCallPost(
     `/v3/${this.props.authStore.prefix}/neoSxPricingMaster/getAll`,
     {}
    );
    arr = Array.isArray(resV3) ? resV3 : resV3?.data?.entities || resV3 || [];
   } catch (e1) {
    try {
     const resMeta = await apiCallPost(
      `/meta-sx/${this.props.authStore.prefix}/neo-sxPricingMaster/getAll`,
      {}
     );
     arr = Array.isArray(resMeta) ? resMeta : resMeta?.data || resMeta || [];
    } catch (e2) {
     arr = [];
    }
   }
   const options = (arr || []).map((x) => {
    const id =
     x._id ||
     x.id ||
     `${x.printedName || x.surgeryName || "Surgery"}-${x.group || "General"}`;
    const pn = x.printedName || x.surgeryName || "Surgery";
    const sn = x.surgeryName || x.printedName || pn;
    const grp = x.group || "General";
    const label = `${pn} - ${sn} - ${grp}`;
    return { id, label, record: x };
   });
   this.setState({
    surgeryMasterOptions: options,
    isLoadingSurgeryMaster: false,
   });
  } catch (err) {
   this.setState({ isLoadingSurgeryMaster: false });
  }
 };

 splitTaxPct = (totalPct) => {
  const p = Number(totalPct || 0);
  const half = Math.max(0, p / 2);
  // Keep halves precise up to 2 decimals (e.g., 5 -> 2.5/2.5)
  const cgst = Math.round(half * 100) / 100;
  const sgst = Math.round((p - half) * 100) / 100;
  return { cgst, sgst };
 };

 createSurgeryBillFromMaster = (m) => {
  const bill = this.createEmptySurgeryBill({
   printedName: m.printedName || m.surgeryName || "Surgery",
   surgeryName: m.surgeryName || m.printedName || "Surgery",
   group: m.group || "General",
   procedureCostPaise: Math.trunc((m.procedureCost || 0) * 100),
   procedureTaxPercent: m.taxApplicable || 0,
   implantTaxPercent: m.implantTax || 0,
  });
  bill.surgeryRatePaise = Math.trunc((m.surgeryRate || 0) * 100);
  bill.anaestheticChargesPaise = Math.trunc((m.anaestheticCharges || 0) * 100);
  bill.tissueProcessingPaise = Math.trunc((m.tissueProcessing || 0) * 100);
  const proc = this.splitTaxPct(m.taxApplicable || 0);
  bill.taxProc = { ...bill.taxProc, ...proc };
  bill.implantCostInclusivePaise = Math.trunc((m.implantCost || 0) * 100);
  const imp = this.splitTaxPct(m.implantTax || 0);
  bill.taxImplant = { ...bill.taxImplant, ...imp };
  bill.femtoChargesPaise = Math.trunc((m.femto || 0) * 100);
  bill.specialDiscountPaise = Math.trunc((m.specialDiscount || 0) * 100);
  bill.iolDiscountPaise = Math.trunc((m.iolDiscount || 0) * 100);
  bill.mouDiscountPercent = Number(m.mouDiscount || 0);

  const totals = this.computeTotalsForSurgery(bill);
  bill.taxProc.absolutePaise = totals.procedureTaxPaise;
  bill.procedureCostPaise = totals.procedureCostPaise;
  bill.taxImplant.absolutePaise = totals.implantTaxPaise;
  bill.mouDiscountPaise =
   bill.mouDiscountPaise || totals.effectiveMouDiscountPaise;
  bill.subtotalPaise = totals.subtotalPaise;
  // Audit: applying master template
  try {
   const entry = this.buildAuditEntry({
    actionType: "APPLY",
    actionSlug: `Applied Surgery Payment Master: ${
     m.printedName || m.surgeryName || "Surgery"
    }`,
    actionMetadata: {
     panel: "BILLING",
     fieldName: `surgeryBills[0]`,
     changeType: "APPLY",
     previousValue: "",
     newValue: {
      surgeryRatePaise: bill.surgeryRatePaise,
      anaestheticChargesPaise: bill.anaestheticChargesPaise,
      tissueProcessingPaise: bill.tissueProcessingPaise,
      taxProcPct: m.taxApplicable || 0,
      implantCostInclusivePaise: bill.implantCostInclusivePaise,
      taxImplantPct: m.implantTax || 0,
      femtoChargesPaise: bill.femtoChargesPaise,
      specialDiscountPaise: bill.specialDiscountPaise,
      iolDiscountPaise: bill.iolDiscountPaise,
      mouDiscountPercent: bill.mouDiscountPercent,
      subtotalPaise: bill.subtotalPaise,
     },
    },
   });
   this.sendAuditEntries([entry]);
  } catch (e) {}
  return bill;
 };

 // Upload authorization letter
 handleLetterUpload = async (file) => {
  if (!file) return;
  if (!this.getBookingId()) return;
  try {
   this.setState((s) => ({
    authForm: { ...s.authForm, isUploading: true },
   }));
   const fd = new FormData();
   fd.append("associatedEntityId", this.getBookingId());
   fd.append("identifier", `auth-letter-${file.name}`);
   fd.append("file", file);
   const uploadRes = await apiCallPostMultipartProgress(
    `/v3/${this.props.authStore.prefix}/neoFiles/uploadNew`,
    fd,
    () => {}
   );

   const prevFile = {
    fileId: this.state.authForm.letterFileId || "",
    fileName: this.state.authForm.letterFileName || "",
   };
   const fileId =
    uploadRes?.data?._id ||
    uploadRes?._id ||
    uploadRes?.file?._id ||
    uploadRes?.id ||
    "";
   const fileName =
    uploadRes?.data?.originalName ||
    uploadRes?.originalName ||
    uploadRes?.fileName ||
    file.name;

   this.setState((s) => ({
    authForm: {
     ...s.authForm,
     letterFileId: fileId,
     letterFileName: fileName,
     isUploading: false,
    },
    authFormDirty: true,
   }));

   // Audit: authorization letter upload
   const entry = this.buildAuditEntry({
    actionType: "UPDATE",
    actionSlug: `Uploaded authorization letter: ${fileName}`,
    actionMetadata: {
     panel: "BILLING",
     fieldName: "authorization.letter",
     changeType: "UPDATE",
     previousValue: prevFile,
     newValue: { fileId, fileName },
    },
   });
   await this.sendAuditEntries([entry]);
   cogoToast.success("Authorization letter uploaded");
  } catch (err) {
   this.setState((s) => ({
    authForm: { ...s.authForm, isUploading: false },
   }));
   cogoToast.error("Upload failed");
  }
 };

shouldTriggerBalanceCheckForAuthorization = (entry) => {
    if (!entry) {
        return false;
    }
    const normalizedType = String(entry.type || "")
        .toLowerCase()
        .replace(/[\s-]+/g, "");
    const isFinalAuthorization =
        normalizedType === "finalauthorization" || normalizedType === "finalauth";
    return isFinalAuthorization && !this.isCashPayorCase();
};

 // Save authorization entry
 saveAuthorizationEntry = async () => {
  if (this.blockIfCancelled()) return;

  const { authForm } = this.state;
  if (!authForm.amountRupees || authForm.amountRupees <= 0) {
   cogoToast.warn("Enter authorized amount");
   return;
  }

  const maxAuthRupees = this.computeSummary().totalReceivable / 100;
  if (authForm.amountRupees > maxAuthRupees) {
   cogoToast.error(
    `Authorization amount cannot exceed Total Receivable (₹${maxAuthRupees.toFixed(2)}).`,
    { hideAfter: 6 }
   );
   return;
  }

  const { ok } = await this.guardFreshBeforeBillingWrite();
  if (!ok) return;

  // Validate that either TPA/Insurance OR CC No/Claim Payor is provided
  const hasTpaInsurance = authForm.tpaId && authForm.insuranceId;
  const hasCcnClaimPayor = authForm.ccn && authForm.claimPayor;

  if (!hasTpaInsurance && !hasCcnClaimPayor) {
   cogoToast.warn(
    "Please specify either TPA/Insurance pair OR CC No/Claim Payor pair"
   );
   return;
  }

  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   cogoToast.error("No surgery booking ID found");
   return;
  }

  try {
   cogoToast.loading("Saving authorization...");
   this.setState({ isSaving: true });

   const newEntry = {
    ts: new Date(),
    type: authForm.type,
    amountRupees: authForm.amountRupees,
    tpaId: authForm.tpaId,
    tpaName: authForm.tpaName,
    insuranceId: authForm.insuranceId,
    insuranceName: authForm.insuranceName,
    ccn: authForm.ccn,
    claimPayor: authForm.claimPayor,
    letterFileId: authForm.letterFileId,
    letterFileName: authForm.letterFileName,
    remarks: authForm.remarks,
    insuranceNo: authForm.insuranceNo,
    uploadedById: this.props.authStore.id,
    uploadedByName: this.props.authStore.name,
   };
   const shouldRequestBalanceCheck = this.shouldTriggerBalanceCheckForAuthorization(
    newEntry
   );

   // Prepare payload for PATCH request
   const payload = {
    surgeryBookingId: surgeryId,
    authorization: {
     entries: [newEntry, ...this.state.authorization.entries],
     activeIndex: 0,
    },
   };

   // Make PATCH request to save authorization data
   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    payload
   );

   if (response && response.success) {
    // Update local state with the new authorization entry and mirror into surgeryData
    const entries = [newEntry, ...this.state.authorization.entries];
    this.setState(
     (prev) => ({
      authorization: { entries, activeIndex: 0 },
      surgeryData: {
       ...(prev.surgeryData || {}),
       authorization: { entries, activeIndex: 0 },
      },
      isSaving: false,
      authForm: this.getDefaultAuthForm(),
      authFormDirty: false,
     }),
     async () => {
      this.persistToMemory();
      // Also persist to billing snapshot so it survives reloads
      this.saveBillingData();
      this.emitSurgeryPatchToParent({
       authorization: { entries, activeIndex: 0 },
      });
      if (shouldRequestBalanceCheck) {
       await this.requestBalanceCheck("authorization-final");
      }
      // Audit: authorization added
      const entry = this.buildAuditEntry({
       actionType: "CREATE",
       actionSlug: `Added authorization (${newEntry.type}) ₹${(
        newEntry.amountRupees || 0
       ).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
       })}`,
       actionMetadata: {
        panel: "BILLING",
        fieldName: "authorization.entries[0]",
        changeType: "CREATE",
        previousValue: "",
        newValue: {
         type: newEntry.type,
         amountRupees: newEntry.amountRupees,
         tpaName: newEntry.tpaName,
         insuranceName: newEntry.insuranceName,
         ccn: newEntry.ccn || "",
         claimPayor: newEntry.claimPayor || "",
         letterFileId: newEntry.letterFileId || "",
         letterFileName: newEntry.letterFileName || "",
         remarks: newEntry.remarks || "",
         insuranceNo: newEntry.insuranceNo || "",
        },
       },
      });
      this.sendAuditEntries([entry]);
     }
    );
    cogoToast.success("Authorization saved successfully");
   } else {
    throw new Error("Failed to save authorization");
   }
  } catch (err) {
   cogoToast.error("Failed to save authorization");
   this.setState({ isSaving: false });
   console.error("Authorization save error:", err);

   // Fallback to local save if API fails
   const newEntry = {
    ts: new Date(),
    type: authForm.type,
    amountRupees: authForm.amountRupees,
    tpaId: authForm.tpaId,
    tpaName: authForm.tpaName,
    insuranceId: authForm.insuranceId,
    insuranceName: authForm.insuranceName,
    ccn: authForm.ccn,
    claimPayor: authForm.claimPayor,
    letterFileId: authForm.letterFileId,
    letterFileName: authForm.letterFileName,
    remarks: authForm.remarks,
    insuranceNo: authForm.insuranceNo,
    insuranceNo: authForm.insuranceNo,
    uploadedById: this.props.authStore.id,
    uploadedByName: this.props.authStore.name,
   };
   const shouldRequestBalanceCheck = this.shouldTriggerBalanceCheckForAuthorization(
    newEntry
   );
   const entries = [newEntry, ...this.state.authorization.entries];
   this.setState(
    {
     authorization: { entries, activeIndex: 0 },
     isSaving: false,
     authForm: this.getDefaultAuthForm(),
     authFormDirty: false,
    },
    async () => {
     this.persistToMemory();
     this.recordAudit({
      panel: "authorization",
      action: "authorization.add",
      fieldPath: "authorization.entries[0]",
      prevValue: null,
      newValue: newEntry,
     });
     this.emitSurgeryPatchToParent({
      authorization: { entries, activeIndex: 0 },
     });
     if (shouldRequestBalanceCheck) {
      await this.requestBalanceCheck("authorization-final");
     }
    }
   );
   cogoToast.success("Authorization saved locally (temporary)");
  }
 };

 // Helpers for line items (surgery bills)

 // Package Cost is derived (not user-editable):
 // Surgery Rate + Anaesthetic Charges + Tissue Processing Charges
 computePackageCostPaise = (surgeryBill) => {
     const surgeryRatePaise = Number(surgeryBill?.surgeryRatePaise || 0);
     const anaestheticChargesPaise = Number(surgeryBill?.anaestheticChargesPaise || 0);
     const tissueProcessingPaise = Number(surgeryBill?.tissueProcessingPaise || 0);
 
     return Math.max(0, Math.trunc(surgeryRatePaise + anaestheticChargesPaise + tissueProcessingPaise));
 };

 // Helper to calculate base amount for MoU discount: (Package Cost + Femto) - Special Discount
 calculateMouDiscountBase = (surgeryBill) => {
     const packageCostRupees = this.computePackageCostPaise(surgeryBill) / 100;

     const baseForMouDiscountRupees =
         packageCostRupees +
         (surgeryBill.femtoChargesPaise || 0) / 100 -
         (surgeryBill.specialDiscountPaise || 0) / 100;
     return Math.max(0, baseForMouDiscountRupees);
 };

 // Update MoU discount percentage and sync absolute amount
 updateMouDiscountPercent = (idx, newPercent) => {
  this.updateSurgeryBill(idx, (sb) => {
   const baseAmount = this.calculateMouDiscountBase(sb);
   const calculatedAmountRupees = (newPercent * baseAmount) / 100;
   return {
    ...sb,
    mouDiscountPercent: Math.max(0, Math.round(newPercent * 100) / 100), // Round to 2 decimal places
    mouDiscountPaise: Math.max(0, Math.round(calculatedAmountRupees * 100)),
   };
  });
 };

 // Update MoU discount absolute amount and sync percentage
 updateMouDiscountAmount = (idx, newAmountRupees) => {
  this.updateSurgeryBill(idx, (sb) => {
   const baseAmount = this.calculateMouDiscountBase(sb);
   const calculatedPercent =
    baseAmount > 0 ? (newAmountRupees / baseAmount) * 100 : 0;
   return {
    ...sb,
    mouDiscountPaise: Math.max(0, Math.round(newAmountRupees * 100)),
    mouDiscountPercent: Math.max(0, Math.round(calculatedPercent * 100) / 100), // Round to 2 decimal places
   };
  });
 };

 // Helper to auto-sync MoU discount amount when base changes
 autoSyncMouDiscount = (surgeryBill) => {
  if (surgeryBill.mouDiscountPercent > 0) {
   const baseAmount = this.calculateMouDiscountBase(surgeryBill);
   const calculatedAmountRupees =
    (surgeryBill.mouDiscountPercent * baseAmount) / 100;
   surgeryBill.mouDiscountPaise = Math.max(
    0,
    Math.round(calculatedAmountRupees * 100)
   );
  }
  return surgeryBill;
 };

updateSurgeryBill = (idx, updater, options = {}) => {
 const { skipDirtyFlag = false } = options;
  const prev = this.state.surgeryBills[idx];
  const nextCandidate = updater({
   ...prev,
   taxProc: { ...(prev.taxProc || {}) },
   taxImplant: { ...(prev.taxImplant || {}) },
  });

  // Keep derived Package Cost in sync with its components
  nextCandidate.combinedRatePaise = this.computePackageCostPaise(nextCandidate);

  // Auto-sync MoU discount amount when base amount changes (except when directly updating MoU fields)
  const fieldsAffectingMouBase = [
   "surgeryRatePaise",
   "anaestheticChargesPaise",
   "tissueProcessingPaise",
   "femtoChargesPaise",
   "specialDiscountPaise",
  ];
  const mouBaseChanged = fieldsAffectingMouBase.some(
   (field) => prev[field] !== nextCandidate[field]
  );

  // Don't auto-sync if we're just storing raw input for typing
  const isStoringRawInput =
   nextCandidate.mouDiscountPercentInput !== prev.mouDiscountPercentInput;

  if (
   mouBaseChanged &&
   nextCandidate.mouDiscountPercent > 0 &&
   !isStoringRawInput
  ) {
   this.autoSyncMouDiscount(nextCandidate);
  }

  const changes = this.diffObjects(
   prev,
   nextCandidate,
   `surgeryBills[${idx}]`,
   "lineItems",
   "lineItem.update"
  );
 const surgeryBills = [...this.state.surgeryBills];
 surgeryBills[idx] = nextCandidate;

  // Check if implant cost changed to auto-load from consumption
  const implantCostChanged =
   prev.implantCostInclusivePaise !== nextCandidate.implantCostInclusivePaise ||
   prev.iolDiscountPaise !== nextCandidate.iolDiscountPaise;

 this.setState(
  (curr) => ({
   surgeryBills,
   hasUnsavedBillingChanges: skipDirtyFlag
    ? curr.hasUnsavedBillingChanges
    : true,
  }),
  () => {
   this.recomputeAndPersist(idx);
  if (!skipDirtyFlag) {
      this.scheduleConsolidatedAutoSave(idx);
  }

   // Auto-load items from consumption when implant cost changes
   if (implantCostChanged && nextCandidate.implantCostInclusivePaise > 0) {
    console.log("🔄 Implant cost changed, auto-loading from consumption");
    setTimeout(() => this.hydrateImplantBillFromConsumptions(false), 100);
   }
  }
 );
 };

 // Removed addSurgeryCard and removeSurgeryCard methods since we now use consolidated billing

 computeTotalsForSurgery = (s) => {
  // All currency fields are stored in paise - convert to rupees for calculations
  const pct = (n) => (typeof n === "number" ? n : 0);
  const sumTaxPctProc = pct(s.taxProc.cgst) + pct(s.taxProc.sgst);
  const sumTaxPctImplant = pct(s.taxImplant.cgst) + pct(s.taxImplant.sgst);

  // Package Cost base in rupees: always derived from components (not editable)
  const packageCostRupees = this.computePackageCostPaise(s) / 100;

  const procedureTaxRupees = (packageCostRupees * sumTaxPctProc) / 100;

  // Procedure cost: allow override in rupees
  const procedureCostRupees =
   s.procedureCostRupees != null
    ? Number(s.procedureCostRupees) || 0
    : s.procedureCostPaise && s.procedureCostPaise > 0
    ? s.procedureCostPaise / 100
    : packageCostRupees + procedureTaxRupees;

  // Implant tax base: (implant cost inclusive - iol discount) - convert paise to rupees
  const implantBaseRupees = Math.max(
   ((s.implantCostInclusivePaise || 0) - (s.iolDiscountPaise || 0)) / 100,
   0
  );
  const implantTaxRupees = (implantBaseRupees * sumTaxPctImplant) / 100;

  // Calculate base amount for MoU discount: (Package Cost + Femto) - Special Discount
  const baseForMouDiscountRupees =
   packageCostRupees +
   (s.femtoChargesPaise || 0) / 100 -
   (s.specialDiscountPaise || 0) / 100;

  // MoU discount - both percentage and absolute amount are always in sync
  // Use the absolute amount since it's always calculated from the percentage
  const effectiveMouDiscountRupees = (s.mouDiscountPaise || 0) / 100;

  // Surgery Subtotal: (Package Cost + Femto) - Special - MoU - IOL
  const subtotalRupees =
   packageCostRupees +
   (s.femtoChargesPaise || 0) / 100 -
   (s.specialDiscountPaise || 0) / 100 -
   (effectiveMouDiscountRupees || 0) -
   (s.iolDiscountPaise || 0) / 100;

  return {
   procedureTaxPaise: Math.max(0, Math.trunc(procedureTaxRupees * 100)),
   procedureCostPaise: Math.max(0, Math.trunc(procedureCostRupees * 100)),
   implantTaxPaise: Math.max(0, Math.trunc(implantTaxRupees * 100)),
   // Convert back to paise for consistency
   effectiveMouDiscountPaise: Math.max(
    0,
    Math.trunc(effectiveMouDiscountRupees * 100)
   ),
   subtotalPaise: Math.max(0, Math.trunc(subtotalRupees * 100)),
  };
 };

 recomputeAndPersist = (idx) => {
  const surgeryBills = [...this.state.surgeryBills];
  const s = surgeryBills[idx];
  // Ensure derived Package Cost stays in sync (important for first-save without edits)
  s.combinedRatePaise = this.computePackageCostPaise(s);
  const totals = this.computeTotalsForSurgery(s);
  s.taxProc.absolutePaise = totals.procedureTaxPaise;
  // Preserve provided procedure cost if present; otherwise set computed
  s.procedureCostPaise =
   s.procedureCostRupees != null
    ? Math.trunc((Number(s.procedureCostRupees) || 0) * 100)
    : s.procedureCostPaise && s.procedureCostPaise > 0
    ? s.procedureCostPaise
    : totals.procedureCostPaise;
  s.taxImplant.absolutePaise = totals.implantTaxPaise;
  s.mouDiscountPaise = s.mouDiscountPaise || totals.effectiveMouDiscountPaise;
  s.subtotalPaise = totals.subtotalPaise;
  surgeryBills[idx] = s;
  this.setState({ surgeryBills }, () => {
   // Recompute on every change to reflect live subtotal and panels
   const recalc = this.computeTotalsForSurgery(surgeryBills[idx]);
   surgeryBills[idx].taxProc.absolutePaise = recalc.procedureTaxPaise;
   surgeryBills[idx].procedureCostPaise = recalc.procedureCostPaise;
   surgeryBills[idx].taxImplant.absolutePaise = recalc.implantTaxPaise;
   surgeryBills[idx].mouDiscountPaise =
    surgeryBills[idx].mouDiscountPaise || recalc.effectiveMouDiscountPaise;
   surgeryBills[idx].subtotalPaise = recalc.subtotalPaise;
   this.setState({ surgeryBills }, () => this.persistToMemory());
  });
 };

 recomputeOverallTotals = () => {
  // Keep in memory and trigger summary redraw
  this.persistToMemory();

  // Force a re-render to update the sticky summary
  this.forceUpdate();
 };

 // Removed: refreshIolFromConsumption (no longer needed)

 // Removed: mapConsumptionToIolRows (no longer needed)

 addManualIolRow = () => {
  const items = [
   ...(this.state.implantBill.items || []),
   {
    itemName: "",
    hsnCode: "90213900",
    serialNo: "",
    expiry: this.formatToISTDate(new Date()) || "",
    qty: 1,
    pricePaise: 0,
    cgst: 0,
    sgst: 0,
    totalPaise: 0,
   },
  ];
  const newIndex = items.length - 1;
  this.setState({ implantBill: { ...this.state.implantBill, items } }, () => {
   this.recomputeIolTotals();
   // Audit: add IOL row
   try {
    const row = items[newIndex];
    const entry = this.buildAuditEntry({
     actionType: "CREATE",
     actionSlug: "Added IOL item row",
     actionMetadata: {
      panel: "BILLING",
      fieldName: `implantBill.items[${newIndex}]`,
      changeType: "CREATE",
      previousValue: "",
      newValue: {
       itemName: row.itemName,
       hsnCode: row.hsnCode,
       serialNo: row.serialNo,
       expiry: row.expiry,
       qty: row.qty,
       pricePaise: row.pricePaise,
       cgst: row.cgst,
       sgst: row.sgst,
      },
     },
    });
    this.sendAuditEntries([entry]);
   } catch (e) {}
  });
 };

 removeIolRow = (index) => {
  const prevRow = (this.state.implantBill.items || [])[index];
  const items = (this.state.implantBill.items || []).filter(
   (_, i) => i !== index
  );
  this.setState({ implantBill: { ...this.state.implantBill, items } }, () => {
   this.recomputeIolTotals();
   // Audit: remove IOL row
   try {
    const entry = this.buildAuditEntry({
     actionType: "DELETE",
     actionSlug: "Removed IOL item row",
     actionMetadata: {
      panel: "BILLING",
      fieldName: `implantBill.items[${index}]`,
      changeType: "DELETE",
      previousValue: prevRow
       ? {
          itemName: prevRow.itemName,
          hsnCode: prevRow.hsnCode,
          serialNo: prevRow.serialNo,
          expiry: prevRow.expiry,
          qty: prevRow.qty,
          pricePaise: prevRow.pricePaise,
          cgst: prevRow.cgst,
          sgst: prevRow.sgst,
         }
       : "",
      newValue: "",
     },
    });
    this.sendAuditEntries([entry]);
   } catch (e) {}
  });
 };

 updateIolRow = (index, updater) => {
  const prev = this.state.implantBill.items[index];
  const nextRow = updater({ ...prev });
  // Enforce Qty to always be 1
  nextRow.qty = 1;
  const items = [...this.state.implantBill.items];
  items[index] = nextRow;
  this.setState({ implantBill: { ...this.state.implantBill, items } }, () => {
   this.recomputeIolTotals();
   // Audit: per-cell updates for IOL row
   try {
    const changes = this.diffObjects(
     prev,
     nextRow,
     `implantBill.items[${index}]`,
     "implant",
     "implant.row.update"
    );
    const entries = (changes || []).map((c) =>
     this.buildAuditEntry({
      actionType: "UPDATE",
      actionSlug: `Updated ${c.fieldPath}`,
      actionMetadata: {
       panel: "BILLING",
       fieldName: c.fieldPath,
       changeType: "UPDATE",
       previousValue: c.prevValue,
       newValue: c.newValue,
      },
     })
    );
    this.sendAuditEntries(entries);
   } catch (e) {}
  });
 };

 computeIolTotals = (items) => {
  // Manual mode: compute totals based on user-entered price and fixed GST per row
  const computed = (items || []).map((r) => {
   const qty = 1; // enforced elsewhere
   const pricePaise = Math.max(0, Number(r.pricePaise || 0));
   const basePaise = pricePaise * qty;
   const cgst = Math.max(0, Number(r.cgst || 0));
   const sgst = Math.max(0, Number(r.sgst || 0));
   const gstPct = cgst + sgst;
   const gstPaise = Math.round((basePaise * gstPct) / 100);
   const totalPaise = basePaise + gstPaise;
   return { ...r, qty: 1, totalPaise };
  });

  const totalBeforeDiscountPaise = computed.reduce(
   (sum, r) => sum + Math.max(0, Number(r.pricePaise || 0)) * 1,
   0
  );
  const totalPaise = computed.reduce(
   (sum, r) => sum + Math.max(0, Number(r.totalPaise || 0)),
   0
  );
  const totalGstPaise = Math.max(0, totalPaise - totalBeforeDiscountPaise);

  return {
   computed,
   totalBeforeDiscountPaise,
   totalDiscountPaise: 0,
   totalGstPaise,
   totalPaise,
  };
 };

 recomputeIolTotals = () => {
  return new Promise((resolve) => {
   const {
    computed,
    totalBeforeDiscountPaise,
    totalDiscountPaise,
    totalGstPaise,
    totalPaise,
   } = this.computeIolTotals(this.state.implantBill.items || []);
   this.setState(
    {
     implantBill: {
      ...this.state.implantBill,
      items: computed,
      totalBeforeDiscountPaise,
      totalDiscountPaise,
      totalGstPaise,
      totalPaise,
     },
    },
    () => {
     this.persistToMemory();
     resolve();
    }
   );
  });
 };

 // Internal save method (no toast messages, used by invoice generation)
 saveImplantBillInternal = async () => {
  if (this.blockIfCancelled()) return;
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   throw new Error("Surgery ID not found");
  }

  // Ensure totals are up-to-date before saving
  await this.recomputeIolTotals();

  const items = (this.state.implantBill.items || []).map((r) => ({
   itemName: r.itemName || "",
   hsnCode: r.hsnCode || "90213900",
   serialNo: r.serialNo || "",
   expiry: r.expiry || "",
   qty: 1,
   pricePaise: Math.max(0, Number(r.pricePaise || 0)),
   cgst: Math.max(0, Number(r.cgst || 0)),
   sgst: Math.max(0, Number(r.sgst || 0)),
   totalPaise: Math.max(0, Number(r.totalPaise || 0)),
  }));

  this.setState({ isSaving: true });
  const payload = {
   surgeryBookingId: surgeryId,
   implantBillItems: items,
  };
  await apiCallPatch(
   `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
   payload
  );

  // Update local cache/state
  const prevLen = Array.isArray(this.state.surgeryData?.implantBillItems)
   ? this.state.surgeryData.implantBillItems.length
   : 0;
  this.setState(
   (s) => ({
    surgeryData: { ...(s.surgeryData || {}), implantBillItems: items },
   }),
   this.persistToMemory
  );

  // Audit (non-blocking)
  try {
   const entry = this.buildAuditEntry({
    actionType: "UPDATE",
    actionSlug: "Saved implant bill items",
    actionMetadata: {
     panel: "BILLING",
     fieldName: "implantBillItems[]",
     changeType: "PERSIST",
     previousValue: prevLen,
     newValue: items.length,
    },
   });
   this.sendAuditEntries([entry]);
  } catch (e) {}

  this.setState({ isSaving: false });
 };

 // Persist Implant/IOL bill items to booking (server) - Public method with UI feedback
 saveImplantBill = async () => {
  if (this.blockIfCancelled()) return;
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   cogoToast.error("Surgery ID not found");
   return;
  }
  try {
   cogoToast.loading("Saving implant bill...");
   await this.saveImplantBillInternal();
   cogoToast.success("Implant bill saved");
  } catch (err) {
   console.error("Failed to save implant bill", err);
   cogoToast.error("Failed to save implant bill");
  }
 };

 // Removed: buildConsumptionItemsFromImplantBill (no longer needed)

 // Removed: scheduleSaveConsumptionItems (no longer needed)

 // Removed: saveConsumptionItems (no longer needed)

 // Payments: MR numbering and save
 generateMoneyReceiptNo = (paymentType) => {
  // Get booking reference from surgery data
  const bookingReference =
   this.state.surgeryData?.bookingReference ||
   this.props.surgery?.bookingReference ||
   this.state.surgeryData?.reference ||
   this.props.surgery?.reference ||
   `REF${this.getBookingId()}` ||
   "UNKNOWN";

  // Type code: P (Payment), R (Refund)
  const typeCode = paymentType === "Refund" ? "R" : "P";

  // Get counter for this specific booking reference
  const counterKey = `mr_${bookingReference}`;
  let current = BillingTab.mrCounterStore.get(counterKey);

  // If counter not initialized, scan existing payments to find the highest counter
  if (current === undefined) {
   const existingPayments = this.state.payments?.events || [];
   const maxCounter = existingPayments.reduce((max, payment) => {
    if (payment.moneyReceiptNo) {
     // Extract counter from receipt number format: IPD/DEL25I2000152/A/01
     const parts = payment.moneyReceiptNo.split("/");
     if (
      parts.length === 4 &&
      parts[0] === "IPD" &&
      parts[1] === bookingReference
     ) {
      const counter = parseInt(parts[3], 10) || 0;
      return Math.max(max, counter);
     }
    }
    return max;
   }, 0);

   current = maxCounter;
   BillingTab.mrCounterStore.set(counterKey, current);
  }

  const next = current + 1;
  BillingTab.mrCounterStore.set(counterKey, next);

  // Format: IPD/bookingReference/typeCode/counter
  // Counter is 2-digit padded (01, 02, 03, etc.)
  const counter = String(next).padStart(2, "0");

  return `IPD/${bookingReference}/${typeCode}/${counter}`;
 };

 recomputePaymentTotals = (events) => {
  const totalPaidPaise = events
   .filter((e) => e.type !== "Refund")
   .reduce((s, e) => s + (e.amountPaise || 0), 0);
  const totalRefundedPaise = events
   .filter((e) => e.type === "Refund")
   .reduce((s, e) => s + (e.amountPaise || 0), 0);
  return { totalPaidPaise, totalRefundedPaise };
 };

 savePayment = async () => {
  if (this.blockIfCancelled()) return;
  const { type, mode, amountPaise, paymentRemarks } = this.state.paymentForm;
  if (type === "Refund" && !mode) {
   cogoToast.warn("Select the refund mode");
   return;
  }
  if (!amountPaise || amountPaise <= 0) {
   cogoToast.warn("Enter amount");
   return;
  }
  if (type === "Refund" && !String(paymentRemarks || "").trim()) {
   cogoToast.warn("Enter the exact reason for the refund in Remarks");
   return;
  }

  if (type !== "Refund") {
   const maxCollectablePaise = this.getMaxCollectablePaise();
   if (maxCollectablePaise <= 0) {
    cogoToast.warn("Balance is ₹0. No further payment can be collected.");
    return;
   }
   if (amountPaise > maxCollectablePaise) {
    cogoToast.warn(
     `Payment cannot exceed the balance receivable of ₹${(maxCollectablePaise / 100).toFixed(2)}.`
    );
    return;
   }
  }

  const { ok } = await this.guardFreshBeforeBillingWrite();
  if (!ok) return;

  // For refunds: cannot exceed the net amount collected (total paid minus already refunded)
  if (type === "Refund") {
   const existingEvents = this.state.payments.events || [];
   const totalPaidPaise = existingEvents
    .filter((e) => e.type !== "Refund")
    .reduce((s, e) => s + (e.amountPaise || 0), 0);
   const totalRefundedPaise = existingEvents
    .filter((e) => e.type === "Refund")
    .reduce((s, e) => s + (e.amountPaise || 0), 0);
   const maxRefundablePaise = totalPaidPaise - totalRefundedPaise;
   if (amountPaise > maxRefundablePaise) {
    const maxRupees = (maxRefundablePaise / 100).toFixed(2);
    cogoToast.warn(
     `Refund amount cannot exceed the net collected amount of ₹${maxRupees}.`
    );
    return;
   }
  }

  const moneyReceiptNo = this.generateMoneyReceiptNo(type);
  const amountRupees = Number(((amountPaise || 0) / 100).toFixed(2));
  const evt = {
   ts: new Date(),
   type: type === "Refund" ? "Refund" : "Payment",
   mode,
   amountPaise,
   amount: amountRupees,
   doneBy: this.props.authStore.name || "Unknown",
   moneyReceiptNo,
   paymentRemarks: paymentRemarks || "",
   remarks: paymentRemarks || "",
  };
  const events = [evt, ...(this.state.payments.events || [])];
  const { totalPaidPaise, totalRefundedPaise } =
   this.recomputePaymentTotals(events);
  // Optimistic update
  this.setState({
   payments: { events, totalPaidPaise, totalRefundedPaise },
   paymentForm: { ...this.state.paymentForm, amountPaise: 0, paymentRemarks: "" },
  });
  this.persistToMemory();
  this.recordAudit({
   panel: "payments",
   action: "payment.add",
   fieldPath: "payments.events[0]",
   prevValue: null,
   newValue: evt,
  });

  // Persist to backend (neoSxBookingMaster)
  const surgeryId = this.getBookingId();
  if (!surgeryId) return;
  try {
   cogoToast.loading("Saving payment...");
   this.setState({ isSaving: true });
   const payload = {
    surgeryBookingId: surgeryId,
    payments: {
     events,
     totalPaidPaise,
     totalRefundedPaise,
     totalPaid: Number((totalPaidPaise / 100).toFixed(2)),
     totalRefunded: Number((totalRefundedPaise / 100).toFixed(2)),
    },
   };
   await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    payload
   );
   cogoToast.success(`Payment saved • ${moneyReceiptNo}`);

   // Notify parent component about payment update (for real-time cancellation validation)
   if (typeof this.props.onPaymentsUpdate === "function") {
    try {
     // Convert events to format expected by parent (with 'amount' field instead of 'amountPaise')
     const paymentsForParent = events.map((e) => ({
      type: e.type === "Refund" ? "Refund" : "Payment",
      amount: e.amount || (e.amountPaise ? e.amountPaise / 100 : 0),
      amountPaise: e.amountPaise,
      mode: e.mode,
      ts: e.ts,
      doneBy: e.doneBy,
      moneyReceiptNo: e.moneyReceiptNo,
      remarks: e.remarks || e.paymentRemarks || "",
     }));
     this.props.onPaymentsUpdate(paymentsForParent);
    } catch (callbackErr) {
     console.warn("Failed to notify parent of payment update:", callbackErr);
    }
   }

   // Audit: payment added
   try {
    const entry = this.buildAuditEntry({
     actionType: "CREATE",
     actionSlug: `${evt.type} • ${evt.mode} • ${moneyReceiptNo}`,
     actionMetadata: {
      panel: "BILLING",
      fieldName: "payments.events[0]",
      changeType: "CREATE",
      previousValue: "",
      newValue: {
       type: evt.type,
       mode: evt.mode,
       amountPaise: evt.amountPaise,
       moneyReceiptNo: evt.moneyReceiptNo,
       paymentRemarks: evt.paymentRemarks || "",
      },
     },
    });
    await this.sendAuditEntries([entry]);
   } catch (auditErr) {}
  await this.requestBalanceCheck("payment-save");
  } catch (err) {
   console.error("Failed to save payment", err);
   cogoToast.error("Failed to save payment");
  } finally {
   this.setState({ isSaving: false });
  }
 };

 // Invoice generation
 deriveBranchShort = () => {
  const name = this.props.surgery?.branchName || "BR";
  // Take letters of words up to 4 chars, else first 3 chars
  const parts = String(name).split(/\s+/).filter(Boolean);
  const code =
   parts.length > 1
    ? parts
       .map((p) => p[0])
       .join("")
       .toUpperCase()
       .slice(0, 4)
    : name.slice(0, 3).toUpperCase();
  return code || "BR";
 };

 generateInvoiceNo = () => {
  // Get booking reference from surgery data
  const bookingReference =
   this.state.surgeryData?.bookingReference ||
   this.props.surgery?.bookingReference ||
   this.state.surgeryData?.reference ||
   this.props.surgery?.reference ||
   `REF${this.getBookingId()}` ||
   "UNKNOWN";

  // Get counter for this specific surgery booking
  const surgeryId = this.getBookingId();
  const counterKey = `inv_${surgeryId}`;
  let current = BillingTab.invoiceCounterStore.get(counterKey);

  // If counter not initialized, check if there are existing invoices for this surgery
  if (current === undefined) {
   // Prefer server-backed invoice history
   const invArr = Array.isArray(this.state.surgeryData?.invoices)
    ? this.state.surgeryData.invoices
    : [];
   if (invArr.length > 0) {
    const last = invArr[invArr.length - 1];
    if (typeof last?.counter === "number" && last.counter >= 0) {
     current = last.counter;
    } else if (last?.number) {
     const parts = String(last.number).split("/");
     if (
      parts.length === 3 &&
      parts[0] === "INV-IPD" &&
      parts[1] === bookingReference
     ) {
      current = parseInt(parts[2], 10) || 0;
     } else {
      current = 0;
     }
    } else {
     current = 0;
    }
   } else {
    // Fallback to currently selected finalInvoice, if any
    const existingInvoice = this.state.finalInvoice;
    if (existingInvoice && existingInvoice.number) {
     const parts = existingInvoice.number.split("/");
     if (
      parts.length === 3 &&
      parts[0] === "INV-IPD" &&
      parts[1] === bookingReference
     ) {
      current = parseInt(parts[2], 10) || 0;
     } else {
      current = 0;
     }
    } else {
     current = 0;
    }
   }
   BillingTab.invoiceCounterStore.set(counterKey, current);
  }

  const next = current + 1;
  BillingTab.invoiceCounterStore.set(counterKey, next);

  // Format: INV-IPD/bookingReference/counter
  // Counter is 2-digit padded (01, 02, 03, etc.)
  const counter = String(next).padStart(2, "0");
  const invoiceNo = `INV-IPD/${bookingReference}/${counter}`;

  return {
   invoiceNo,
   bookingReference,
   counter: next,
  };
 };

 // Build invoice record to persist to server (append-only)
 buildInvoiceRecord = ({
  invoiceNo,
  bookingReference,
  counter,
  snapshot,
  overallPaise,
  checksum,
 }) => {
  const summaryPaise = this.computeSummary();
  const payments = (this.state.payments?.events || []).map((p) => ({
   ts: p?.ts || new Date(),
   type: p?.type || "Payment",
   mode: p?.mode || "Cash",
   amountPaise: p?.amountPaise || 0,
   moneyReceiptNo: p?.moneyReceiptNo || null,
   paymentRemarks: p?.paymentRemarks || p?.remarks || "",
  }));
  return {
   number: invoiceNo,
   generatedAt: new Date(),
   generatedBy: this.getUser(),
   bookingReference,
   counter,
   surgeryBillsSnapshot: snapshot,
   overallPaise,
   checksum,
   summaryPaise: {
    totalReceivable: summaryPaise.totalReceivable,
    authorized: summaryPaise.authorized,
    netPayable: summaryPaise.netPayable,
    collected: summaryPaise.collected,
    refunded: summaryPaise.refunded,
    balance: summaryPaise.balance,
   },
   payments,
   consolidatedBilling: this.state.surgeryData?.consolidatedBilling || null,
  };
 };

 // Append invoice to server and update local state
 appendInvoiceToServer = async (invoiceRecord) => {
  const surgeryId = this.getBookingId();
  if (!surgeryId) return;
  try {
   this.setState({ isSaving: true });
   const prevInvoices = Array.isArray(this.state.surgeryData?.invoices)
    ? this.state.surgeryData.invoices
    : [];
   const nextInvoices = [...prevInvoices, invoiceRecord];
   const payload = {
    surgeryBookingId: surgeryId,
    invoices: nextInvoices,
   };
   await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    payload
   );
   this.setState((s) => ({
    surgeryData: { ...(s.surgeryData || {}), invoices: nextInvoices },
   }));
   try {
    const entry = this.buildAuditEntry({
     actionType: "CREATE",
     actionSlug: `Appended invoice ${invoiceRecord.number}`,
     actionMetadata: {
      panel: "BILLING",
      fieldName: "invoices[]",
      changeType: "CREATE",
      previousValue: (prevInvoices || []).length,
      newValue: (nextInvoices || []).length,
     },
    });
    // await this.sendAuditEntries([entry]);
   } catch (e) {}
   cogoToast.success("Invoice saved to server");
  } catch (err) {
   console.error("Failed to append invoice", err);
   cogoToast.error("Failed to save invoice to server");
  } finally {
   this.setState({ isSaving: false });
  }
 };

 surgeryBillsSignature = (bills) => {
  try {
   const minimal = (bills || []).map((s) => ({
    header: s.header,
    surgeryRatePaise: s.surgeryRatePaise,
    anaestheticChargesPaise: s.anaestheticChargesPaise,
    tissueProcessingPaise: s.tissueProcessingPaise,
    manualRows: (s.manualRows || []).map((r) => ({
     label: r.label,
     amountPaise: r.amountPaise,
    })),
    taxProc: {
     cgst: s.taxProc?.cgst || 0,
     sgst: s.taxProc?.sgst || 0,
    },
    procedureCostPaise: s.procedureCostPaise,
    implantCostInclusivePaise: s.implantCostInclusivePaise,
    taxImplant: {
     cgst: s.taxImplant?.cgst || 0,
     sgst: s.taxImplant?.sgst || 0,
    },
    femtoChargesPaise: s.femtoChargesPaise,
    specialDiscountPaise: s.specialDiscountPaise,
    iolDiscountPaise: s.iolDiscountPaise,
    mouDiscountPercent: s.mouDiscountPercent,
    mouDiscountPaise: s.mouDiscountPaise,
    subtotalPaise: s.subtotalPaise,
   }));
   return JSON.stringify(minimal);
  } catch (e) {
   return "";
  }
 };

 computeOverallTotalFromBills = (bills) => {
  return (bills || []).reduce((acc, s) => acc + (s.subtotalPaise || 0), 0);
 };

 ensureConsolidatedBillingSaved = async () => {
  const surgeryId = this.getBookingId();
  if (!surgeryId) return;

  const cb = this.state.surgeryData?.consolidatedBilling;
  const hasCb =
   cb &&
   (typeof cb.surgeryRatePaise === "number" ||
    typeof cb.combinedRatePaise === "number" ||
    typeof cb.procedureCostRupees === "number");
  if (hasCb) return;

  const sb = (this.state.surgeryBills || [])[0];
  if (!sb) return;

  const procedureCostRupees =
   sb.procedureCostRupees != null
    ? Number(sb.procedureCostRupees || 0)
    : Math.round((Number(sb.procedureCostPaise || 0) / 100) * 100) / 100;

  const payload = {
   surgeryBookingId: surgeryId,
   consolidatedBilling: {
    surgeryRatePaise: sb.surgeryRatePaise || 0,
    anaestheticChargesPaise: sb.anaestheticChargesPaise || 0,
    tissueProcessingPaise: sb.tissueProcessingPaise || 0,
    combinedRatePaise: sb.combinedRatePaise || 0,
    procedureTaxPercent: (sb.taxProc?.cgst || 0) + (sb.taxProc?.sgst || 0),
    procedureCostRupees: procedureCostRupees,
    implantCostInclusivePaise: sb.implantCostInclusivePaise || 0,
    implantTaxPercent: (sb.taxImplant?.cgst || 0) + (sb.taxImplant?.sgst || 0),
    femtoChargesPaise: sb.femtoChargesPaise || 0,
    specialDiscountPaise: sb.specialDiscountPaise || 0,
    specialDiscountRemarks: sb.specialDiscountRemarks || "",
    iolDiscountPaise: sb.iolDiscountPaise || 0,
    mouDiscountPercent: sb.mouDiscountPercent || 0,
    mouDiscountPaise: sb.mouDiscountPaise || 0,
    selectedAnesthesiaId: sb.selectedAnesthesiaId || "",
   },
   selectedSurgeries: Array.isArray(this.state.surgeryData?.selectedSurgeries)
    ? this.state.surgeryData.selectedSurgeries
    : [],
   anesthesia: sb.selectedAnesthesiaId || "",
  };

  try {
   await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    payload
   );
   this.setState((s) => ({
    surgeryData: {
     ...(s.surgeryData || {}),
     consolidatedBilling: payload.consolidatedBilling,
     anesthesia: payload.anesthesia,
    },
   }));
  } catch (err) {
   console.error(
    "Failed to persist consolidated billing before invoice generation",
    err
   );
   cogoToast.error("Failed to save consolidated billing before invoice");
   throw err;
  }
 };

 generateFinalInvoice = async () => {
  if (this.blockIfCancelled()) return;
  try {
   await this.ensureConsolidatedBillingSaved();
  } catch (e) {
   throw new Error("Failed to save consolidated billing");
  }

  const { invoiceNo, bookingReference, counter } = this.generateInvoiceNo();
  const snapshot = JSON.parse(JSON.stringify(this.state.surgeryBills || []));
  const checksum = this.surgeryBillsSignature(this.state.surgeryBills);
  const overallPaise = this.computeOverallTotalFromBills(snapshot);
  const prevInvNo = this.state.finalInvoice?.number || null;
  const prevChecksum = this.state.finalInvoice?.checksum || null;

  const invoiceRecord = this.buildInvoiceRecord({
   invoiceNo,
   bookingReference,
   counter,
   snapshot,
   overallPaise,
   checksum,
  });

  return new Promise((resolve, reject) => {
   this.setState({ finalInvoice: invoiceRecord }, async () => {
    try {
     this.persistToMemory();
     const isRegen = !!prevInvNo;

     // Audit: invoice generate/regenerate
     const entry = this.buildAuditEntry({
      actionType: isRegen ? "UPDATE" : "CREATE",
      actionSlug: isRegen
       ? `Regenerated invoice ${invoiceRecord.number}`
       : `Generated invoice ${invoiceRecord.number}`,
      actionMetadata: {
       panel: "BILLING",
       fieldName: "finalInvoice.number",
       changeType: isRegen ? "UPDATE" : "CREATE",
       previousValue: prevInvNo || "",
       newValue: invoiceRecord.number,
       checksumBefore: prevChecksum || "",
       checksumAfter: invoiceRecord.checksum,
       overallPaise: invoiceRecord.overallPaise,
      },
     });

     await this.appendInvoiceToServer(invoiceRecord);
     await this.sendAuditEntries([entry]);
     cogoToast.success(
      `Final invoice ${isRegen ? "regenerated" : "generated"} • ${
       invoiceRecord.number
      }`
     );
     resolve(invoiceRecord);
    } catch (error) {
     console.error("Error in generateFinalInvoice:", error);
     reject(error);
    }
   });
  });
 };

 // Hydrate latest invoice from surgeryData.invoices on load
 hydrateInvoiceFromSurgery = (surgeryData) => {
  console.log("🔍 hydrateInvoiceFromSurgery called with:", {
   hasData: !!surgeryData,
   implantInvoices: surgeryData?.implantInvoices,
  });

  // Hydrate regular final invoice
  try {
   const invArr = Array.isArray(surgeryData?.invoices)
    ? surgeryData.invoices
    : [];
   if (invArr.length > 0) {
    const latest = invArr[invArr.length - 1];
    // Normalize dates if needed
    if (
     latest &&
     latest.generatedAt &&
     typeof latest.generatedAt === "string"
    ) {
     latest.generatedAt = new Date(latest.generatedAt);
    }
    this.setState({ finalInvoice: latest });
   } else {
    console.log("ℹ️ No regular invoices found");
   }
  } catch (e) {
   console.error("Error hydrating finalInvoice:", e);
  }

  // Also hydrate implant invoice
  try {
   const implantInvArr = Array.isArray(surgeryData?.implantInvoices)
    ? surgeryData.implantInvoices
    : [];
   console.log(
    `📋 Found ${implantInvArr.length} implant invoices in surgeryData`
   );

   if (implantInvArr.length === 0) {
    console.log("⚠️ No implant invoices found in surgeryData");
    return;
   }

   const latestImplant = implantInvArr[implantInvArr.length - 1];
   console.log("✅ Hydrating latest implant invoice:", latestImplant?.number);

   // Normalize dates if needed
   if (
    latestImplant &&
    latestImplant.generatedAt &&
    typeof latestImplant.generatedAt === "string"
   ) {
    latestImplant.generatedAt = new Date(latestImplant.generatedAt);
   }
   this.setState({ finalImplantInvoice: latestImplant }, () => {
    console.log(
     "✅ finalImplantInvoice set in state:",
     this.state.finalImplantInvoice?.number
    );
   });
  } catch (e) {
   console.error("❌ Error hydrating finalImplantInvoice:", e);
  }
 };

 invoiceDiverged = () => {
  if (!this.state.finalInvoice?.checksum) return false;
  const current = this.surgeryBillsSignature(this.state.surgeryBills);
  return current !== this.state.finalInvoice.checksum;
 };

 // ===== IMPLANT/IOL INVOICE GENERATION =====

 generateImplantInvoiceNo = () => {
  // Get booking reference from surgery data
  const bookingReference =
   this.state.surgeryData?.bookingReference ||
   this.props.surgery?.bookingReference ||
   this.state.surgeryData?.reference ||
   this.props.surgery?.reference ||
   `REF${this.getBookingId()}` ||
   "UNKNOWN";

  // Get counter for this specific surgery booking
  const surgeryId = this.getBookingId();
  const counterKey = `iol_inv_${surgeryId}`;

  // ALWAYS read from the latest surgeryData.implantInvoices to get the most recent counter
  const invArr = Array.isArray(this.state.surgeryData?.implantInvoices)
   ? this.state.surgeryData.implantInvoices
   : [];

  let current = 0;

  if (invArr.length > 0) {
   // Get the highest counter from all existing invoices
   const maxCounter = invArr.reduce((max, inv) => {
    if (typeof inv?.counter === "number" && inv.counter > max) {
     return inv.counter;
    }
    // Fallback: parse from invoice number
    if (inv?.number) {
     const parts = String(inv.number).split("/");
     if (
      parts.length === 4 &&
      parts[0] === "INV-IPD" &&
      parts[1] === bookingReference &&
      parts[2] === "I"
     ) {
      const parsedCounter = parseInt(parts[3], 10) || 0;
      return parsedCounter > max ? parsedCounter : max;
     }
    }
    return max;
   }, 0);
   current = maxCounter;
  }

  const next = current + 1;

  // Format: INV-IPD/bookingReference/I/counter
  // Example: INV-IPD/DEL25J1300005/I/01
  const counter = String(next).padStart(2, "0");
  const invoiceNo = `INV-IPD/${bookingReference}/I/${counter}`;

  console.log(`🔢 Invoice Counter Debug:`, {
   surgeryId,
   existingInvoices: invArr.length,
   currentMaxCounter: current,
   nextCounter: next,
   newInvoiceNo: invoiceNo,
  });

  return {
   invoiceNo,
   bookingReference,
   counter: next,
  };
 };

 implantBillSignature = (items) => {
  try {
   const minimal = (items || []).map((item) => ({
    itemName: item.itemName,
    hsnCode: item.hsnCode,
    serialNo: item.serialNo,
    expiry: item.expiry,
    qty: item.qty,
    pricePaise: item.pricePaise,
    cgst: item.cgst,
    sgst: item.sgst,
    totalPaise: item.totalPaise,
   }));
   return JSON.stringify(minimal);
  } catch (e) {
   return "";
  }
 };

 buildImplantInvoiceRecord = ({
  invoiceNo,
  bookingReference,
  counter,
  itemsSnapshot,
  totalPaise,
  checksum,
 }) => {
  return {
   number: invoiceNo,
   generatedAt: new Date(),
   generatedBy: this.getUser(),
   bookingReference,
   counter,
   itemsSnapshot: itemsSnapshot,
   totalPaise: totalPaise,
   checksum: checksum,
   totalBeforeDiscountPaise:
    this.state.implantBill.totalBeforeDiscountPaise || 0,
   totalGstPaise: this.state.implantBill.totalGstPaise || 0,
   totalDiscountPaise: this.state.implantBill.totalDiscountPaise || 0,
  };
 };

 appendImplantInvoiceToServer = async (invoiceRecord) => {
  const surgeryId = this.getBookingId();
  if (!surgeryId) return;
  try {
   this.setState({ isSaving: true });
   const prevInvoices = Array.isArray(this.state.surgeryData?.implantInvoices)
    ? this.state.surgeryData.implantInvoices
    : [];

   // Always append new invoice (regeneration creates new invoice with incremented counter)
   const nextInvoices = [...prevInvoices, invoiceRecord];

   const payload = {
    surgeryBookingId: surgeryId,
    implantInvoices: nextInvoices,
   };
   await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    payload
   );

   // Wait for state update to complete before continuing
   await new Promise((resolve) => {
    this.setState(
     (s) => ({
      surgeryData: { ...(s.surgeryData || {}), implantInvoices: nextInvoices },
     }),
     resolve
    );
   });

   // 🔥 INVALIDATE CACHE - Update cache with new implantInvoices data
   if (
    typeof window !== "undefined" &&
    window?.__neoCache?.bookingById?.[surgeryId]
   ) {
    // Update the cache with new data instead of clearing it (preserves other fields)
    window.__neoCache.bookingById[surgeryId] = {
     ...window.__neoCache.bookingById[surgeryId],
     implantInvoices: nextInvoices,
    };
    console.log(
     `🔄 Cache updated with implantInvoices - count: ${nextInvoices.length}`
    );
   }

   // Also clear any in-flight requests to ensure fresh data on next fetch
   if (
    typeof window !== "undefined" &&
    window?.__neoInflight?.bookingById?.[surgeryId]
   ) {
    delete window.__neoInflight.bookingById[surgeryId];
   }

   console.log(
    `✅ State updated - implantInvoices count: ${nextInvoices.length}`
   );

   try {
    const actionSlug = invoiceRecord.isRegeneration
     ? `Regenerated implant invoice ${invoiceRecord.number} (supersedes ${invoiceRecord.supersedes})`
     : `Created implant invoice ${invoiceRecord.number}`;
    const entry = this.buildAuditEntry({
     actionType: "CREATE",
     actionSlug,
     actionMetadata: {
      panel: "BILLING",
      fieldName: "implantInvoices[]",
      changeType: "CREATE",
      previousValue: (prevInvoices || []).length,
      newValue: (nextInvoices || []).length,
      supersedes: invoiceRecord.supersedes,
     },
    });
    await this.sendAuditEntries([entry]);
   } catch (e) {}
   cogoToast.success("Implant invoice saved to server");
  } catch (err) {
   console.error("Failed to save implant invoice", err);
   cogoToast.error("Failed to save implant invoice to server");
  } finally {
   this.setState({ isSaving: false });
  }
 };

 generateImplantInvoice = async () => {
  if (this.blockIfCancelled()) return;

  try {
   // Step 1: Save implant bill items first (this will recompute totals)
   cogoToast.loading("Saving implant bill items...");
   await this.saveImplantBillInternal();

   // Step 2: Wait for next tick to ensure state is fresh
   await new Promise((resolve) => setTimeout(resolve, 0));

   // Step 3: Generate invoice with fresh state values
   cogoToast.loading("Generating implant invoice...");

   // Read all values from state in one place to ensure consistency
   const implantBill = this.state.implantBill;
   const prevInvNo = this.state.finalImplantInvoice?.number || null;
   const prevChecksum = this.state.finalImplantInvoice?.checksum || null;
   const isRegen = !!prevInvNo;

   // Always generate a NEW invoice number (counter increments on regeneration)
   const { invoiceNo, bookingReference, counter } =
    this.generateImplantInvoiceNo();

   const itemsSnapshot = JSON.parse(JSON.stringify(implantBill.items || []));
   const checksum = this.implantBillSignature(implantBill.items);
   const totalPaise = implantBill.totalPaise || 0;
   const totalBeforeDiscountPaise = implantBill.totalBeforeDiscountPaise || 0;
   const totalGstPaise = implantBill.totalGstPaise || 0;
   const totalDiscountPaise = implantBill.totalDiscountPaise || 0;

   // Log values for debugging
   console.log(
    `📊 Invoice ${isRegen ? "Regeneration" : "Generation"} - Current Values:`,
    {
     isRegen,
     invoiceNo,
     itemCount: itemsSnapshot.length,
     totalPaise,
     totalBeforeDiscountPaise,
     totalGstPaise,
     totalDiscountPaise,
     checksum,
     previousChecksum: prevChecksum,
     checksumChanged: checksum !== prevChecksum,
    }
   );

   if (isRegen) {
    console.log("🔄 Regeneration Changes:", {
     "Previous Invoice": prevInvNo,
     "New Invoice": invoiceNo,
     "Items Changed": checksum !== prevChecksum ? "YES" : "NO",
     "Previous Total": `₹${
      (this.state.finalImplantInvoice?.totalPaise || 0) / 100
     }`,
     "New Total": `₹${totalPaise / 100}`,
     "Total Changed":
      totalPaise !== (this.state.finalImplantInvoice?.totalPaise || 0)
       ? "YES"
       : "NO",
    });
   }

   // Build invoice record with exact structure
   const invoiceRecord = {
    number: invoiceNo,
    generatedAt: new Date(),
    generatedBy: this.getUser(),
    bookingReference,
    counter,
    itemsSnapshot,
    totalPaise,
    checksum,
    totalBeforeDiscountPaise,
    totalGstPaise,
    totalDiscountPaise,
    isRegeneration: isRegen,
    supersedes: isRegen ? prevInvNo : null,
    originalInvoiceNo: isRegen
     ? this.state.finalImplantInvoice?.originalInvoiceNo || prevInvNo
     : null,
   };

   console.log("📄 Implant Invoice Record (Exact Structure):", {
    number: invoiceRecord.number,
    generatedAt: invoiceRecord.generatedAt,
    generatedBy: invoiceRecord.generatedBy,
    bookingReference: invoiceRecord.bookingReference,
    counter: invoiceRecord.counter,
    itemsSnapshot: invoiceRecord.itemsSnapshot.map((item) => ({
     itemName: item.itemName,
     hsnCode: item.hsnCode,
     serialNo: item.serialNo,
     expiry: item.expiry,
     qty: item.qty,
     pricePaise: item.pricePaise,
     cgst: item.cgst,
     sgst: item.sgst,
     totalPaise: item.totalPaise,
    })),
    totalPaise: invoiceRecord.totalPaise,
    checksum: invoiceRecord.checksum,
    totalBeforeDiscountPaise: invoiceRecord.totalBeforeDiscountPaise,
    totalGstPaise: invoiceRecord.totalGstPaise,
    totalDiscountPaise: invoiceRecord.totalDiscountPaise,
   });

   console.log(
    "📤 Full Invoice Record to Server:",
    JSON.stringify(invoiceRecord, null, 2)
   );

   return new Promise((resolve, reject) => {
    this.setState({ finalImplantInvoice: invoiceRecord }, async () => {
     try {
      this.persistToMemory();

      // Audit: invoice generate/regenerate
      const entry = this.buildAuditEntry({
       actionType: isRegen ? "UPDATE" : "CREATE",
       actionSlug: isRegen
        ? `Regenerated implant invoice ${invoiceRecord.number}`
        : `Generated implant invoice ${invoiceRecord.number}`,
       actionMetadata: {
        panel: "BILLING",
        fieldName: "finalImplantInvoice.number",
        changeType: isRegen ? "UPDATE" : "CREATE",
        previousValue: prevInvNo || "",
        newValue: invoiceRecord.number,
        checksumBefore: prevChecksum || "",
        checksumAfter: invoiceRecord.checksum,
        totalPaise: invoiceRecord.totalPaise,
       },
      });

      await this.appendImplantInvoiceToServer(invoiceRecord);
      await this.sendAuditEntries([entry]);
      cogoToast.success(
       `Implant invoice ${isRegen ? "regenerated" : "generated"} • ${
        invoiceRecord.number
       }`
      );
      resolve(invoiceRecord);
     } catch (error) {
      console.error("Error in generateImplantInvoice:", error);
      cogoToast.error("Failed to generate implant invoice");
      reject(error);
     }
    });
   });
  } catch (error) {
   console.error("Error in generateImplantInvoice:", error);
   cogoToast.error("Failed to save implant bill before invoice generation");
   throw error;
  }
 };

 implantInvoiceDiverged = () => {
  if (!this.state.finalImplantInvoice?.checksum) return false;
  const current = this.implantBillSignature(this.state.implantBill.items);
  return current !== this.state.finalImplantInvoice.checksum;
 };

 renderInvoicePanel = () => {
  const inv = this.state.finalInvoice;
  const diverged = this.invoiceDiverged();
  // Patient header (shown always at top of invoice panel)
  const s = this.props.surgery || {};
  const activeAuth = (this.state.authorization.entries || [])[0] || {};
  const headerBlock = (
   <div
    style={{
     marginBottom: "10px",
     padding: "10px",
     border: "1px solid var(--border-color)",
     borderRadius: 6,
     backgroundColor: "var(--bg-surface)",
    }}
   >
    <div
     style={{
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      alignItems: "center",
     }}
    >
     <Tag large minimal>
      Patient: {s.patientName || "N/A"}
     </Tag>
     {activeAuth?.tpaName ? (
      <Tag large minimal>
       TPA: {activeAuth.tpaName}
      </Tag>
     ) : null}
     {activeAuth?.insuranceName ? (
      <Tag large minimal>
       Insurer: {activeAuth.insuranceName}
      </Tag>
     ) : null}
     <Tag large minimal>
      Age/Sex: {s.patientAge || "-"} / {s.patientSex || "-"}
     </Tag>
     <Tag large minimal>
      PID: {s.patientGenericPid || "-"}
     </Tag>
     <Tag large minimal>
      Phone: {s.patientPhone || "-"}
     </Tag>
    </div>
   </div>
  );

  if (!inv) {
   return (
    <div>
     {headerBlock}
     {/* <Text style={{ color: "var(--text-secondary)", marginBottom: 10 }}>
            Generate a consolidated invoice for this encounter. This snapshots the current surgery billing tables and
            totals.
          </Text> */}
     <Button
      icon="document"
      intent="primary"
      onClick={this.generateFinalInvoice}
     >
      Generate Final Invoice
     </Button>
     <Button
      style={{ marginLeft: 8 }}
      small
      icon="eye-open"
      onClick={this.handlePrintFinalInvoice}
     >
      Print Final Invoice
     </Button>
    </div>
   );
  }

  return (
   <div>
    {headerBlock}
    {diverged && (
     <Callout
      intent="warning"
      title="Invoice exists — review totals before reissue."
      style={{ marginBottom: 10 }}
     />
    )}
    <div
     style={{
      display: "flex",
      gap: 10,
      alignItems: "center",
      marginBottom: 10,
     }}
    >
     <Tag large minimal intent="primary">
      Invoice No.
     </Tag>
     <Tag large>{inv.number}</Tag>
     <Tag large minimal>
      Generated
     </Tag>
     <Tag large>{new Date(inv.generatedAt).toLocaleString()}</Tag>
     <Tag large minimal>
      By
     </Tag>
     <Tag large>
      {inv.generatedBy && inv.generatedBy.name
       ? inv.generatedBy.name
       : "Unknown"}
     </Tag>
     {/* <Button small icon="export" onClick={this.prepareSummaryForPrint}>
            Prepare Summary for Print
          </Button> */}
     <Button
      small
      icon="eye-open"
      onClick={() =>
       window.open(
        `${printApiUrl}/neo/sx/invoice/${this.getBookingId()}`,
        "_blank"
       )
      }
     >
      Print Final Invoice
     </Button>
     <Button
      small
      icon="refresh"
      intent="warning"
      onClick={this.generateFinalInvoice}
     >
      Regenerate Final Invoice
     </Button>
    </div>

    <div style={{ display: "grid", gap: 12 }}>
     {(inv.surgeryBillsSnapshot || []).map((sb, idx) => (
      <Card
       key={`inv-s-${idx}`}
       elevation={Elevation.ONE}
       style={{ padding: 12 }}
      >
       <div
        style={{
         marginBottom: 8,
         fontWeight: 600,
         color: "var(--text-primary)",
        }}
       >
        {sb.header?.printedName} - {sb.header?.surgeryName} - {sb.header?.group}
       </div>
       {/* Read-only 13-row view remains */}
       <div style={{ display: "grid", rowGap: 8 }}>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Surgery Rate (₹)
         </Tag>
         <Tag large>{indianRupee(sb.surgeryRatePaise || 0)}</Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Anaesthetic Charges (₹)
         </Tag>
         <Tag large>{indianRupee(sb.anaestheticChargesPaise || 0)}</Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Tissue Processing Charges (₹)
         </Tag>
         <Tag large>{indianRupee(sb.tissueProcessingPaise || 0)}</Tag>
        </ControlGroup>
        <div>
         <Tag minimal large style={{ minWidth: 280, marginRight: 8 }}>
          Manual entry
         </Tag>
         {(sb.manualRows || []).length === 0 ? (
          <Tag minimal large>
           None
          </Tag>
         ) : (
          (sb.manualRows || []).map((m, i) => (
           <div
            key={`m-${i}`}
            style={{
             display: "flex",
             gap: 8,
             alignItems: "center",
             marginTop: 4,
            }}
           >
            <Tag minimal large>
             {m.label || "-"}
            </Tag>
            <Tag large>{indianRupee(m.amountPaise || 0)}</Tag>
           </div>
          ))
         )}
        </div>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Procedure Tax (%) — CGST/SGST
         </Tag>
         <Tag large minimal>
          CGST {sb.taxProc?.cgst || 0}%
         </Tag>
         <Tag large minimal>
          SGST {sb.taxProc?.sgst || 0}%
         </Tag>
         <Tag large>
          Abs: {((sb.taxProc?.cgst || 0) + (sb.taxProc?.sgst || 0)).toFixed(2)}%
         </Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Procedure Cost (auto)
         </Tag>
         <Tag large>
          {sb.procedureCostRupees != null
           ? `₹${Number(sb.procedureCostRupees).toLocaleString("en-IN", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
             })}`
           : indianRupee(sb.procedureCostPaise || 0)}
         </Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Implant Cost (inclusive of tax) (₹)
         </Tag>
         <Tag large>{indianRupee(sb.implantCostInclusivePaise || 0)}</Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Implant Tax (%) — CGST/SGST
         </Tag>
         <Tag large minimal>
          CGST {sb.taxImplant?.cgst || 0}%
         </Tag>
         <Tag large minimal>
          SGST {sb.taxImplant?.sgst || 0}%
         </Tag>
         <Tag large>
          Abs:{" "}
          {((sb.taxImplant?.cgst || 0) + (sb.taxImplant?.sgst || 0)).toFixed(2)}
          %
         </Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Femto Charges (₹)
         </Tag>
         <Tag large>{indianRupee(sb.femtoChargesPaise || 0)}</Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          Special Discount (₹)
         </Tag>
         <Tag large>{indianRupee(sb.specialDiscountPaise || 0)}</Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          IOL Discount (₹)
         </Tag>
         <Tag large>{indianRupee(sb.iolDiscountPaise || 0)}</Tag>
        </ControlGroup>
        <ControlGroup>
         <Tag minimal large style={{ minWidth: 280 }}>
          MoU Discount
         </Tag>
         <Tag large minimal>
          % {sb.mouDiscountPercent || 0}
         </Tag>
         <Tag large>{indianRupee(sb.mouDiscountPaise || 0)}</Tag>
        </ControlGroup>
       </div>
       <div
        style={{
         display: "flex",
         justifyContent: "flex-end",
         marginTop: 10,
        }}
       >
        <Tag large intent="primary">
         Surgery Subtotal: {indianRupee(sb.subtotalPaise || 0)}
        </Tag>
       </div>
      </Card>
     ))}
     <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Tag large intent="primary">
       Overall Total: {indianRupee(inv.overallPaise || 0)}
      </Tag>
     </div>
    </div>
   </div>
  );
 };

 prepareSummaryForPrint = () => {
  if (!this.state.finalInvoice) {
   cogoToast.warn("Generate final invoice first");
   return;
  }
  const latestAuth = (this.state.authorization.entries || [])[0] || null;
  const payload = {
   invoice: {
    number: this.state.finalInvoice.number,
    generatedAt: this.state.finalInvoice.generatedAt,
    branchShort: this.state.finalInvoice.branchShort,
    overallPaise: this.state.finalInvoice.overallPaise,
    generatedBy: this.state.finalInvoice.generatedBy || null,
   },
   surgeries: this.state.finalInvoice.surgeryBillsSnapshot || [],
   payments: (this.state.payments.events || []).map((p) => ({
    moneyReceiptNo: p.moneyReceiptNo || null,
    ts: p.ts || null,
    type: p.type,
    mode: p.mode,
    amountPaise: p.amountPaise,
   })),
   authorization: latestAuth
    ? {
       type: latestAuth.type,
       amountRupees: latestAuth.amountRupees,
       ts: latestAuth.ts,
       letterFileId: latestAuth.letterFileId || null,
       letterFileName: latestAuth.letterFileName || null,
       remarks: latestAuth.remarks || null,
      }
    : null,
  };
  try {
   const text = JSON.stringify(payload);
   copyToClipboard(text);
   cogoToast.success("Summary prepared and copied to clipboard");
  } catch (e) {
   cogoToast.success("Summary prepared");
  }
  // Audit: prepared summary (non-material)
  try {
   const entry = this.buildAuditEntry({
    actionType: "VIEW",
    actionSlug: "Prepared summary for print",
    actionMetadata: {
     panel: "BILLING",
     fieldName: "print.summary.prepare",
     changeType: "VIEW",
     previousValue: "",
     newValue: { invoiceNo: this.state.finalInvoice.number },
    },
   });
   this.sendAuditEntries([entry]);
  } catch (e) {}
  // Open merged dummy PDF
  this.previewMergedSummaryPdf();
 };

 // ----- Audit helpers -----
 getUser = () => ({
  id: this.props.authStore.id || "",
  name: this.props.authStore.name || "Unknown",
 });
 getSurgeryId = () => this.props.surgery?.id || this.props.surgery?._id || "";

 // ----- Centralized Audit helpers (server-side durable logs) -----
 getAuditResourceInfo = () => {
  const resourceId = this.getSurgeryId();
  const resourceName = `Billing - ${
   this.props.surgery?.patientName || "Unknown Patient"
  }`;
  return { resourceId, resourceName };
 };

 buildAuditEntry = ({ actionType, actionSlug, actionMetadata }) => {
  const { resourceId, resourceName } = this.getAuditResourceInfo();
  if (!resourceId || !this.props.authStore?.prefix) return null;
  return {
   resourceType: "SURGERY_BILLING",
   resourceId,
   resourceName,
   actionType,
   actionSlug,
   actionMetadata,
  };
 };

 sendAuditEntries = async (entries) => {
  try {
   const payload = (entries || []).filter(Boolean);
   if (!payload.length) return;
   await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
    payload
   );
  } catch (e) {
   // Never block UX on audit errors
   console.warn("BillingTab audit send failed", e);
  }
 };

 recordAudit = (entry) => {
  const row = {
   surgeryId: this.getSurgeryId(),
   at: new Date(),
   ...entry,
   by: this.getUser(),
  };
  const auditEntries = [row, ...this.state.auditEntries];
  this.setState({
   auditEntries,
   lastUpdatedBy: row.by,
   lastUpdatedAt: row.at,
  });
 };

 // Primitive diff for flat objects
 diffObjects = (prev, next, basePath, panel, action) => {
  const changes = [];
  const keys = new Set([
   ...Object.keys(prev || {}),
   ...Object.keys(next || {}),
  ]);
  keys.forEach((k) => {
   if (["isUploading", "priceInput"].includes(k)) return; // skip ephemeral
   const p = prev ? prev[k] : undefined;
   const n = next ? next[k] : undefined;
   const same = JSON.stringify(p) === JSON.stringify(n);
   if (!same) {
    changes.push({
     panel,
     action,
     fieldPath: basePath ? `${basePath}.${k}` : k,
     prevValue: p,
     newValue: n,
    });
   }
  });
  return changes;
 };

 // Compute minimal before/after for consolidated billing fields
 deepEqualForAudit = (a, b) => {
  try {
   return JSON.stringify(a) === JSON.stringify(b);
  } catch (e) {
   return a === b;
  }
 };

 computeConsolidatedDiff = (prev, next) => {
  const KEYS = [
   "surgeryRatePaise",
   "anaestheticChargesPaise",
   "tissueProcessingPaise",
   "combinedRatePaise",
   "procedureTaxPercent",
   "procedureCostRupees",
   "implantCostInclusivePaise",
   "implantTaxPercent",
   "femtoChargesPaise",
   "specialDiscountPaise",
   "specialDiscountRemarks",
   "iolDiscountPaise",
   "mouDiscountPercent",
   "mouDiscountPaise",
   "selectedAnesthesiaId",
  ];
  const previousValue = {};
  const newValue = {};
  KEYS.forEach((k) => {
   const pv = prev ? prev[k] : undefined;
   const nv = next ? next[k] : undefined;
   if (!this.deepEqualForAudit(pv, nv)) {
    previousValue[k] = typeof pv !== "undefined" ? pv : "";
    newValue[k] = typeof nv !== "undefined" ? nv : "";
   }
  });
  return {
   previousValue,
   newValue,
   changedCount: Object.keys(newValue).length,
  };
 };

 getConsolidatedFieldLabel = (key) => {
  const MAP = {
   surgeryRatePaise: "Surgery Rate",
   anaestheticChargesPaise: "Anaesthetic Charges",
   tissueProcessingPaise: "Tissue Processing Charges",
   combinedRatePaise: "Package Cost",
   procedureTaxPercent: "Procedure Tax (%)",
   procedureCostRupees: "Procedure Cost",
   implantCostInclusivePaise: "Implant Cost (inclusive of tax)",
   implantTaxPercent: "Implant Tax (%)",
   femtoChargesPaise: "Femto Charges",
   specialDiscountPaise: "Special Discount",
   specialDiscountRemarks: "Special Discount Remarks",
   iolDiscountPaise: "IOL Discount",
   mouDiscountPercent: "MoU Discount (%)",
   mouDiscountPaise: "MoU Discount (₹)",
   selectedAnesthesiaId: "Anesthesia",
  };
  return MAP[key] || key;
 };

 formatConsolidatedDisplayValue = (key, value) => {
  if (value === "" || typeof value === "undefined" || value === null) return "";
  try {
   if (String(key).endsWith("Paise")) {
    return indianRupee(Number(value || 0));
   }
   if (String(key).endsWith("Rupees")) {
    const paise = Math.trunc((Number(value) || 0) * 100);
    return indianRupee(paise);
   }
   if (String(key).endsWith("Percent")) {
    const num = Number(value || 0);
    return `${num}%`;
   }
   if (key === "selectedAnesthesiaId") {
    const opt = (this.state.anesthesiaOptions || []).find(
     (o) => o.id === value
    );
    return opt?.name || String(value || "");
   }
   return String(value);
  } catch (e) {
   return String(value);
  }
 };

 // ----- UI helpers for Audit -----
 renderAuditDrawer = () => {
  const entries = (this.state.auditEntries || []).filter((e) => {
   const byPanel =
    this.state.auditFilter.panel === "ALL" ||
    e.panel === this.state.auditFilter.panel;
   const byAction =
    this.state.auditFilter.action === "ALL" ||
    e.action === this.state.auditFilter.action;
   return byPanel && byAction;
  });
  const uniquePanels = [
   "ALL",
   ...Array.from(new Set((this.state.auditEntries || []).map((e) => e.panel))),
  ];
  const uniqueActions = [
   "ALL",
   ...Array.from(new Set((this.state.auditEntries || []).map((e) => e.action))),
  ];
  return (
   <Drawer
    className="bp3-dark"
    isOpen={this.state.isAuditOpen}
    onClose={() => this.setState({ isAuditOpen: false })}
    title="Billing Audit Log"
    size={Drawer.SIZE_LARGE}
    style={{
     backgroundColor: "var(--bg-primary)",
     color: "var(--text-primary)",
    }}
   >
    <div
     className={DrawerClasses.DRAWER_BODY}
     style={{
      padding: 16,
      backgroundColor: "var(--bg-primary)",
      color: "var(--text-primary)",
     }}
    >
     <div
      style={{
       display: "flex",
       gap: 10,
       alignItems: "center",
       marginBottom: 10,
      }}
     >
      <Tag minimal large style={{ color: "var(--text-secondary)" }}>
       Panel
      </Tag>
      <HTMLSelect
       value={this.state.auditFilter.panel}
       onChange={(e) =>
        this.setState({
         auditFilter: {
          ...this.state.auditFilter,
          panel: e.target.value,
         },
        })
       }
      >
       {uniquePanels.map((p) => (
        <option key={p} value={p}>
         {p}
        </option>
       ))}
      </HTMLSelect>
      <Tag minimal large style={{ color: "var(--text-secondary)" }}>
       Action
      </Tag>
      <HTMLSelect
       value={this.state.auditFilter.action}
       onChange={(e) =>
        this.setState({
         auditFilter: {
          ...this.state.auditFilter,
          action: e.target.value,
         },
        })
       }
      >
       {uniqueActions.map((a) => (
        <option key={a} value={a}>
         {a}
        </option>
       ))}
      </HTMLSelect>
     </div>
     <HTMLTable
      striped
      interactive
      style={{
       width: "100%",
       color: "var(--text-primary)",
       backgroundColor: "var(--bg-primary)",
      }}
     >
      <thead
       style={{
        backgroundColor: "var(--bg-surface)",
        color: "var(--text-primary)",
       }}
      >
       <tr>
        <th>Time</th>
        <th>User</th>
        <th>Action</th>
        <th>Field</th>
        <th>Before → After</th>
       </tr>
      </thead>
      <tbody>
       {entries.map((e, i) => (
        <tr
         key={`aud-${i}`}
         style={{
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
         }}
        >
         <td>{new Date(e.at).toLocaleString()}</td>
         <td>{e.by?.name || "Unknown"}</td>
         <td>{e.action}</td>
         <td>{e.fieldPath}</td>
         <td>
          <div
           style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap",
           }}
          >
           <Tag minimal style={{ color: "var(--text-secondary)" }}>
            {typeof e.prevValue === "object"
             ? JSON.stringify(e.prevValue)
             : String(e.prevValue)}
           </Tag>
           <span>→</span>
           <Tag minimal intent="primary">
            {typeof e.newValue === "object"
             ? JSON.stringify(e.newValue)
             : String(e.newValue)}
           </Tag>
          </div>
         </td>
        </tr>
       ))}
      </tbody>
     </HTMLTable>
    </div>
   </Drawer>
  );
 };

  clearAuthorizationsAdmin = async () => {
   if (!isSxBillingSuperuser(this.props.authStore)) return;
   if (this.blockIfCancelled()) return;
   const surgeryId = this.getBookingId();
   const prefix = this.props.authStore?.prefix;
   if (!surgeryId || !prefix) {
    cogoToast.error("Surgery ID not found");
    return;
   }
   const prevCount = (this.state.authorization?.entries || []).length;
   if (prevCount === 0) {
    cogoToast.info("No authorizations to clear");
    return;
   }
   if (
    !window.confirm(
     `Remove all ${prevCount} authorization entr${prevCount === 1 ? "y" : "ies"} for this booking?`
    )
   ) {
    return;
   }
   const emptyAuth = { entries: [], activeIndex: -1 };
   try {
    cogoToast.loading("Clearing authorizations…");
    this.setState({ isSaving: true });
    const patchRes = await apiCallPatch(
     `/v3/${prefix}/neoSxBookingMaster/update/${surgeryId}`,
     {
      surgeryBookingId: surgeryId,
      authorization: emptyAuth,
     }
    );
    if (!patchRes?.success) {
     throw new Error(patchRes?.message || "Failed to clear authorizations on booking");
    }
    await new Promise((resolve) => {
     this.setState(
      (prev) => ({
       authorization: emptyAuth,
       surgeryData: {
        ...(prev.surgeryData || {}),
        authorization: emptyAuth,
       },
       hasUnsavedBillingChanges: true,
      }),
      resolve
     );
    });
    await this.saveBillingData({ skipAutoUnbill: true });
    this.emitSurgeryPatchToParent({ authorization: emptyAuth });
    this.persistToMemory();
    const entry = this.buildAuditEntry({
     actionType: "UPDATE",
     actionSlug: `Admin: cleared all authorizations (${prevCount} removed)`,
     actionMetadata: {
      panel: "BILLING",
      fieldName: "authorization.entries",
      changeType: "ADMIN_CLEAR",
      previousCount: prevCount,
      newValue: [],
      billingAdminOverride: true,
     },
    });
    await this.sendAuditEntries([entry]);
    cogoToast.success("Authorizations cleared on booking and billing snapshot");
   } catch (e) {
    console.error("clearAuthorizationsAdmin failed", e);
    cogoToast.error(
     e?.message?.includes("snapshot")
      ? "Failed to sync billing snapshot after clearing authorizations"
      : "Failed to clear authorizations on server"
    );
   } finally {
    this.setState({ isSaving: false });
   }
  };

  adminSetBilledFlag = async (toBilled) => {
   if (!isSxBillingSuperuser(this.props.authStore)) return;
   if (this.blockIfCancelled()) return;
   const surgeryId = this.getBookingId();
   if (!surgeryId) {
    cogoToast.error("Surgery ID not found");
    return;
   }
   const prevBilled = this.isCurrentlyBilled();
   if (Boolean(toBilled) === prevBilled) {
    cogoToast.info(toBilled ? "Already marked as billed" : "Already not billed");
    return;
   }
   const ok = window.confirm(
    toBilled
     ? "Mark this booking as billed (admin override — balance rules skipped)?"
     : "Set billing status to NOT billed and clear billed metadata?"
   );
   if (!ok) return;
   try {
    this.setState({ isSaving: true });
    cogoToast.loading(toBilled ? "Marking as billed..." : "Updating billed status...");
    if (toBilled) {
     const nowIso = new Date().toISOString();
     const billedBy = {
      id: this.props.authStore.id || "",
      name: this.props.authStore.name || "",
     };
     const previousStatus = String(
      this.state.surgeryData?.status || this.props.surgery?.status || ""
     );
     const res = await apiCallPatch(
      `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
      { isBilled: true, markedBilledAt: nowIso, markedBilledBy: billedBy }
     );
     if (!res?.success) throw new Error(res?.message || "Failed to update");
     this.setState((s) => ({
      surgeryData: {
       ...(s.surgeryData || {}),
       isBilled: true,
       markedBilledAt: nowIso,
       markedBilledBy: billedBy,
      },
     }));
     try {
      if (typeof this.props.onStatusChange === "function") {
       this.props.onStatusChange({
        isBilled: true,
        markedBilledAt: nowIso,
        markedBilledBy: billedBy,
       });
      }
     } catch (e) {}
     const entry = this.buildAuditEntry({
      actionType: "UPDATE",
      actionSlug: "Admin override: marked as billed",
      actionMetadata: {
       panel: "BILLING",
       fieldName: "isBilled",
       changeType: "ADMIN_UPDATE",
       previousValue: prevBilled,
       newValue: true,
       previousStatus: previousStatus || "",
       markedBilledAt: nowIso,
       markedBilledBy: billedBy,
       billingAdminOverride: true,
      },
     });
     await this.sendAuditEntries([entry]);
     cogoToast.success("Marked as billed (admin)");
    } else {
     const res = await apiCallPatch(
      `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
      { isBilled: false }
     );
     if (!res?.success) throw new Error(res?.message || "Failed to update");
     this.setState((prev) => ({
      surgeryData: {
       ...(prev.surgeryData || {}),
       isBilled: false,
       // Preserve markedBilledAt / markedBilledBy for audit (server + UI) unless API clears them
      },
     }));
     try {
      if (typeof this.props.onStatusChange === "function") {
       this.props.onStatusChange({
        isBilled: false,
       });
      }
     } catch (e) {}
     this.emitSurgeryPatchToParent({
      isBilled: false,
     });
     const entry = this.buildAuditEntry({
      actionType: "UPDATE",
      actionSlug: "Admin override: set not billed",
      actionMetadata: {
       panel: "BILLING",
       fieldName: "isBilled",
       changeType: "ADMIN_UPDATE",
       previousValue: prevBilled,
       newValue: false,
       billingAdminOverride: true,
      },
     });
     await this.sendAuditEntries([entry]);
     cogoToast.success("Set to not billed (admin)");
    }
   } catch (err) {
    console.error("adminSetBilledFlag failed", err);
    cogoToast.error("Failed to update billed status");
   } finally {
    this.setState({ isSaving: false });
   }
  };

  // Mark surgery as Billed when balance is zero
  markSurgeryBilled = async () => {
   if (this.state.hasUnsavedBillingChanges) {
     cogoToast.warn("Please save your billing changes before marking as billed.");
     return;
   }
    if (this.blockIfCancelled()) return;
    const { ok } = await this.guardFreshBeforeBillingWrite();
    if (!ok) return;
    try {
      const billingBlock = this.getMarkBilledBlockReason();
      if (billingBlock) {
        cogoToast.warn(billingBlock, { hideAfter: 6 });
        return;
      }
      const surgeryId = this.getBookingId();
      if (!surgeryId) {
        cogoToast.error("Surgery ID not found");
        return;
      }
      const previousStatus = String(
        this.state.surgeryData?.status || this.props.surgery?.status || ""
      );
      cogoToast.loading("Marking as billed...");
      this.setState({ isSaving: true });
      const nowIso = new Date().toISOString();
      const billedBy = {
        id: this.props.authStore.id || "",
        name: this.props.authStore.name || "",
      };
      const res = await apiCallPatch(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
        { isBilled: true, markedBilledAt: nowIso, markedBilledBy: billedBy }
      );
      if (res?.success) {
        this.setState((s) => ({
          surgeryData: {
            ...(s.surgeryData || {}),
            isBilled: true,
            markedBilledAt: nowIso,
            markedBilledBy: billedBy,
          },
        }));
        // Notify parent so header/actions can reflect new billed flags
        try {
          if (typeof this.props.onStatusChange === "function") {
            this.props.onStatusChange({
              isBilled: true,
              markedBilledAt: nowIso,
              markedBilledBy: billedBy,
            });
          }
        } catch (e) {}
        try {
          const entry = this.buildAuditEntry({
            actionType: "UPDATE",
            actionSlug: `Marked as billed`,
            actionMetadata: {
              panel: "BILLING",
              fieldName: "isBilled",
              changeType: "UPDATE",
              previousValue: false,
              newValue: true,
              previousStatus: previousStatus || "",
              markedBilledAt: nowIso,
              markedBilledBy: billedBy,
            },
          });
          await this.sendAuditEntries([entry]);
        } catch (e) {}
        cogoToast.success("Marked as Billed");
      } else {
        throw new Error(res?.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Failed to mark as billed", err);
      cogoToast.error("Failed to mark as billed");
    } finally {
      this.setState({ isSaving: false });
    }
  };

 // ----- Existing rendering (header, sections) -----
render() {
 const {
  totalReceivable,
  authorized,
  netPayable,
  collected,
  refunded,
  balance,
 } = this.computeSummary();
 const isLocked = this.props.canEdit === false;
 const statusNorm = String(this.props.surgery?.status || this.state.surgeryData?.status || "")
  .trim()
  .toLowerCase();
 const isHardLocked = this.isSurgeryCancelled() || statusNorm === "discharged";
 // After billed, lock consolidated billing + tools; keep Payments & History usable.
 const isBillingLocked = isLocked;
 const isPaymentLocked = isHardLocked;
    const payorPanel =
        this.state.surgeryData?.payorPanel || this.props.surgery?.payorPanel || "";
    const isCashPayorPanel =
        String(payorPanel || "")
            .trim()
            .toLowerCase() === "cash";
        const showInitialSpinner =
            this.state.isLoading && !this.state.hasLoadedInitialData;

        if (showInitialSpinner) {
            return (
                <div
                    style={{
                        minHeight: "420px",
                        padding: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "16px",
                        backgroundColor: "var(--bg-primary)",
                        color: "var(--text-primary)",
                    }}
                >
                    <Spinner intent="primary" size={48} />
                    <Text style={{ color: "var(--text-secondary)", fontSize: "16px" }}>
                        Loading billing details...
                    </Text>
                </div>
            );
        }

  // const activeAuth =
  //   this.state.authorization.activeIndex >= 0
  //     ? this.state.authorization.entries[this.state.authorization.activeIndex]
  //     : null;

  return (
   <div style={{ padding: "20px", paddingTop: "0px", position: "relative" }}>
    {isSxBillingSuperuser(this.props.authStore) &&
     !this.isSurgeryCancelled() &&
     String(this.props.surgery?.status || "").trim().toLowerCase() !== "discharged" && (
     <Callout
      intent="primary"
      icon="key"
      style={{ marginBottom: "16px" }}
      title="Billing administration"
     >
      <Text style={{ color: "var(--text-primary)", marginBottom: "10px", display: "block" }}>
       Restricted tools: clear insurance authorizations or change billed status without normal balance
       checks. All actions are audit-logged.
      </Text>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
       <Button
        icon="eraser"
        onClick={this.clearAuthorizationsAdmin}
        disabled={this.state.isSaving}
       >
        Clear all authorizations
       </Button>
       <Button
        icon="cross"
        intent="warning"
        onClick={() => this.adminSetBilledFlag(false)}
        disabled={this.state.isSaving || !this.isCurrentlyBilled()}
       >
        Set not billed
       </Button>
       <Button
        icon="tick"
        intent="primary"
        onClick={() => this.adminSetBilledFlag(true)}
        disabled={this.state.isSaving || this.isCurrentlyBilled()}
       >
        Set billed
       </Button>
      </div>
     </Callout>
    )}
    {isLocked && (
     <Card
      elevation={Elevation.TWO}
      style={{
       backgroundColor: "#5c255c",
       marginBottom: "16px",
       padding: "10px 16px",
       display: "flex",
       alignItems: "center",
       gap: "10px",
      }}
     >
      <Tag intent="warning" icon="lock" large minimal>
       Locked
      </Tag>
      <Text style={{ color: "#f5f8fa" }}>
       {this.props.lockReason ||
        "Billing fields are locked because this surgery has been discharged or cancelled."}
      </Text>
     </Card>
    )}
    {/* Header row with title and audit */}
    {/* <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <H3 style={{ marginBottom: "20px", color: "var(--text-primary)" }}>Billing</H3>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {this.state.lastUpdatedBy && (
              <Text style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                Last updated by {this.state.lastUpdatedBy.name} at {new Date(this.state.lastUpdatedAt).toLocaleString()}
              </Text>
            )}
            <Button small icon="clipboard" onClick={() => this.setState({ isAuditOpen: true })}>
              Audit
            </Button>
          </div>
        </div> */}

    {/* Sticky Summary */}
    <div
     style={{
      position: "fixed",
      top: "50%",
      right: 10,
      transform: "translateY(-50%)",
      zIndex: 5,
     }}
    >
     <Card
      elevation={Elevation.THREE}
      style={{
       padding: "10px",
       minWidth: "300px",
       marginRight: "20px",
       marginBottom: "20px",
      }}
     >
      <div
       style={{
        display: "grid",
        gridTemplateColumns: "auto auto",
        gap: "8px",
       }}
      >
       <Tag large minimal intent="primary">
        Total Receivable
       </Tag>
       <Tag large>{indianRupee(totalReceivable)}</Tag>
       <Tag large minimal intent="success">
        Authorization
       </Tag>
       <Tag large>{indianRupee(authorized)}</Tag>
       <Tag large minimal intent="warning">
        Net Payable
       </Tag>
       <Tag large>{indianRupee(netPayable)}</Tag>
       <Tag large minimal intent="success">
        Collected
       </Tag>
       <Tag large>{indianRupee(collected)}</Tag>
       <Tag large minimal intent="danger">
        Refunded
       </Tag>
       <Tag large>{indianRupee(refunded)}</Tag>
       <Tag
        large
        minimal
        intent={balance > 0 ? "warning" : balance < 0 ? "danger" : "success"}
       >
        Balance
       </Tag>
       <Tag
        large
        intent={balance > 0 ? "warning" : balance < 0 ? "danger" : "success"}
       >
        {indianRupee(balance)}
       </Tag>
          {(() => {
            const isBilled = Boolean(
                this.state.surgeryData?.isBilled || this.props.surgery?.isBilled
            );
            if (isBilled || this.state.hasUnsavedBillingChanges) return null;
            const blockReason = this.getMarkBilledBlockReason();
            if (blockReason) {
                const entries = this.state.authorization?.entries || [];
                const latestIdx = this.getLatestAuthIndex(entries);
                const latestIsInactiveFinal =
                    !isCashPayorPanel &&
                    latestIdx >= 0 &&
                    this.isFinalAuthType(entries[latestIdx]?.type) &&
                    this.state.authorization?.activeIndex !== latestIdx;
                if (!latestIsInactiveFinal) return null;
                return (
                    <Text
                        style={{
                            gridColumn: "1 / span 2",
                            marginTop: 4,
                            fontSize: 12,
                            color: "var(--text-secondary)",
                            maxWidth: 280,
                        }}
                    >
                        {blockReason}
                    </Text>
                );
            }
             return (
               <div style={{ gridColumn: "1 / span 2", marginTop: 4 }}>
                 <Button
                   fill
                   icon="endorsed"
                   intent="success"
                   onClick={this.markSurgeryBilled}
                   loading={this.state.isSaving}
                 >
                   Mark Sx Billed
                 </Button>
               </div>
             );
           })()}
      </div>
     </Card>
    </div>

    {/* 1) Authorization */}
    {!isCashPayorPanel && (
        <Card
         elevation={Elevation.ONE}
         style={{ padding: "15px", marginBottom: "15px" }}
        >
         {this.renderHeader("Authorization", this.state.isAuthOpen, () =>
          this.setState({ isAuthOpen: !this.state.isAuthOpen })
         )}
         <Collapse isOpen={this.state.isAuthOpen}>
      <fieldset disabled={isBillingLocked} style={{ border: "none", padding: 0, margin: 0 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
       {/* Row 1: Amount and Type */}
       <div
        style={{
         display: "grid",
         gridTemplateColumns: "160px 250px 160px 250px",
         gap: "12px",
         alignItems: "center",
        }}
       >
        <Tag minimal large>
         Authorized Amount
        </Tag>
        <NumericInput
         placeholder="0"
         rightElement={
          <Tag minimal>
           {`≤ ${indianRupee(totalReceivable)}`}
          </Tag>
         }
         intent={
          Number(this.state.authForm.amountRupees || 0) * 100 > totalReceivable
           ? "danger"
           : "none"
         }
         title="Authorization amount cannot exceed Total Receivable"
         fill
         value={this.state.authForm.amountRupees}
         onValueChange={(v) =>
          this.setState((s) => ({
           authForm: {
            ...s.authForm,
            amountRupees: Math.max(0, v || 0),
           },
           authFormDirty: true,
          }))
         }
        />
        <Tag minimal large>
         Type
        </Tag>
        <SingleSelectWrapper
         items={this.createAuthTypeItems()}
         selectedItem={this.state.authForm.type || null}
         placeholder={"Select Type"}
         onChange={this.handleAuthTypeSelect}
         width="100%"
         noResultsText={"No options"}
         inputProps={{
          className: "v3-select",
         }}
        />
       </div>

       {/* Row 2: TPA and Insurance */}
       <div
        style={{
         display: "grid",
         gridTemplateColumns: "160px 250px 160px 250px",
         gap: "12px",
         alignItems: "center",
        }}
       >
        <Tag minimal large>
         TPA
        </Tag>
        <SingleSelectWrapper
         items={this.createTPAItems()}
         selectedItem={this.state.authForm.tpaName || null}
         placeholder={
          this.state.isLoadingTPA ? "Loading TPAs..." : "Search & Select TPA"
         }
         disabled={this.state.isLoadingTPA}
         onChange={this.handleTPASelect}
         width="100%"
         noResultsText={
          this.state.isLoadingTPA ? "Loading TPAs..." : "No TPAs found"
         }
         inputProps={{
          className: "v3-select",
          onFocus: () => {
           console.log("🎯 TPA dropdown focused, loading options...");
           this.loadTPAOptions();
          },
         }}
        />
        <Tag minimal large>
         Insurance
        </Tag>
        <SingleSelectWrapper
         items={this.createInsuranceItems()}
         selectedItem={this.state.authForm.insuranceName || null}
         placeholder={
          this.state.isLoadingInsurance
           ? "Loading insurers..."
           : "Search & Select Insurance"
         }
         disabled={this.state.isLoadingInsurance}
         onChange={this.handleInsuranceSelect}
         width="100%"
         noResultsText={
          this.state.isLoadingInsurance
           ? "Loading insurers..."
           : "No insurance companies found"
         }
         inputProps={{
          className: "v3-select",
          onFocus: () => {
           console.log("🎯 Insurance dropdown focused, loading options...");
           this.loadInsuranceOptions();
          },
         }}
        />
       </div>

       {/* Insurance No. - Commented out as requested */}
       {/* <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr",
                    gap: "12px",
                    alignItems: "center",
                  }}
                >
                  <Tag minimal large>
                    Insurance No.
                  </Tag>
                  <InputGroup
                    fill
                    placeholder="Enter insurance/policy number"
                    value={this.state.authForm.insuranceNo}
                    onChange={(e) =>
                      this.setState((s) => ({
                        authForm: { ...s.authForm, insuranceNo: e.target.value },
                        authFormDirty: true,
                      }))
                    }
                  />
                </div> */}

       {/* Row 3: CCN and Claim Payor */}
       <div
        style={{
         display: "grid",
         gridTemplateColumns: "160px 250px 160px 250px",
         gap: "12px",
         alignItems: "center",
        }}
       >
        <Tag minimal large>
         CC No
        </Tag>
        <InputGroup
         fill
         placeholder="Enter Claim No"
         value={this.state.authForm.ccn}
         onChange={(e) =>
          this.setState((s) => ({
           authForm: { ...s.authForm, ccn: e.target.value },
           authFormDirty: true,
          }))
         }
        />
        <Tag minimal large>
         Claim Payor
        </Tag>
        <SingleSelectWrapper
         items={this.createClaimPayorItems()}
         selectedItem={this.state.authForm.claimPayor || null}
         placeholder={"Select Claim Payor"}
         onChange={this.handleClaimPayorSelect}
         width="100%"
         noResultsText={"No options"}
         inputProps={{
          className: "v3-select",
         }}
        />
       </div>

       {/* Row 4: Remarks with Save Button */}
       <div
        style={{
         display: "grid",
         gridTemplateColumns: "160px 500px 100px",
         gap: "12px",
         alignItems: "center",
        }}
       >
        <Tag minimal large>
         Remarks
        </Tag>
        <InputGroup
         fill
         placeholder="Enter remarks or notes (optional)"
         value={this.state.authForm.remarks}
         onChange={(e) =>
          this.setState((s) => ({
           authForm: { ...s.authForm, remarks: e.target.value },
           authFormDirty: true,
          }))
         }
        />
        {!isBillingLocked && (
        <Button
         icon="floppy-disk"
         intent="primary"
         onClick={this.saveAuthorizationEntry}
         loading={this.state.authForm.isUploading || this.state.isSaving}
         disabled={
          !this.state.authFormDirty ||
          this.state.authForm.isUploading ||
          this.state.isSaving
         }
        >
         Save
        </Button>
        )}
       </div>

       {/* Letter - Commented out as requested */}
       {/* <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr",
                    gap: "12px",
                    alignItems: "center",
                  }}
                >
                  <Tag minimal large>
                    Letter
                  </Tag>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      onChange={(e) => this.handleLetterUpload(e.target.files?.[0])}
                      disabled={this.state.authForm.isUploading}
                    />
                    {this.state.authForm.letterFileName ? (
                      <Tag large minimal intent="success">
                        {this.state.authForm.letterFileName}
                      </Tag>
                    ) : null}
                  </div>
                </div> */}

       {/* Authorization History */}
       <div style={{ marginTop: "6px" }}>
        <H4
         style={{
          margin: 0,
          color: "var(--text-primary)",
          fontSize: "14px",
         }}
        >
         Authorization History
        </H4>
        {this.state.authorization.entries.length === 0 ? (
         <Text
          style={{
           color: "var(--text-secondary)",
           fontStyle: "italic",
          }}
         >
          No authorizations yet.
         </Text>
        ) : (
         <div style={{ display: "grid", gap: "8px", marginTop: "8px" }}>
          {this.state.authorization.entries.map((e, idx) => (
           <Card
            key={`authrow-${idx}`}
            elevation={Elevation.ZERO}
            style={{
             padding: "8px",
             display: "flex",
             alignItems: "center",
             justifyContent: "space-between",
            }}
           >
            <div
             style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
             }}
            >
             <Tag minimal large intent="success">
              {e.type}
             </Tag>
             <Tag large>
              ₹
              {(e.amountRupees || 0).toLocaleString("en-IN", {
               minimumFractionDigits: 2,
               maximumFractionDigits: 2,
              })}
             </Tag>
             {e.tpaName ? (
              <Tag minimal large>
               TPA: {e.tpaName}
              </Tag>
             ) : null}
             {e.insuranceName ? (
              <Tag minimal large>
               Insurer: {e.insuranceName}
              </Tag>
             ) : null}
             {e.insuranceNo ? (
              <Tag minimal large>
               Policy: {e.insuranceNo}
              </Tag>
             ) : null}
             {e.ccn ? (
              <Tag minimal large>
               CC No: {e.ccn}
              </Tag>
             ) : null}
             {e.claimPayor ? (
              <Tag minimal large>
               Claim Payor: {e.claimPayor}
              </Tag>
             ) : null}
             {e.uploadedByName ? (
              <Tag minimal large>
               By: {e.uploadedByName}
              </Tag>
             ) : null}
             {e.ts ? (
              <Tag minimal large>
               {new Date(e.ts).toLocaleString()}
              </Tag>
             ) : null}
             {e.remarks ? (
              <Tag minimal large>
               Note: {e.remarks}
              </Tag>
             ) : null}
            </div>
            <div
             style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
             }}
            >
             {this.state.authorization.activeIndex === idx ? (
              <Tag intent="primary" large minimal>
               Active
              </Tag>
             ) : (
              <Button
               small
               icon="endorsed"
               disabled={Boolean(this.getSetActiveBlockReason(idx))}
               title={this.getSetActiveBlockReason(idx) || ""}
               onClick={async () => {
                const blockReason = this.getSetActiveBlockReason(idx);
                if (blockReason) {
                 cogoToast.warn(blockReason);
                 return;
                }
                const prev = this.state.authorization.activeIndex;
                this.setState(
                 (s) => ({
                  authorization: {
                   ...s.authorization,
                   activeIndex: idx,
                  },
                 }),
                 this.persistToMemory
                );
                // Audit: set active authorization
                try {
                 const entry = this.buildAuditEntry({
                  actionType: "UPDATE",
                  actionSlug: "Set active authorization",
                  actionMetadata: {
                   panel: "BILLING",
                   fieldName: "authorization.activeIndex",
                   changeType: "UPDATE",
                   previousValue: typeof prev === "number" ? prev : "",
                   newValue: idx,
                  },
                 });
                 await this.sendAuditEntries([entry]);
                } catch (e) {}
               }}
              >
               Set Active
              </Button>
             )}
            </div>
           </Card>
          ))}
         </div>
        )}
       </div>
      </div>
      </fieldset>
         </Collapse>
        </Card>
    )}

    {/* 2) Line Items & Totals */}
    <Card
     elevation={Elevation.ONE}
     style={{ padding: "15px", marginBottom: "15px" }}
    >
     {this.renderHeader(
      "Consolidated Line Items & Totals",
      this.state.isLineItemsOpen,
      () => this.setState({ isLineItemsOpen: !this.state.isLineItemsOpen })
     )}
     <Collapse isOpen={this.state.isLineItemsOpen}>
      <fieldset disabled={isBillingLocked} style={{ border: "none", padding: 0, margin: 0 }}>
      {/* Helper text for consolidated billing */}
      {/* <Text
              style={{
                color: "var(--text-secondary)",
                fontSize: 12,
                marginBottom: 6,
              }}
            >
              Consolidated billing across all selected surgeries. Values are automatically summed where applicable.
            </Text> */}
      {/* Surgery cards */}
      <div style={{ display: "grid", gap: "12px" }}>
       {this.state.surgeryBills.map((s, idx) => {
        // Debug log for the dropdown value
        console.log(
         `🔍 BillingTab: Rendering surgery bill ${idx} - selectedAnesthesiaId:`,
         s.selectedAnesthesiaId,
         "options:",
         this.state.anesthesiaOptions.length
        );

        return (
         <Card
          key={`sb-${idx}`}
          elevation={Elevation.TWO}
          style={{ padding: "12px" }}
         >
          {/* Row 1 */}
          <div
           style={{
            marginBottom: "8px",
            color: "var(--text-primary)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
           }}
          >
           <div style={{ fontWeight: 600 }}>
            {(() => {
             const selectedSurgeries =
              this.state.surgeryData?.selectedSurgeries ||
              this.props.surgeries ||
              [];
             if (Array.isArray(selectedSurgeries) && selectedSurgeries.length) {
              const names = selectedSurgeries
               .map(
                (item) =>
                 item?.name ||
                 item?.printedName ||
                 item?.procedure ||
                 item?.surgeryName ||
                 ""
               )
               .filter(Boolean);
              if (names.length) {
               return names.join(", ");
              }
             }
             return (
              s.header?.printedName ||
              s.header?.surgeryName ||
              this.props.surgery?.procedure ||
              "Surgery"
             );
            })()}
           </div>
          </div>

          {/* Table Rows */}
          <div style={{ display: "grid", rowGap: 8 }}>
           {/* Row 2 */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Surgery Rate (₹)
            </Tag>
            <NumericInput
             value={(s.surgeryRatePaise || 0) / 100}
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               surgeryRatePaise: Math.max(0, Math.trunc((v || 0) * 100)),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
           </ControlGroup>
           {/* Row 3 */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Anaesthetic Charges (₹)
            </Tag>
            <HTMLSelect
             value={s.selectedAnesthesiaId || ""}
             onChange={(e) => {
              const selectedOption = this.state.anesthesiaOptions.find(
               (opt) => opt.id === e.target.value
              );
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               selectedAnesthesiaId: e.target.value,
               anaestheticChargesPaise: selectedOption
                ? Math.trunc(selectedOption.rate * 100)
                : 0,
              }));

              // Also update the parent component's anesthesia selection if this is the consolidated bill
              if (idx === 0 && this.props.onAnesthesiaChange) {
               this.props.onAnesthesiaChange(e.target.value);
              }
             }}
             onFocus={() => {
              console.log(
               "🎯 Anesthesia dropdown focused - current value:",
               s.selectedAnesthesiaId,
               "options:",
               this.state.anesthesiaOptions.length
              );
              this.loadAnesthesiaOptions();
             }}
             style={{ width: 200 }}
            >
             <option value="">Select Anesthesia</option>
             {this.state.anesthesiaOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
               {opt.name} - ₹{opt.rate}
              </option>
             ))}
            </HTMLSelect>
            <NumericInput
             value={(s.anaestheticChargesPaise || 0) / 100}
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               anaestheticChargesPaise: Math.max(0, Math.trunc((v || 0) * 100)),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 120, marginLeft: 8 }}
             placeholder="Rate"
            />
           </ControlGroup>
           {/* Row 4: Tissue Processing Charges moved above Rate */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Tissue Processing Charges (₹)
            </Tag>
            <NumericInput
             value={(s.tissueProcessingPaise || 0) / 100}
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               tissueProcessingPaise: Math.max(0, Math.trunc((v || 0) * 100)),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
           </ControlGroup>
           {/* Row 5: Package Cost (derived, not editable) */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Package Cost (₹)
            </Tag>
            <NumericInput
             value={this.computePackageCostPaise(s) / 100}
             disabled
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
           </ControlGroup>
           {/* Manual entry removed */}
           {/* Row 6 Procedure Tax % */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Procedure Tax (%) — CGST/SGST
            </Tag>
            <InputGroup
             placeholder="CGST %"
             value={
              s.taxProcInputCgst !== undefined
               ? s.taxProcInputCgst
               : String(s.taxProc.cgst || 0)
             }
             onChange={(e) =>
              this.updateSurgeryBill(idx, (sb) => {
               const raw = e.target.value;
               const cgstVal = parseFloat(raw);
               const next = { ...sb };
               next.taxProcInputCgst = raw;
               if (!Number.isNaN(cgstVal)) {
                next.taxProc = {
                 ...sb.taxProc,
                 cgst: Math.max(0, cgstVal),
                };
               }
               return next;
              })
             }
             rightElement={<Tag minimal>%</Tag>}
             style={{ width: 100 }}
            />
            <InputGroup
             placeholder="SGST %"
             value={
              s.taxProcInputSgst !== undefined
               ? s.taxProcInputSgst
               : String(s.taxProc.sgst || 0)
             }
             onChange={(e) =>
              this.updateSurgeryBill(idx, (sb) => {
               const raw = e.target.value;
               const sgstVal = parseFloat(raw);
               const next = { ...sb };
               next.taxProcInputSgst = raw;
               if (!Number.isNaN(sgstVal)) {
                next.taxProc = {
                 ...sb.taxProc,
                 sgst: Math.max(0, sgstVal),
                };
               }
               return next;
              })
             }
             rightElement={<Tag minimal>%</Tag>}
             style={{ width: 100 }}
            />
            <Tag large minimal>
             Abs: {indianRupee(s.taxProc.absolutePaise || 0)}
            </Tag>
           </ControlGroup>
           {/* Row 7 Procedure Cost (auto) */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Procedure Cost (₹)
            </Tag>
            <NumericInput
             value={
              s.procedureCostRupees != null
               ? s.procedureCostRupees
               : Math.round((s.procedureCostPaise || 0) / 100)
             }
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               // Store in rupees to allow manual override
               procedureCostRupees: Math.max(0, v || 0),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
           </ControlGroup>
           {/* Row 8 Implant Cost inclusive */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Implant Cost (inclusive of tax) (₹)
            </Tag>
            <NumericInput
             value={(s.implantCostInclusivePaise || 0) / 100}
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               implantCostInclusivePaise: Math.max(
                0,
                Math.trunc((v || 0) * 100)
               ),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
           </ControlGroup>
           {/* Row 9 IOL Discount (moved above tax) */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             IOL Discount (₹)
            </Tag>
            <NumericInput
             value={(s.iolDiscountPaise || 0) / 100}
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               iolDiscountPaise: Math.max(0, Math.trunc((v || 0) * 100)),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
           </ControlGroup>
           {/* Row 10 Implant Cost - IOL Discount (inclusive of taxes) */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Implant Cost − IOL discount (inclusive of taxes) (₹)
            </Tag>
            <Tag large>
             {`${indianRupee(
              Math.max(
               0,
               (s.implantCostInclusivePaise || 0) - (s.iolDiscountPaise || 0)
              )
             ).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
            </Tag>
           </ControlGroup>
           {/* Row 11 Implant Tax % */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Implant Tax (%) — CGST/SGST
            </Tag>
            <InputGroup
             placeholder="CGST %"
             value={
              s.taxImplantInputCgst !== undefined
               ? s.taxImplantInputCgst
               : String(s.taxImplant.cgst || 0)
             }
             onChange={(e) =>
              this.updateSurgeryBill(idx, (sb) => {
               const raw = e.target.value;
               const cgstVal = parseFloat(raw);
               const next = { ...sb };
               next.taxImplantInputCgst = raw;
               if (!Number.isNaN(cgstVal)) {
                next.taxImplant = {
                 ...sb.taxImplant,
                 cgst: Math.max(0, cgstVal),
                };
               }
               return next;
              })
             }
             rightElement={<Tag minimal>%</Tag>}
             style={{ width: 100 }}
            />
            <InputGroup
             placeholder="SGST %"
             value={
              s.taxImplantInputSgst !== undefined
               ? s.taxImplantInputSgst
               : String(s.taxImplant.sgst || 0)
             }
             onChange={(e) =>
              this.updateSurgeryBill(idx, (sb) => {
               const raw = e.target.value;
               const sgstVal = parseFloat(raw);
               const next = { ...sb };
               next.taxImplantInputSgst = raw;
               if (!Number.isNaN(sgstVal)) {
                next.taxImplant = {
                 ...sb.taxImplant,
                 sgst: Math.max(0, sgstVal),
                };
               }
               return next;
              })
             }
             rightElement={<Tag minimal>%</Tag>}
             style={{ width: 100 }}
            />
            <Tag large minimal>
             Abs:{" "}
             {(
              (((s.taxImplant.cgst || 0) + (s.taxImplant.sgst || 0)) *
               Math.max(
                0,
                (s.implantCostInclusivePaise || 0) - (s.iolDiscountPaise || 0)
               )) /
              10000
             ).toLocaleString("en-IN", {
              style: "currency",
              currency: "INR",
              minimumFractionDigits: 2,
             })}
            </Tag>
           </ControlGroup>
           {/* Row 12 Femto Charges */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Femto Charges (₹)
            </Tag>
            <NumericInput
             value={(s.femtoChargesPaise || 0) / 100}
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               femtoChargesPaise: Math.max(0, Math.trunc((v || 0) * 100)),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
           </ControlGroup>
           {/* Row 13 MoU Discount (fetched percent, editable) */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             MoU Discount
            </Tag>
            <InputGroup
             placeholder="0.00"
             value={
              s.mouDiscountPercentInput !== undefined
               ? s.mouDiscountPercentInput
               : String(s.mouDiscountPercent || 0)
             }
             onChange={(e) => {
              const inputValue = e.target.value;
              // Allow typing decimal numbers and store raw input
              if (inputValue === "" || /^\d*\.?\d*$/.test(inputValue)) {
               // Store the raw input value for display
               this.updateSurgeryBill(idx, (sb) => ({
                ...sb,
                mouDiscountPercentInput: inputValue,
               }));
              }
             }}
             onBlur={(e) => {
              // Process the final value on blur
              const inputValue = e.target.value;
              if (inputValue !== "") {
               const newPercent = parseFloat(inputValue) || 0;
               const validPercent = Math.max(0, Math.min(100, newPercent));
               this.updateMouDiscountPercent(idx, validPercent);
              }
              // Clear the raw input after processing
              setTimeout(() => {
               this.updateSurgeryBill(idx, (sb) => ({
                ...sb,
                mouDiscountPercentInput: undefined,
               }));
              }, 100);
             }}
             onKeyPress={(e) => {
              // Process on Enter key
              if (e.key === "Enter") {
               e.target.blur(); // Trigger onBlur
              }
             }}
             rightElement={<Tag minimal>%</Tag>}
             style={{ width: 120 }}
            />
            <NumericInput
             placeholder="Amount ₹ (synced)"
             value={(s.mouDiscountPaise || 0) / 100}
             onValueChange={(v) => {
              const newAmountRupees = Math.max(0, v || 0);
              this.updateMouDiscountAmount(idx, newAmountRupees);
             }}
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 200 }}
            />
           </ControlGroup>
           {/* Special Discount (₹) - single instance */}
           <ControlGroup>
            <Tag minimal large style={{ minWidth: 280 }}>
             Special Discount (₹)
            </Tag>
            <NumericInput
             value={(s.specialDiscountPaise || 0) / 100}
             onValueChange={(v) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               specialDiscountPaise: Math.max(0, Math.trunc((v || 0) * 100)),
              }))
             }
             rightElement={<Tag minimal>₹</Tag>}
             style={{ width: 160 }}
            />
            <Tag minimal large style={{ marginLeft: 8, minWidth: 120 }}>
             Remarks
            </Tag>
            <InputGroup
             placeholder="Reason/Notes"
             value={s.specialDiscountRemarks || ""}
             onChange={(e) =>
              this.updateSurgeryBill(idx, (sb) => ({
               ...sb,
               specialDiscountRemarks: e.target.value,
              }))
             }
             style={{ width: 280 }}
            />
           </ControlGroup>
           {/* Removed duplicate discount fields here as requested */}
          </div>

          {/* Subtotal */}
          <div
           style={{
            display: "flex",
            justifyContent: "flex-start",
            marginTop: 10,
           }}
          >
           <Tag large intent="primary">
            Surgery Subtotal: {indianRupee(s.subtotalPaise || 0)}
           </Tag>
           {!isBillingLocked && (
           <Button
            small
            intent="success"
            icon="floppy-disk"
            style={{ marginLeft: 8 }}
            onClick={() => this.saveSurgeryLine(idx)}
           >
            Save
           </Button>
           )}
          </div>
         </Card>
        );
       })}
      </div>
      </fieldset>
     </Collapse>
    </Card>

    {/* 3) Implant/IOL Bill */}
    <Card
     elevation={Elevation.ONE}
     style={{ padding: "15px", marginBottom: "15px" }}
    >
     {this.renderHeader("Implant/IOL Bill", this.state.isImplantOpen, () =>
      this.setState({ isImplantOpen: !this.state.isImplantOpen })
     )}
     <Collapse isOpen={this.state.isImplantOpen}>
      <fieldset disabled={isBillingLocked} style={{ border: "none", padding: 0, margin: 0 }}>
      {/* Invoice Display Section */}
      {console.log(
       "🔍 RENDER - finalImplantInvoice:",
       this.state.finalImplantInvoice
      )}
      {console.log(
       "🔍 RENDER - surgeryData.implantInvoices:",
       this.state.surgeryData?.implantInvoices?.length || 0
      )}
      {this.state.finalImplantInvoice && (
       <div
        style={{
         marginBottom: 15,
         padding: 12,
         border: "1px solid var(--border-color)",
         borderRadius: 6,
         backgroundColor: "var(--bg-surface)",
        }}
       >
        {this.implantInvoiceDiverged() && (
         <Callout
          intent="warning"
          title="Invoice exists — items changed after invoice generation."
          style={{ marginBottom: 10 }}
         />
        )}
        <div
         style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          marginBottom: 10,
          flexWrap: "wrap",
         }}
        >
         <Tag large minimal intent="primary">
          Invoice No.
         </Tag>
         <Tag large>{this.state.finalImplantInvoice.number}</Tag>
         <Tag large minimal>
          Generated
         </Tag>
         <Tag large>
          {new Date(
           this.state.finalImplantInvoice.generatedAt
          ).toLocaleString()}
         </Tag>
         <Tag large minimal>
          By
         </Tag>
         <Tag large>
          {this.state.finalImplantInvoice.generatedBy?.name || "Unknown"}
         </Tag>
         {this.state.finalImplantInvoice.isRegeneration && (
          <>
           <Tag large minimal intent="warning">
            Supersedes
           </Tag>
           <Tag large intent="warning">
            {this.state.finalImplantInvoice.supersedes}
           </Tag>
          </>
         )}
         <Tag large minimal>
          Total
         </Tag>
         <Tag large intent="primary">
          {indianRupee(this.state.finalImplantInvoice.totalPaise || 0)}
         </Tag>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
         <Button
          small
          icon="refresh"
          intent="warning"
          onClick={this.generateImplantInvoice}
         >
          Regenerate Invoice
         </Button>
         <Button small icon="eye-open" onClick={this.previewImplantBillPdf}>
          Print Invoice
         </Button>
        </div>
       </div>
      )}
      </fieldset>

      {/* Action Buttons — outside fieldset so print stays enabled after discharge */}
      <div
       style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10,
       }}
      >
       {!isBillingLocked && (
       <Button small icon="add" onClick={this.addManualIolRow}>
        Add Item
       </Button>
       )}
       {!this.state.finalImplantInvoice && !isBillingLocked ? (
        <Button
         small
         icon="document"
         intent="primary"
         onClick={this.generateImplantInvoice}
        >
         Generate Invoice
        </Button>
       ) : null}
       <Button small icon="eye-open" onClick={this.previewImplantBillPdf}>
        Print Implant Bill
       </Button>
       {!isBillingLocked && (
       <Button
        small
        icon="floppy-disk"
        intent="success"
        onClick={this.saveImplantBill}
        loading={this.state.isSaving}
       >
        Save Implant Bill
       </Button>
       )}
      </div>

      <fieldset disabled={isBillingLocked} style={{ border: "none", padding: 0, margin: 0 }}>
      {/* Table */}
      <div
       style={{
        border: "1px solid var(--border-color)",
        borderRadius: 6,
        overflow: "hidden",
       }}
      >
       <div
        style={{
         display: "grid",
         gridTemplateColumns:
          "2fr 1.2fr 1fr 0.8fr 0.8fr 1fr 0.8fr 0.8fr 1fr 0.6fr",
         background: "var(--bg-surface)",
         fontWeight: 600,
         padding: "8px 10px",
         color: "var(--text-primary)",
        }}
       >
        <div>ITEM</div>
        <div>HSN Code</div>
        <div>Serial No. / Batch No.</div>
        <div>EXP</div>
        <div>QTY</div>
        <div>PRICE</div>
        <div>CGST(%)</div>
        <div>SGST(%)</div>
        <div>TOTAL</div>
        <div></div>
       </div>
       {(this.state.implantBill.items || []).map((r, i) => {
        // Get implant cost from consolidated surgery bill
        const firstSurgeryBill = this.state.surgeryBills?.[0];
        const implantCostPaise =
         firstSurgeryBill?.implantCostInclusivePaise || 0;
        const iolDiscountPaise = firstSurgeryBill?.iolDiscountPaise || 0;
        const totalImplantCostPaise = Math.max(
         0,
         implantCostPaise - iolDiscountPaise
        );

        return (
         <div
          key={`iol-${i}`}
          style={{
           display: "grid",
           gridTemplateColumns:
            "2fr 1.2fr 1fr 0.8fr 0.8fr 1fr 0.8fr 0.8fr 1fr 0.6fr",
           padding: "8px 10px",
           borderTop: "1px solid var(--border-color)",
           alignItems: "center",
          }}
         >
          <InputGroup
           placeholder="Item"
           value={r.itemName}
           onChange={(e) =>
            this.updateIolRow(i, (row) => ({
             ...row,
             itemName: e.target.value,
            }))
           }
           style={{ width: "100%" }}
          />
          <InputGroup
           placeholder="HSN Code"
           value={r.hsnCode || "90213900"}
           onChange={(e) =>
            this.updateIolRow(i, (row) => ({
             ...row,
             hsnCode: e.target.value,
            }))
           }
           style={{ width: 120 }}
          />
          <InputGroup
           placeholder="Serial"
           value={r.serialNo}
           onChange={(e) =>
            this.updateIolRow(i, (row) => ({
             ...row,
             serialNo: e.target.value,
            }))
           }
           style={{ width: 140 }}
          />
          <InputGroup
           value={r.expiry || ""}
           onChange={(e) =>
            this.updateIolRow(i, (row) => ({
             ...row,
             expiry: e.target.value,
            }))
           }
           placeholder="DD-MM-YYYY"
           style={{ width: 110 }}
          />
          <NumericInput
           value={r.qty || 1}
           onValueChange={(v) =>
            this.updateIolRow(i, (row) => ({
             ...row,
             qty: Math.max(1, Math.trunc(v || 1)),
            }))
           }
           buttonPosition="none"
           style={{ width: 80 }}
           min={1}
          />
          {/*
            Price accepts decimals (e.g. implant prices are maintained with
            paise). Blueprint's NumericInput re-derives its displayed value
            from the numeric prop on every render, which strips an in-progress
            decimal point before the fractional digits can be typed; a plain
            string-backed InputGroup avoids that entirely. The raw typed
            string lives in priceInput; pricePaise stays the integer source of
            truth. Applied to all tenants — whole numbers still type freely.
          */}
          <InputGroup
           value={
            r.priceInput != null
             ? r.priceInput
             : String((r.pricePaise || 0) / 100)
           }
           onChange={(e) => {
            const raw = e.target.value;
            // Allow only digits and a single decimal point (max 2 places).
            if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
            this.updateIolRow(i, (row) => ({
             ...row,
             priceInput: raw,
             pricePaise: Math.round(Math.max(0, Number(raw) || 0) * 100),
            }));
           }}
           inputMode="decimal"
           rightElement={<Tag minimal>₹</Tag>}
           style={{ width: 120 }}
          />
          <NumericInput
           value={r.cgst || 2.5}
           onValueChange={(v) =>
            this.updateIolRow(i, (row) => ({
             ...row,
             cgst: Math.max(0, Number(v || 0)),
            }))
           }
           buttonPosition="none"
           style={{ width: 100 }}
           min={0}
           max={100}
          />
          <NumericInput
           value={r.sgst || 2.5}
           onValueChange={(v) =>
            this.updateIolRow(i, (row) => ({
             ...row,
             sgst: Math.max(0, Number(v || 0)),
            }))
           }
           buttonPosition="none"
           style={{ width: 100 }}
           min={0}
           max={100}
          />
          <div style={{ textAlign: "right", fontWeight: 600 }}>
           {indianRupee(r.totalPaise || 0)}
          </div>
          <Button
           small
           icon="trash"
           intent="danger"
           minimal
           onClick={() => this.removeIolRow(i)}
          />
         </div>
        );
       })}
      </div>

      {/* Totals footer */}
      <div
       style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 16,
        marginTop: 10,
        flexWrap: "wrap",
       }}
      >
       <Tag large minimal>
        Total (Before Discount):{" "}
        {indianRupee(this.state.implantBill.totalBeforeDiscountPaise || 0)}
       </Tag>
       <Tag large minimal>
        Total GST: {indianRupee(this.state.implantBill.totalGstPaise || 0)}
       </Tag>
       <Tag large intent="primary">
        Total: {indianRupee(this.state.implantBill.totalPaise || 0)}
       </Tag>
      </div>
      </fieldset>
     </Collapse>
    </Card>

    {/* 4) Payments & Money Receipts */}
    <Card
     elevation={Elevation.ONE}
     style={{ padding: "15px", marginBottom: "15px" }}
    >
     {this.renderHeader(
      "Payments & Money Receipts",
      this.state.isPaymentsOpen,
      () => this.setState({ isPaymentsOpen: !this.state.isPaymentsOpen })
     )}
     <Collapse isOpen={this.state.isPaymentsOpen}>
      <fieldset disabled={isPaymentLocked} style={{ border: "none", padding: 0, margin: 0 }}>
      {/* <Text
              style={{
                color: "var(--text-secondary)",
                fontSize: 12,
                marginBottom: 6,
              }}
            >
              Choose Payment or Refund. Totals update immediately.
            </Text> */}
      {/* Form */}
      <div
       style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "center",
        marginBottom: 10,
       }}
      >
       <Tag minimal large>
        Type
       </Tag>
       <HTMLSelect
        options={["Payment", "Refund"]}
        value={this.state.paymentForm.type}
        onChange={(e) => {
         const nextType = e.target.value;
         this.setState({
          paymentForm: {
           ...this.state.paymentForm,
           type: nextType,
           mode:
            nextType === "Refund"
             ? ""
             : this.state.paymentForm.mode || "Cash",
           amountPaise: 0,
          },
         });
        }}
       />
       <Tag minimal large>
        Mode
       </Tag>
       <HTMLSelect
        options={[
         ...(this.state.paymentForm.type === "Refund" || !this.state.paymentForm.mode
          ? [{ label: "Select mode", value: "" }]
          : []),
         "Cash",
         "Card",
         "UPI",
         "NEFT",
         "Wallet",
         "Cheque",
        ]}
        value={this.state.paymentForm.mode}
        onChange={(e) =>
         this.setState({
          paymentForm: {
           ...this.state.paymentForm,
           mode: e.target.value,
          },
         })
        }
       />
       <Tag minimal large>
        Amount
       </Tag>
       {(() => {
        const isRefund = this.state.paymentForm.type === "Refund";
        const existingEvents = this.state.payments?.events || [];
        const totalPaidPaise = existingEvents
         .filter((e) => e.type !== "Refund")
         .reduce((s, e) => s + (e.amountPaise || 0), 0);
        const totalAlreadyRefundedPaise = existingEvents
         .filter((e) => e.type === "Refund")
         .reduce((s, e) => s + (e.amountPaise || 0), 0);
        const maxRefundableRupees = isRefund
         ? Math.max(0, (totalPaidPaise - totalAlreadyRefundedPaise) / 100)
         : undefined;
        const maxPaymentRupees = isRefund
         ? undefined
         : this.getMaxCollectablePaise() / 100;
        const maxRupees = isRefund ? maxRefundableRupees : maxPaymentRupees;
        return (
         <>
          <NumericInput
           value={this.state.paymentForm.amountPaise / 100}
           onValueChange={(v) =>
            this.setState({
             paymentForm: {
              ...this.state.paymentForm,
              amountPaise: Math.max(
               0,
               Math.round(Math.min(v || 0, maxRupees) * 100)
              ),
             },
            })
           }
           max={maxRupees}
           min={0}
           rightElement={<Tag minimal>₹</Tag>}
           style={{ width: 140 }}
           intent={
            isRefund &&
            this.state.paymentForm.amountPaise > 0 &&
            this.state.paymentForm.amountPaise / 100 >= maxRefundableRupees
             ? "danger"
             : "none"
           }
          />
          {isRefund && maxRefundableRupees !== undefined && (
           <Tag
            minimal
            intent={
             this.state.paymentForm.amountPaise / 100 > maxRefundableRupees
              ? "danger"
              : "none"
            }
            style={{ fontSize: 12 }}
           >
            Max refund: ₹{maxRefundableRupees.toFixed(2)}
           </Tag>
          )}
          {!isRefund && (
           <Tag
            minimal
            intent={maxPaymentRupees > 0 ? "none" : "warning"}
            style={{ fontSize: 12 }}
           >
            {maxPaymentRupees > 0
             ? `Max payment: ₹${maxPaymentRupees.toFixed(2)}`
             : "Balance ₹0 – no payment can be collected"}
           </Tag>
          )}
         </>
        );
       })()}
       <Tag minimal large>
        Remarks{this.state.paymentForm.type === "Refund" ? " *" : ""}
       </Tag>
       <InputGroup
        placeholder={
         this.state.paymentForm.type === "Refund"
          ? "Exact reason for refund (required)"
          : "Remarks"
        }
        intent={
         this.state.paymentForm.type === "Refund" &&
         !String(this.state.paymentForm.paymentRemarks || "").trim()
          ? "warning"
          : "none"
        }
        value={this.state.paymentForm.paymentRemarks || ""}
        onChange={(e) =>
         this.setState({
          paymentForm: {
           ...this.state.paymentForm,
           paymentRemarks: e.target.value,
          },
         })
        }
        style={{ width: 260 }}
       />
       <Tag minimal large>
        Done by
       </Tag>
       <Tag large>{this.props.authStore.name || "Unknown"}</Tag>
       {!isPaymentLocked && (() => {
        const { type, mode, amountPaise, paymentRemarks } = this.state.paymentForm;
        let disabledReason = null;
        if (type === "Refund") {
         if (!mode) disabledReason = "Select the refund mode";
         else if (!amountPaise) disabledReason = "Enter the refund amount";
         else if (!String(paymentRemarks || "").trim())
          disabledReason = "Enter the reason for the refund in Remarks";
        } else if (this.getMaxCollectablePaise() <= 0) {
         const entries = this.state.authorization?.entries || [];
         const activeAuth = entries[this.state.authorization?.activeIndex];
         disabledReason =
          !isCashPayorPanel && this.isFinalAuthType(activeAuth?.type)
           ? "Final Authorization covers the full amount – no payment can be collected"
           : "Balance is ₹0 – no payment can be collected";
        }
        return (
         <>
          <Button
           icon="floppy-disk"
           intent="primary"
           onClick={this.savePayment}
           disabled={Boolean(disabledReason)}
           title={disabledReason || ""}
          >
           Save Payment
          </Button>
          {disabledReason && (
           <Text style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {disabledReason}
           </Text>
          )}
         </>
        );
       })()}
      </div>
      </fieldset>

      {/* List (reverse chronological) — outside fieldset so Print Receipt stays enabled after discharge */}
      {this.state.payments.events.length === 0 ? (
       <Text style={{ color: "var(--text-secondary)", fontStyle: "italic" }}>
        No payments yet.
       </Text>
      ) : (
       <div style={{ display: "grid", gap: 8 }}>
        {(this.state.payments.events || []).map((p, idx) => (
         <Card
          key={`pay-${idx}`}
          elevation={Elevation.ZERO}
          style={{
           padding: 8,
           display: "flex",
           justifyContent: "space-between",
           alignItems: "center",
          }}
         >
          <div
           style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
           }}
          >
           <Tag
            large
            minimal
            intent={p?.type === "Refund" ? "danger" : "warning"}
           >
            {p?.type || "Payment"}
           </Tag>
           <Tag large minimal>
            {p?.mode || "Cash"}
           </Tag>
           <Tag large>{indianRupee(p?.amountPaise || 0)}</Tag>

           {p?.moneyReceiptNo ? (
            <Tag large minimal>
             MR: {p.moneyReceiptNo}
            </Tag>
           ) : null}
           <Tag large minimal>
            By: {p?.doneBy || this.props.authStore.name || "Unknown"}
           </Tag>
           <Tag large minimal>
            {p?.ts ? new Date(p.ts).toLocaleString() : ""}
           </Tag>
           {p?.paymentRemarks || p?.remarks ? (
            <Tag large minimal>
             Remarks: {p.paymentRemarks || p.remarks}
            </Tag>
           ) : null}
          </div>
          <div>
           <Button
            small
            icon="eye-open"
            onClick={() => this.openReceiptPrint(p?.moneyReceiptNo || "")}
           >
            Print Receipt
           </Button>
          </div>
         </Card>
        ))}
       </div>
      )}
     </Collapse>
    </Card>

    {/* 5) Invoice & Print (Final Invoice) */}
    <Card
     elevation={Elevation.ONE}
     style={{ padding: "15px", marginBottom: "10px" }}
    >
     {this.renderHeader("Invoice & Print", this.state.isInvoiceOpen, () =>
      this.setState({ isInvoiceOpen: !this.state.isInvoiceOpen })
     )}
     <Collapse isOpen={this.state.isInvoiceOpen}>
      {this.renderInvoicePanel()}
     </Collapse>
    </Card>

    {this.renderAuditDrawer()}
   </div>
  );
 }

 renderHeader = (title, isOpen, onToggle) => (
  <div
   style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px",
   }}
  >
   <H4 style={{ margin: 0, color: "var(--text-primary)" }}>{title}</H4>
   <Button
    small
    minimal
    icon={isOpen ? "chevron-up" : "chevron-down"}
    onClick={onToggle}
   />
  </div>
 );

 // ----- Dummy PDF helpers -----
 pdfEscape = (text) =>
  String(text ?? "")
   .replace(/\\/g, "\\\\")
   .replace(/\(/g, "\\(")
   .replace(/\)/g, "\\)");

 makeDummyPdf = (pages) => {
  // pages: [{ title: string, lines: string[] }]
  const offsets = [];
  let pdf = "%PDF-1.4\n";

  const addObject = (str) => {
   offsets.push(pdf.length);
   pdf += str + "\n";
  };

  // Fonts
  addObject("1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj");
  const kidsRefs = pages.map((_, i) => `${3 + i * 2} 0 R`).join(" ");
  addObject(
   `2 0 obj << /Type /Pages /Count ${pages.length} /Kids [ ${kidsRefs} ] >> endobj`
  );

  pages.forEach((page, idx) => {
   const pageObj = 3 + idx * 2;
   const contentObj = pageObj + 1;
   const width = 612,
    height = 792;
   addObject(
    `${pageObj} 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 ${width} ${height}] /Resources << /Font << /F1  ${
     pages.length * 2 + 3
    } 0 R >> >> /Contents ${contentObj} 0 R >> endobj`
   );
   const title = this.pdfEscape(page.title || "");
   const lines = (page.lines || []).map((l) => this.pdfEscape(l));
   const headerY = 760;
   const content = [
    "BT",
    "/F1 16 Tf",
    `72 ${headerY} Td`,
    `(${title}) Tj`,
    "14 TL",
    "/F1 12 Tf",
    "T*",
    ...lines.map((l) => `(${l}) Tj T*`),
    "ET",
   ].join("\n");
   const len = content.length;
   addObject(
    `${contentObj} 0 obj << /Length ${len} >> stream\n${content}\nendstream endobj`
   );
  });

  // Font object (Helvetica)
  addObject(
   `${
    pages.length * 2 + 3
   } 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj`
  );

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${pages.length * 2 + 4}\n`;
  pdf += `0000000000 65535 f \n`;
  for (let i = 0; i < offsets.length; i++) {
   const off = String(offsets[i]).padStart(10, "0");
   pdf += `${off} 00000 n \n`;
  }
  pdf += `trailer << /Size ${
   pages.length * 2 + 4
  } /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  const blob = new Blob([pdf], { type: "application/pdf" });
  return URL.createObjectURL(blob);
 };

 openPdf = (url) => {
  try {
   window.open(url, "_blank");
  } catch (e) {}
 };

 // ----- Specific preview builders -----
 previewAuthorizationPdf = () => {
  const auth = (this.state.authorization.entries || [])[0];
  const surgeryId = this.getSurgeryId();
  if (!auth) {
   cogoToast.warn("No authorization to preview");
   return;
  }
  const url = this.makeDummyPdf([
   {
    title: "Authorization Letter",
    lines: [
     `Surgery ID: ${surgeryId}`,
     `Type: ${auth.type}`,
     `Authorized Amount: ₹${(auth.amountRupees || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
     })}`,
     `TPA: ${auth.tpaName || "NA"}`,
     `Insurer: ${auth.insuranceName || "NA"}`,
     `Uploaded By: ${auth.uploadedByName || "-"}`,
     `At: ${new Date(auth.ts).toLocaleString()}`,
     `Remarks: ${auth.remarks || "No remarks"}`,
    ],
   },
  ]);
  this.openPdf(url);
  // Audit: view preview (non-material)
  try {
   const entry = this.buildAuditEntry({
    actionType: "VIEW",
    actionSlug: "Preview authorization letter",
    actionMetadata: {
     panel: "BILLING",
     fieldName: "print.authorization.preview",
     changeType: "VIEW",
     previousValue: "",
     newValue: { surgeryId },
    },
   });
   this.sendAuditEntries([entry]);
  } catch (e) {}
 };

 previewPaymentPdf = () => {
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   cogoToast.warn("Surgery ID not found");
   return;
  }

  // const url = `https://newchart-prints-env-staging-radical-health.vercel.app/neo/sx/${this.props.authStore.prefix}/neoSxBookingMaster/getByIdForBill/${sxId}`;
  // window.open(url, "_blank");
 };

 previewImplantBillPdf = () => {
  if (this.blockIfCancelled()) return;
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   cogoToast.warn("Surgery ID not found");
   return;
  }

  const url = `${printApiUrl}/neo/sx/implant/${surgeryId}`;
  window.open(url, "_blank");
  // Audit: view preview (non-material)
  try {
   const entry = this.buildAuditEntry({
    actionType: "VIEW",
    actionSlug: "Preview implant/IOL bill",
    actionMetadata: {
     panel: "BILLING",
     fieldName: "print.implant.preview",
     changeType: "VIEW",
     previousValue: "",
     newValue: { surgeryId },
    },
   });
   this.sendAuditEntries([entry]);
  } catch (e) {}
 };

 openReceiptPrint = (moneyReceiptNo) => {
  if (this.blockIfCancelled()) return;
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   cogoToast.warn("Surgery ID not found");
   return;
  }
  window.open(
   `${printApiUrl}/neo/sx/cashReciept/${this.getBookingId()}?moneyReceiptNo=${encodeURIComponent(
    moneyReceiptNo || ""
   )}`,
   "_blank"
  );
 };

 // Helper: format date to IST and DD-MM-YYYY
 formatToISTDate = (d) => {
  try {
   if (!d) return "";
   const date = new Date(d);
   const tzDate = new Date(
    date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
   );
   const dd = String(tzDate.getDate()).padStart(2, "0");
   const mm = String(tzDate.getMonth() + 1).padStart(2, "0");
   const yyyy = tzDate.getFullYear();
   return `${dd}-${mm}-${yyyy}`;
  } catch (e) {
   return "";
  }
 };

 // Parse DD-MM-YYYY to Date
 parseDateFromDisplay = (s) => {
  try {
   console.log("🔍 parseDateFromDisplay received:", s, "Type:", typeof s);

   if (!s || typeof s !== "string") {
    // Return a valid default date instead of null
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() + 1);
    console.log("⚠️ Not a string, returning default:", defaultDate);
    return defaultDate;
   }

   const parts = s.split("-");
   console.log("📋 Split parts:", parts);

   if (parts.length !== 3) {
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() + 1);
    console.log("⚠️ Not 3 parts, returning default:", defaultDate);
    return defaultDate;
   }

   const [dd, mm, yyyy] = parts.map((x) => parseInt(x, 10));
   console.log("📋 Parsed components - DD:", dd, "MM:", mm, "YYYY:", yyyy);

   // Validate components
   if (isNaN(dd) || isNaN(mm) || isNaN(yyyy)) {
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() + 1);
    console.log("⚠️ NaN in components, returning default:", defaultDate);
    return defaultDate;
   }

   if (yyyy < 1900 || yyyy > 2100 || mm < 1 || mm > 12 || dd < 1 || dd > 31) {
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() + 1);
    console.log("⚠️ Out of valid range, returning default:", defaultDate);
    return defaultDate;
   }

   const date = new Date(yyyy, mm - 1, dd);
   console.log("📅 Created date object:", date);

   // Validate the date was created correctly (handles invalid dates like Feb 30)
   if (
    date.getFullYear() !== yyyy ||
    date.getMonth() !== mm - 1 ||
    date.getDate() !== dd
   ) {
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() + 1);
    console.log("⚠️ Date validation failed, returning default:", defaultDate);
    return defaultDate;
   }

   console.log("✅ Successfully parsed date:", date);
   return date;
  } catch (e) {
   const defaultDate = new Date();
   defaultDate.setFullYear(defaultDate.getFullYear() + 1);
   console.log(
    "❌ Error in parseDateFromDisplay:",
    e,
    "returning default:",
    defaultDate
   );
   return defaultDate;
  }
 };

 // Resilient parse for expiry value (string DD-MM-YYYY or Date)
 parseExpiryToDate = (val) => {
  try {
   if (!val || val === "") {
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() + 1);
    return defaultDate;
   }

   if (val instanceof Date) {
    if (!isNaN(val.getTime())) {
     return val;
    }
    const defaultDate = new Date();
    defaultDate.setFullYear(defaultDate.getFullYear() + 1);
    return defaultDate;
   }

   if (typeof val === "string") {
    return this.parseDateFromDisplay(val);
   }

   // Try to convert to date
   const date = new Date(val);
   if (!isNaN(date.getTime())) {
    return date;
   }

   // Fallback to default
   const defaultDate = new Date();
   defaultDate.setFullYear(defaultDate.getFullYear() + 1);
   return defaultDate;
  } catch (e) {
   const defaultDate = new Date();
   defaultDate.setFullYear(defaultDate.getFullYear() + 1);
   return defaultDate;
  }
 };

 handlePrintFinalInvoice = async () => {
  if (this.blockIfCancelled()) return;
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   cogoToast.warn("Surgery ID not found");
   return;
  }

  // Check if invoice exists
  if (!this.state.finalInvoice) {
   try {
    cogoToast.loading("Generating invoice for print...");
    // Generate invoice automatically
    await this.generateFinalInvoice();
    // Invoice has been generated, now proceed with print
    this.previewFinalInvoicePdf();
   } catch (error) {
    console.error("Error generating invoice for print:", error);
    cogoToast.error("Failed to generate invoice for print");
    return;
   }
  } else {
   // Invoice exists, proceed with print
   this.previewFinalInvoicePdf();
  }
 };

 previewFinalInvoicePdf = () => {
  if (this.blockIfCancelled()) return;
  const surgeryId = this.getBookingId();
  if (!surgeryId) {
   cogoToast.warn("Surgery ID not found");
   return;
  }

  const url = `https://newchart-prints-env-staging-radical-health.vercel.app/neo/sx/invoice/${surgeryId}`;
  window.open(url, "_blank");
  // Audit: view preview (non-material)
  try {
   const entry = this.buildAuditEntry({
    actionType: "VIEW",
    actionSlug: "Preview final invoice",
    actionMetadata: {
     panel: "BILLING",
     fieldName: "print.invoice.preview",
     changeType: "VIEW",
     previousValue: "",
     newValue: { surgeryId },
    },
   });
   // this.sendAuditEntries([entry]);
  } catch (e) {}
 };

 previewMergedSummaryPdf = () => {
  if (!this.state.finalInvoice) {
   cogoToast.warn("Generate final invoice first");
   return;
  }
  const pages = [];
  // Invoice first
  const inv = this.state.finalInvoice;
  (inv.surgeryBillsSnapshot || []).forEach((s, idx) => {
   const lines = [
    `Surgery: ${s.header?.printedName || "-"} / ${s.header?.group || "-"}`,
    `Subtotal: ${indianRupee(s.subtotalPaise || 0)}`,
   ];
   pages.push({ title: `Invoice • Surgery ${idx + 1}`, lines });
  });
  pages.push({
   title: "Invoice • Overall Totals",
   lines: [`Overall: ${indianRupee(inv.overallPaise || 0)}`],
  });
  // Money receipts in time order (descending events already stored; we can sort)
  const payments = [...(this.state.payments.events || [])].sort(
   (a, b) => new Date(a.ts) - new Date(b.ts)
  );
  payments.forEach((p, i) => {
   pages.push({
    title: `Money Receipt ${i + 1}`,
    lines: [
     `Receipt No: ${p.moneyReceiptNo || "-"}`,
     `Type: ${p.type}`,
     `Mode: ${p.mode}`,
     `Amount: ${indianRupee(p.amountPaise || 0)}`,
     `By: ${p.doneBy || "Unknown"}`,
     `Time: ${p.ts ? new Date(p.ts).toLocaleString() : "-"}`,
    ],
   });
  });
  // Latest authorization
  const auth = (this.state.authorization.entries || [])[0];
  if (auth) {
   pages.push({
    title: "Authorization Letter",
    lines: [
     `Type: ${auth.type}`,
     `Authorized Amount: ₹${(auth.amountRupees || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
     })}`,
     `TPA: ${auth.tpaName || "NA"}`,
     `Insurer: ${auth.insuranceName || "NA"}`,
     `At: ${new Date(auth.ts).toLocaleString()}`,
     `Remarks: ${auth.remarks || "No remarks"}`,
    ],
   });
  }
  const url = this.makeDummyPdf(pages);
  this.openPdf(url);
  // Audit: view summary preview (non-material)
  try {
   const entry = this.buildAuditEntry({
    actionType: "VIEW",
    actionSlug: "Preview merged summary",
    actionMetadata: {
     panel: "BILLING",
     fieldName: "print.summary.preview",
     changeType: "VIEW",
     previousValue: "",
     newValue: { invoiceNo: this.state.finalInvoice.number },
    },
   });
   this.sendAuditEntries([entry]);
  } catch (e) {}
 };
}

export default inject("authStore")(observer(BillingTab));
